<template>
  <!-- 🔴 TWO SHAPES, ONE DIALOG. A kind with no card keeps the ORIGINAL geometry exactly — 820px wide and no
       `height` at all, i.e. as tall as its content. The card kinds get a wide, fixed-height window instead,
       because the second column only works if the dialog has a height for its two scrollers to divide; a
       content-sized dialog would let the editor column grow forever and the preview would scroll off screen
       with it. `undefined` (not '' / 'auto') is what leaves the prop unset on the non-card path. -->
  <v-dialog
    :model-value="modelValue"
    :max-width="showCardPreview ? 'min(96vw, 1440px)' : '820px'"
    :height="showCardPreview ? '88vh' : undefined"
    persistent
    scrollable
    data-testid="tq2-item-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card @keydown.esc="closeDialog">
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">{{ isEditMode ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">{{ isEditMode ? 'แก้คำถาม' : 'เพิ่มคำถาม' }}</div>
          <div class="text-caption opacity-80">
            การเปลี่ยนแปลงจะถูกพักไว้ — กด SAVE ที่หน้า builder เพื่อบันทึกถาวร
          </div>
        </div>
        <v-btn icon variant="text" color="white" data-testid="tq2-item-dialog-close" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
          <v-tooltip activator="parent" location="top">ปิด</v-tooltip>
        </v-btn>
      </v-card-title>

      <!-- 🔴 `overflow: hidden` on this element (via `.tq2-split`) is the whole trick: `scrollable` makes
           `v-card-text` the dialog's ONE scroller, and while it keeps scrolling itself the two columns inside it
           move together — the preview slides away the moment the author scrolls the form. Taking the scroll off
           the parent is what lets each column own its own. On the non-card path the class is the original `pa-6`
           and nothing about this element changes. -->
      <v-card-text :class="showCardPreview ? 'tq2-split pa-0' : 'pa-6'">
      <!-- ── LEFT: the editor, i.e. every field this dialog has ever had, wrapped and not otherwise touched.
           The per-kind `v-if` / `v-else-if` chain lives inside as a complete set of siblings, so wrapping the
           block whole cannot break it — see the chain notes further down. -->
      <div class="tq2-editor-col" :class="showCardPreview ? 'pa-6' : ''">
        <v-alert
          v-if="submitAttempted && validationErrors.length"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
          data-testid="tq2-item-dialog-error"
        >
          <div class="font-weight-medium mb-1">กรุณาตรวจสอบข้อมูลให้ครบถ้วน</div>
          <ul class="tq2-error-list">
            <li v-for="(msg, i) in validationErrors" :key="i">{{ msg }}</li>
          </ul>
        </v-alert>

        <!-- ── BLOCK: ชนิด & ข้อมูลเชื่อมโยง ─────────────────────────── -->
        <div class="tq2-block">
          <h4 class="tq2-block-title">ชนิด &amp; ข้อมูลเชื่อมโยง</h4>
          <v-select
            data-testid="tq2-item-kind"
            :model-value="item.itemKind"
            :items="itemKindOptions"
            item-title="title"
            item-value="value"
            label="ชนิดคำถาม (item kind) *"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-shape-outline"
            hide-details
            @update:model-value="onKindChange"
          />
          <p class="text-caption text-medium-emphasis mt-2 mb-0">{{ kindHint }}</p>

          <!-- FIELD_INPUT → single FieldKey picker (from the TaxConfigRuleV2 registry). A picker and not a
               free-text box on purpose: the key has to be one that really exists in the registry, because the card
               and every VisibleWhen below it read it BY NAME — a typo binds the answer to a name nothing reads. -->
          <v-autocomplete
            v-if="isFieldInput"
            data-testid="tq2-item-fieldkey"
            v-model="item.fieldKey"
            :items="ruleOptions"
            item-title="title"
            item-value="value"
            :return-object="false"
            label="ผูกกับ Field (fieldKey) *"
            :loading="loadingRules"
            :no-data-text="rulePickerNoData"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-link-variant"
            hint="เลือก Field Key ที่สร้างไว้ใน Tax Config V2 — เป็นชื่อ variable ที่ node เก็บคำตอบ"
            persistent-hint
            class="mt-3"
          />

          <!-- SUMMARY → the card's two config blobs. No fieldKey field at all: the backend rejects a SUMMARY
               that carries one, because a FieldKey-less item is what keeps SeedUnansweredDefaults from seeding an
               empty gate fact that closes every `field = value` condition below it. -->
          <template v-if="isSummary">
            <v-textarea
              data-testid="tq2-item-cardconfig"
              v-model="item.cardConfigJson"
              label="Card config (JSON) *"
              variant="outlined"
              density="comfortable"
              rows="8"
              auto-grow
              class="mt-3 code-area"
              :error="!!cardConfigError"
              :error-messages="cardConfigError ? [cardConfigError] : []"
              hint="cardType · refId · owner (SELF/SPOUSE) · groups[] — ชื่อหมวดภาษาไทย, การรวมหมวด เช่น (1)-(2), และ clause tag (ก)/(ข) ของประเภทย่อย · number เว้นว่างได้ (การ์ดจะไม่แสดง badge เลขหมวด)"
              persistent-hint
            >
              <template #append-inner>
                <!-- 🔴 `@mousedown.prevent`: the button sits INSIDE the field, so without it the press also focuses
                     the textarea and drops the caret wherever the pointer landed — which, in a document that is about
                     to be reflowed, is not where the author was looking. -->
                <v-btn
                  data-testid="tq2-item-cardconfig-beautify"
                  icon
                  size="x-small"
                  variant="text"
                  :disabled="!canFormatJson(item.cardConfigJson)"
                  @mousedown.prevent
                  @click.stop="beautifyItemJson('cardConfigJson')"
                >
                  <v-icon size="18">mdi-code-braces</v-icon>
                  <v-tooltip activator="parent" location="top">จัดรูปแบบ JSON</v-tooltip>
                </v-btn>
              </template>
            </v-textarea>
            <!-- 🔴 The two keys below are the ONLY way a group that folds several §40 sections together can name its
                 deduction/net seat, and this textarea is the only editor for them. The column is stored and
                 round-tripped VERBATIM (mapItemDtoToUi keeps the string, buildItemRequest only trims it), so an
                 unknown key is never dropped — but nothing surfaces one either, which is why they are spelled out. -->
            <v-alert
              data-testid="tq2-item-cardconfig-fieldkey-hint"
              type="info"
              variant="tonal"
              density="compact"
              class="mt-2 text-caption"
            >
              ผูกผลคำนวณเข้าการ์ด: ใส่ <code>deductionFieldKey</code> / <code>netFieldKey</code> ใน group ที่ต้องการ
              (ชี้ไปที่ FieldKey ของผลลัพธ์ที่สูตรเขียนไว้ เช่น <code>EXPENSE_40_5</code>)
              — <strong>ว่างได้</strong>: กลุ่มที่มีมาตราเดียวระบบจะเดาเป็น <code>EXPENSE_&lt;มาตรา&gt;</code> ให้เอง,
              กลุ่มที่ยุบหลายมาตรา (เช่น <code>(1)-(2)</code>) เดาไม่ได้ ต้องใส่เอง ·
              ไม่ใส่ <code>netFieldKey</code> = ระบบคิด <code>net = gross − deduction</code> ให้เมื่อมีครบทั้งคู่
            </v-alert>
            <v-textarea
              data-testid="tq2-item-summarytemplate"
              v-model="item.summaryTemplateJson"
              label="Card template (JSON) *"
              variant="outlined"
              density="comfortable"
              rows="10"
              auto-grow
              class="mt-3 code-area"
              :error="!!summaryTemplateError"
              :error-messages="summaryTemplateError ? [summaryTemplateError] : []"
              :hint="summaryTemplateHint"
              persistent-hint
            >
              <template #append-inner>
                <!-- 🔴 `@mousedown.prevent`: the button sits INSIDE the field, so without it the press also focuses
                     the textarea and drops the caret wherever the pointer landed — which, in a document that is about
                     to be reflowed, is not where the author was looking. -->
                <v-btn
                  data-testid="tq2-item-summarytemplate-beautify"
                  icon
                  size="x-small"
                  variant="text"
                  :disabled="!canFormatJson(item.summaryTemplateJson)"
                  @mousedown.prevent
                  @click.stop="beautifyItemJson('summaryTemplateJson')"
                >
                  <v-icon size="18">mdi-code-braces</v-icon>
                  <v-tooltip activator="parent" location="top">จัดรูปแบบ JSON</v-tooltip>
                </v-btn>
              </template>
            </v-textarea>
            <v-alert type="info" variant="tonal" density="compact" class="mt-2 text-caption">
              การ์ดถูกส่งครั้งเดียวตอนที่บทสนทนาเดินมาถึงข้อนี้ และจะไม่อัปเดตอีกเมื่อลูกค้าแก้คำตอบทีหลัง
              — ถ้าต้องการสรุปใหม่ ให้วางข้อ SUMMARY อีกข้อไว้ท้ายฟอร์ม
            </v-alert>
          </template>

          <!-- CALCULATE → one calc version + the formulas it runs, IN ORDER. No fieldKey field at all: each
               result lands on the FieldKey the FORMULA itself declares (TaxCalcFunctionV2.FieldKey), so a key on
               the item would name nothing — and, like SUMMARY, a FieldKey-less item is the one
               SeedUnansweredDefaults skips instead of seeding an empty gate fact.
               🔴 `v-else-if`, NOT `v-if`: the fieldKey text-field below chains its `v-else-if` onto this block.
               Breaking that chain with a bare `v-if` makes the INCOME_CLASSIFIER / CONFIRM key field unreachable
               (the same bug the §40 catalog editor once had, two blocks down). -->
          <template v-else-if="isCalculate">
            <v-select
              data-testid="tq2-item-calc-version"
              :model-value="calcConfig.calcVersionId"
              :items="calcVersionOptions"
              item-title="title"
              item-value="value"
              label="เวอร์ชันการคำนวณ (Tax Calculate) *"
              :loading="loadingCalcVersions"
              :no-data-text="calcVersionNoData"
              :disabled="configVersionId == null"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-calculator-variant-outline"
              hint="ชุดสูตรของ Tax Calculate V2 ที่อยู่ใต้ config version เดียวกับแบบสอบถามนี้ — เปลี่ยนเวอร์ชันแล้วสูตรที่เลือกไว้จะถูกล้างทั้งหมด"
              persistent-hint
              class="mt-3"
              @update:model-value="onCalcVersionChange"
            >
              <template #item="{ props: itemProps, item: opt }">
                <v-list-item v-bind="itemProps" :subtitle="opt.raw.subtitle || null">
                  <template #append>
                    <v-chip v-if="opt.raw.isActive" size="x-small" variant="tonal" color="success">active</v-chip>
                  </template>
                </v-list-item>
              </template>
            </v-select>

            <v-alert
              v-if="calcNotice"
              data-testid="tq2-item-calc-notice"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-2 text-caption"
            >
              {{ calcNotice }}
            </v-alert>

            <div class="tq2-editor-header mt-4">
              <span class="tq2-label">
                สูตรที่จะรัน
                <span class="tq2-label-flag"> · ต้องมีอย่างน้อย 1 สูตร</span>
              </span>
            </div>
            <div class="tq2-calc-add">
              <v-select
                v-model="calcFnToAdd"
                data-testid="tq2-item-calc-fn-picker"
                :items="calcFunctionOptions"
                item-title="title"
                item-value="value"
                label="เพิ่มสูตร"
                :loading="loadingCalcFunctions"
                :no-data-text="calcFunctionNoData"
                :disabled="calcConfig.calcVersionId == null"
                variant="outlined"
                density="compact"
                hide-details
                prepend-inner-icon="mdi-function-variant"
              >
                <!-- Already-picked formulas are filtered OUT of this list (calcFunctionOptions): the backend 400s
                     on a duplicate functionId, so offering one again only buys a failed save. -->
                <template #item="{ props: itemProps, item: opt }">
                  <v-list-item v-bind="itemProps" :subtitle="opt.raw.functionCode || null">
                    <template #append>
                      <v-chip v-if="opt.raw.fieldKey" size="x-small" variant="tonal" color="primary" class="ml-1">
                        <v-icon start size="13">mdi-key-variant</v-icon>{{ opt.raw.fieldKey }}
                      </v-chip>
                      <v-chip v-if="opt.raw.warn" size="x-small" variant="tonal" color="warning" class="ml-1">
                        {{ opt.raw.warn }}
                      </v-chip>
                    </template>
                  </v-list-item>
                </template>
              </v-select>
              <v-btn
                data-testid="tq2-item-calc-fn-add"
                size="small"
                variant="outlined"
                color="primary"
                :disabled="calcFnToAdd == null"
                @click="addCalcFunction"
              >
                <v-icon start>mdi-plus</v-icon>เพิ่ม
              </v-btn>
            </div>

            <!-- The picked formulas, in RUN ORDER. A row whose formula no longer resolves in the selected version
                 still renders (with a red chip) — dropping it silently would erase config the admin never chose
                 to remove, and the save payload keeps sending it either way. -->
            <div v-if="selectedCalcFunctions.length" class="tq2-row-list">
              <div
                v-for="(row, idx) in selectedCalcFunctions"
                :key="`${row.functionId}-${idx}`"
                class="tq2-calc-row"
                :data-testid="`tq2-item-calc-fn-${idx}`"
              >
                <input
                  class="tq2-row-index tq2-row-index-input"
                  type="number"
                  inputmode="numeric"
                  :min="1"
                  :max="selectedCalcFunctions.length"
                  :value="idx + 1"
                  aria-label="ลำดับสูตร (พิมพ์เลขเพื่อย้ายตำแหน่ง)"
                  :data-testid="`tq2-item-calc-fn-order-${idx}`"
                  @change="setCalcFunctionPosition(idx, $event)"
                  @keydown.enter.prevent="$event.target.blur()"
                />
                <div class="tq2-calc-main">
                  <div class="tq2-calc-head">
                    <span class="tq2-calc-name">{{ row.name }}</span>
                    <code class="tq2-calc-code">{{ row.functionCode || '—' }}</code>
                  </div>
                  <div class="tq2-calc-chips">
                    <v-chip
                      v-if="row.missing"
                      size="x-small"
                      variant="tonal"
                      color="error"
                      :data-testid="`tq2-item-calc-fn-missing-${idx}`"
                    >
                      <v-icon start size="13">mdi-alert-circle-outline</v-icon>ไม่พบสูตรนี้ในเวอร์ชันที่เลือก
                      <v-tooltip activator="parent" location="top">
                        สูตรอาจถูกลบ หรือถูกย้ายไปอยู่คนละเวอร์ชันการคำนวณ — ลบแถวนี้ทิ้งแล้วเลือกใหม่
                      </v-tooltip>
                    </v-chip>
                    <template v-else-if="row.resolved">
                      <v-chip
                        v-if="row.fieldKey"
                        size="x-small"
                        variant="tonal"
                        color="primary"
                        :data-testid="`tq2-item-calc-fn-fieldkey-${idx}`"
                      >
                        <v-icon start size="13">mdi-key-variant</v-icon>{{ row.fieldKey }}
                      </v-chip>
                      <v-chip
                        v-else
                        size="x-small"
                        variant="tonal"
                        color="warning"
                        :data-testid="`tq2-item-calc-fn-nofieldkey-${idx}`"
                      >
                        <v-icon start size="13">mdi-key-alert-outline</v-icon>ยังไม่ผูก FieldKey — ผลลัพธ์จะไม่มีที่บันทึก
                      </v-chip>
                      <v-chip v-if="row.warn" size="x-small" variant="tonal" color="warning">{{ row.warn }}</v-chip>
                    </template>
                  </div>
                </div>
                <v-btn
                  size="small"
                  density="comfortable"
                  variant="text"
                  icon
                  aria-label="เลื่อนสูตรขึ้น"
                  :disabled="idx === 0"
                  :data-testid="`tq2-item-calc-fn-up-${idx}`"
                  @click="moveCalcFunction(idx, -1)"
                >
                  <v-icon size="18">mdi-arrow-up</v-icon>
                  <v-tooltip activator="parent" location="top">เลื่อนขึ้น</v-tooltip>
                </v-btn>
                <v-btn
                  size="small"
                  density="comfortable"
                  variant="text"
                  icon
                  aria-label="เลื่อนสูตรลง"
                  :disabled="idx === selectedCalcFunctions.length - 1"
                  :data-testid="`tq2-item-calc-fn-down-${idx}`"
                  @click="moveCalcFunction(idx, 1)"
                >
                  <v-icon size="18">mdi-arrow-down</v-icon>
                  <v-tooltip activator="parent" location="top">เลื่อนลง</v-tooltip>
                </v-btn>
                <v-btn
                  size="small"
                  density="comfortable"
                  variant="text"
                  icon
                  color="error"
                  aria-label="ลบสูตรออกจากข้อนี้"
                  :data-testid="`tq2-item-calc-fn-remove-${idx}`"
                  @click="removeCalcFunction(idx, row.functionCode || row.name)"
                >
                  <v-icon size="18">mdi-delete-outline</v-icon>
                  <v-tooltip activator="parent" location="top">ลบ</v-tooltip>
                </v-btn>
              </div>
            </div>
            <p v-else class="text-caption text-medium-emphasis mb-0" data-testid="tq2-item-calc-fn-empty">
              ยังไม่ได้เลือกสูตร — เลือกจากช่อง "เพิ่มสูตร" ด้านบนแล้วกด "เพิ่ม"
            </p>

            <v-alert type="info" variant="tonal" density="compact" class="mt-3 text-caption">
              ลำดับในลิสต์คือ <strong>ลำดับการรัน</strong> (บนลงล่าง) · ข้อนี้ไม่ถูกถามลูกค้าและไม่ขึ้นในแชท ·
              <strong>รอบนี้ระบบยังไม่รันสูตรจริง</strong> — รอ Tax Calculate V2 ทำ syntax ดึงค่าจาก tax json ก่อน
              แล้วจึงต่อ executor เข้ามาทีหลัง (config ที่บันทึกไว้ตอนนี้จะถูกใช้ได้ทันทีเมื่อถึงตอนนั้น)
            </v-alert>
          </template>

          <!-- INCOME_CLASSIFIER / INCOME_CLASSIFY / CONFIRM → stable key -->
          <v-text-field
            v-else-if="isConfirmLike || hasIncomeCatalog"
            data-testid="tq2-item-fieldkey"
            v-model="item.fieldKey"
            :label="fieldKeyLabel"
            :placeholder="fieldKeyPlaceholder"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-identifier"
            :hint="fieldKeyHint"
            persistent-hint
            class="mt-3"
          />

          <!-- SHORT_FORM → one mini-form card: its own checkpoint key, the copy the app prints on the card, and the
               ordered list of amount boxes it draws. The AMOUNTS never land here — each row names a SIBLING
               FIELD_INPUT item in this same question version and the number goes to THAT item's answer row, which is
               why the per-row picker is the same TaxConfigRuleV2 registry the FIELD_INPUT branch uses.
               🔴 `v-else-if`, NOT `v-if`: this is one more branch of the SAME per-kind chain that starts at
               `isSummary` above, and the chain is what keeps exactly one kind editor on screen. Ending it here with
               a bare `v-if` leaves the next kind branch someone appends chained to the wrong head — the failure this
               file already records twice (see the CALCULATE note above and the §40 catalog note below). -->
          <!-- FILING_CHOICE / SIMULATE — one branch, because the two need the SAME two boxes: their own fieldKey
               and their own cardConfig. 🔴 Both kinds were absent from this dialog entirely until 2026-08-12 while
               being live in the data, so an author who opened one and saved it sent a null key and got a 400 over a
               row that already had one. See the four kind lists in utils/taxQuestionV2Store.js. -->
          <template v-else-if="isFilingChoice || isSimulate">
            <v-text-field
              data-testid="tq2-item-filingsim-fieldkey"
              v-model="item.fieldKey"
              :label="isFilingChoice ? 'Field ที่เก็บวิธียื่น (fieldKey) *' : 'Checkpoint key ของการ์ดจำลอง (fieldKey) *'"
              :placeholder="isFilingChoice ? 'เช่น FILING_MODE' : 'เช่น SIMULATE_PLAN'"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-identifier"
              :error="!!filingOrSimulateKeyError"
              :error-messages="filingOrSimulateKeyError ? [filingOrSimulateKeyError] : []"
              :hint="isFilingChoice
                ? 'ค่าที่เก็บคือ value ของตัวเลือกที่ลูกค้าเลือก (ถ้าไม่ได้ตั้ง options จะเป็น separate | joint) '
                  + '— เป็นค่าจริงที่สูตรคำนวณอ่านต่อ ไม่ใช่แค่หมุดบอกว่าตอบแล้ว'
                : 'หมุดประจำการ์ด — ตัวเลขจากแถบเลื่อนลง AnswerValuesJson ของแถวนี้ ไม่ได้ลงบนคีย์นี้'"
              persistent-hint
              class="mt-3"
            />

            <v-textarea
              data-testid="tq2-item-filingsim-cardconfig"
              v-model="item.cardConfigJson"
              :label="isSimulate ? 'Card config (JSON) *' : 'Card config (JSON)'"
              variant="outlined"
              density="comfortable"
              rows="8"
              auto-grow
              class="mt-3 code-area"
              :error="!!cardConfigError"
              :error-messages="cardConfigError ? [cardConfigError] : []"
              :hint="isFilingChoice
                ? 'ไม่บังคับ — เว้นว่างได้ (ใช้การ์ดแยก/ร่วมมาตรฐาน) · ใส่ options[] เพื่อกำหนดตัวเลือกเองกี่ใบก็ได้ '
                  + '(label · value ที่จะบันทึก · amountFieldKey ของยอดหลัก · details[] ของบรรทัดย่อย) '
                  + 'ใบที่ยอดน้อยสุดจะได้แท็กแนะนำอัตโนมัติ · ใส่ submitLabel เพื่อให้มีปุ่มยืนยันบนการ์ด · '
                  + 'สี/ขนาดตัวอักษรใส่ที่ style · optionStyle · bestOptionStyle · submitButtonStyle'
                : 'บังคับ — รายการลดหย่อนที่ให้เลื่อน + แถวที่จะเทียบก่อน/หลัง (backend ปฏิเสธถ้าเว้นว่าง) · '
                  + 'การ์ดผลจำลอง (cardPart = RESULT) ใส่ editLabel เพื่อให้มีปุ่มย้อนกลับไปแก้แผน — ต้องมี '
                  + 'scenarioFromFieldKey ชี้ไปยังข้อ picker ด้วย (editMessage = ข้อความที่ส่งเข้าแชท) · '
                  + 'สี/ขนาดตัวอักษรใส่ที่ style · pickerStyle · resultStyle · savingsStyle · comparisonStyle · '
                  + 'selectionStyle · productsButtonStyle · editButtonStyle และ style ของแต่ละ group / item / row'"
              persistent-hint
            >
              <template #append-inner>
                <!-- 🔴 `@mousedown.prevent`: the button sits INSIDE the field, so without it the press also focuses
                     the textarea and drops the caret wherever the pointer landed — which, in a document that is about
                     to be reflowed, is not where the author was looking. -->
                <v-btn
                  data-testid="tq2-item-filingsim-cardconfig-beautify"
                  icon
                  size="x-small"
                  variant="text"
                  :disabled="!canFormatJson(item.cardConfigJson)"
                  @mousedown.prevent
                  @click.stop="beautifyItemJson('cardConfigJson')"
                >
                  <v-icon size="18">mdi-code-braces</v-icon>
                  <v-tooltip activator="parent" location="top">จัดรูปแบบ JSON</v-tooltip>
                </v-btn>
              </template>
            </v-textarea>

            <!-- FILING_CHOICE only → the JSON envelope the app is handed. A SECOND box, not more keys in the one
                 above: that box holds the VALUES the card prints (labels, options[], the FieldKey each amount is
                 read from), this one holds the whole SHAPE they sit in.
                 🔴 SHAPE only. Which tile wins the badge, and which one carries the "ต้องจ่ายเพิ่ม" row, is decided
                 in code FROM THE NUMBERS and never here — a template that re-decided it would contradict the very
                 amounts it is drawing. Blank is the working configuration: blank = the structure the system emits.
                 🔴 Still a child of `v-else-if="isFilingChoice || isSimulate"`, NOT a new chain — see the chain
                 notes above; a bare `v-if` here does not extend the per-kind chain, it only gates this one box.
                 🔴 The inner `v-if="isFilingChoice"` is what keeps the two kinds' boxes apart. They share this
                 branch but read DIFFERENT columns (`filingTemplateJson` / `simulationTemplateJson`), and
                 `buildItemRequest` NULLs the other one on save — so one shared box would throw an author's work
                 away on the very next save. -->
            <v-expansion-panels v-if="isFilingChoice" variant="accordion" class="tq2-meta-panel mt-3">
              <v-expansion-panel data-testid="tq2-item-filing-template-panel">
                <v-expansion-panel-title>
                  <v-icon size="16" class="mr-2">mdi-code-json</v-icon>
                  โครง JSON ของการ์ด (ขั้นสูง)
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="text-caption text-medium-emphasis mb-3">
                    โครง JSON ที่ส่งออกไปให้แอปวาดการ์ดใบนี้ ·
                    <strong>ปล่อยว่าง = ใช้โครงมาตรฐานของระบบ</strong> (ไม่ต้องกรอกอะไรเลย) ·
                    ตัวเลือกและยอดเงินยังมาจาก <code>options[]</code> ใน Card config ด้านบนเหมือนเดิม —
                    ตรงนี้<strong>จัดโครงอย่างเดียว ไม่ได้สร้างตัวเลือกใหม่ และไม่ได้ตัดสินว่าใบไหนคุ้มกว่า</strong>
                  </p>
                  <div class="tq2-editor-header">
                    <span class="tq2-label">
                      Card template (JSON)
                      <span class="tq2-label-flag"> · ว่าง = ใช้โครงมาตรฐานของระบบ</span>
                    </span>
                    <v-btn
                      data-testid="tq2-item-filing-template-default"
                      size="small"
                      variant="outlined"
                      color="primary"
                      :loading="filingTemplateLoading"
                      :disabled="filingTemplateLoading"
                      @click="applyFilingDefaultTemplate"
                    >
                      <v-icon start>mdi-file-restore-outline</v-icon>ใส่โครงเริ่มต้น
                    </v-btn>
                  </div>
                  <v-textarea
                    data-testid="tq2-item-filing-template"
                    v-model="item.filingTemplateJson"
                    label="Card template (JSON)"
                    variant="outlined"
                    density="comfortable"
                    rows="12"
                    auto-grow
                    class="code-area"
                    :error="!!filingTemplateError"
                    :error-messages="filingTemplateError ? [filingTemplateError] : []"
                    :hint="filingTemplateHint"
                    persistent-hint
                  >
                    <template #append-inner>
                      <!-- 🔴 `@mousedown.prevent`: the button sits INSIDE the field, so without it the press also focuses
                           the textarea and drops the caret wherever the pointer landed — which, in a document that is about
                           to be reflowed, is not where the author was looking. -->
                      <v-btn
                        data-testid="tq2-item-filing-template-beautify"
                        icon
                        size="x-small"
                        variant="text"
                        :disabled="!canFormatJson(item.filingTemplateJson)"
                        @mousedown.prevent
                        @click.stop="beautifyItemJson('filingTemplateJson')"
                      >
                        <v-icon size="18">mdi-code-braces</v-icon>
                        <v-tooltip activator="parent" location="top">จัดรูปแบบ JSON</v-tooltip>
                      </v-btn>
                    </template>
                  </v-textarea>
                  <!-- 🔴 `v-pre`: everything below is SAMPLE TEXT for the author to copy, and `{{ … }}` is Vue's own
                       interpolation syntax — without v-pre the compiler would try to evaluate `filing.title` as an
                       expression of THIS component and print an empty string where the sample should be. -->
                  <p v-pre class="text-caption text-medium-emphasis mt-3 mb-0">
                    ตัวอย่าง ·
                    ค่าเดี่ยว: <code>"title": "{{ filing.title }}"</code> ·
                    ไทล์ตัวเลือก (ยาวไม่คงที่): <code>{ "@repeat": "filing.options", "@template": { … } }</code>
                    (ใน @template ใช้ <code>$</code> แทนใบปัจจุบัน เช่น <code>"@repeat": "$.details"</code>) ·
                    คีย์ที่ต้องหายไปทั้งคีย์เมื่อไม่มีค่า: <code>{ "@omitWhenNull": "$.value", "@value": "{{ $.value }}" }</code>
                  </p>
                  <v-alert
                    v-if="filingTemplateNotice"
                    :type="filingTemplateNoticeType"
                    variant="tonal"
                    density="compact"
                    class="mt-3 text-caption"
                    data-testid="tq2-item-filing-template-message"
                  >
                    {{ filingTemplateNotice }}
                  </v-alert>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SIMULATE only → the same second box, one kind over, and the LAST of the four card kinds to get
                 one. The box above it holds the slider GEOMETRY (which deductions may be slid, their ceilings,
                 steps and labels); this one holds the whole SHAPE they sit in.
                 🔴 WHY IT EXISTS. Until it did, an author's only surface was Card config — which the backend
                 deserializes into a fixed DTO, so a key it does not declare (someone adding `postback` by hand to
                 make a replayed card answerable) saved cleanly, came back in this textarea, and was dropped
                 silently at render. Blank is still the working configuration: blank = the structure the system
                 emits, byte for byte.
                 🔴 SHAPE only. Where a bar's floor sits, which rows may be compared, and whether a saving may be
                 claimed are decided in code FROM THE NUMBERS and never here. -->
            <v-expansion-panels v-if="isSimulate" variant="accordion" class="tq2-meta-panel mt-3">
              <v-expansion-panel data-testid="tq2-item-simulation-template-panel">
                <v-expansion-panel-title>
                  <v-icon size="16" class="mr-2">mdi-code-json</v-icon>
                  โครง JSON ของการ์ด (ขั้นสูง)
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="text-caption text-medium-emphasis mb-3">
                    โครง JSON ที่ส่งออกไปให้แอปวาดการ์ดใบนี้ ·
                    <strong>ปล่อยว่าง = ใช้โครงมาตรฐานของระบบ</strong> (ไม่ต้องกรอกอะไรเลย) ·
                    แถบเลื่อนและตัวเลขยังมาจาก Card config ด้านบนเหมือนเดิม —
                    ตรงนี้<strong>จัดโครงอย่างเดียว ไม่ได้เพิ่มแถบ ไม่ได้ย้ายพื้นของแถบ และไม่ได้ตัดสินว่าประหยัดเท่าไหร่</strong>
                  </p>
                  <div class="tq2-editor-header">
                    <span class="tq2-label">
                      Card template (JSON)
                      <span class="tq2-label-flag"> · ว่าง = ใช้โครงมาตรฐานของระบบ</span>
                    </span>
                    <v-btn
                      data-testid="tq2-item-simulation-template-default"
                      size="small"
                      variant="outlined"
                      color="primary"
                      :loading="simulationTemplateLoading"
                      :disabled="simulationTemplateLoading"
                      @click="applySimulationDefaultTemplate"
                    >
                      <v-icon start>mdi-file-restore-outline</v-icon>ใส่โครงเริ่มต้น
                    </v-btn>
                  </div>
                  <v-textarea
                    data-testid="tq2-item-simulation-template"
                    v-model="item.simulationTemplateJson"
                    label="Card template (JSON)"
                    variant="outlined"
                    density="comfortable"
                    rows="12"
                    auto-grow
                    class="code-area"
                    :error="!!simulationTemplateError"
                    :error-messages="simulationTemplateError ? [simulationTemplateError] : []"
                    :hint="simulationTemplateHint"
                    persistent-hint
                  >
                    <template #append-inner>
                      <!-- 🔴 `@mousedown.prevent`: same reason as the box above — the button sits INSIDE the field,
                           so without it the press also focuses the textarea and drops the caret wherever the
                           pointer landed, in a document about to be reflowed. -->
                      <v-btn
                        data-testid="tq2-item-simulation-template-beautify"
                        icon
                        size="x-small"
                        variant="text"
                        :disabled="!canFormatJson(item.simulationTemplateJson)"
                        @mousedown.prevent
                        @click.stop="beautifyItemJson('simulationTemplateJson')"
                      >
                        <v-icon size="18">mdi-code-braces</v-icon>
                        <v-tooltip activator="parent" location="top">จัดรูปแบบ JSON</v-tooltip>
                      </v-btn>
                    </template>
                  </v-textarea>
                  <!-- 🔴 `v-pre`: sample text for the author to copy, and `{{ … }}` is Vue's own interpolation
                       syntax — without it the compiler evaluates `sim.picker.title` against THIS component. -->
                  <p v-pre class="text-caption text-medium-emphasis mt-3 mb-0">
                    ตัวอย่าง ·
                    ค่าเดี่ยว: <code>"title": "{{ sim.picker.title }}"</code> ·
                    กลุ่มแถบเลื่อน (ยาวไม่คงที่): <code>{ "@repeat": "sim.picker.groups", "@template": { … } }</code>
                    (ใน @template ใช้ <code>$</code> แทนกลุ่มปัจจุบัน เช่น <code>"@repeat": "$.items"</code>) ·
                    คีย์ที่ต้องหายไปทั้งคีย์เมื่อไม่มีค่า: <code>{ "@omitWhenNull": "$.unit", "@value": "{{ $.unit }}" }</code>
                  </p>
                  <v-alert
                    v-if="simulationTemplateNotice"
                    :type="simulationTemplateNoticeType"
                    variant="tonal"
                    density="compact"
                    class="mt-3 text-caption"
                    data-testid="tq2-item-simulation-template-message"
                  >
                    {{ simulationTemplateNotice }}
                  </v-alert>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>

          <template v-else-if="isShortForm">
            <!-- The card's OWN key — a free-text field, NOT the rule picker, for exactly the reason the CONFIRM
                 kinds above use one: this key names nothing in the TaxConfigRuleV2 registry. It is bookkeeping —
                 the turn contract addresses the focus question BY its FieldKey, so the card needs a name to be
                 answerable at all, and it is stored verbatim. The amounts have registry keys of their own, one per
                 row below; offering the registry picker here would invite the author to pick one of those. -->
            <v-text-field
              data-testid="tq2-item-shortform-fieldkey"
              v-model="item.fieldKey"
              label="Checkpoint key ของการ์ด (fieldKey) *"
              placeholder="เช่น deduc_form_done"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-identifier"
              :error="!!shortFormOwnKeyError"
              :error-messages="shortFormOwnKeyError ? [shortFormOwnKeyError] : []"
              hint="คีย์ประจำการ์ดใบนี้ — ไม่ใช่ช่องยอดเงิน (ยอดเงินลง Field ของแต่ละแถวด้านล่าง) · ห้ามซ้ำกับ Field ของแถวใดแถวหนึ่ง ไม่งั้น backend ปฏิเสธการบันทึกทั้งเวอร์ชัน"
              persistent-hint
              class="mt-3"
            />

            <!-- 🔴 The switch picks which member of the copy bags EVERY text box on this card shows and edits
                 (the card's four and each row's five), and which language the preview renders in. The counts say
                 how many seats each language covers, so an untranslated card is visible without opening the JSON. -->
            <div class="tq2-editor-header mt-4">
              <span class="tq2-label">ข้อความบนการ์ด</span>
              <v-btn-toggle
                v-model="cardLang"
                mandatory
                divided
                density="compact"
                variant="outlined"
                color="primary"
                data-testid="tq2-item-shortform-lang"
              >
                <v-btn
                  v-for="l in cardLangItems"
                  :key="l.value"
                  :value="l.value"
                  size="small"
                  :data-testid="`tq2-item-shortform-lang-${l.value}`"
                >
                  {{ l.title }}
                  <span v-if="l.count" class="tq2-lang-count" :class="{ 'tq2-lang-count--gap': !l.complete }">{{ l.count }}</span>
                </v-btn>
              </v-btn-toggle>
            </div>
            <v-alert
              v-if="cardLang !== 'th'"
              type="info"
              variant="tonal"
              density="compact"
              class="mb-3 text-caption"
              data-testid="tq2-item-shortform-lang-note"
            >
              กำลังแก้คำแปล {{ cardLangTitle }} ของทุกช่องบนการ์ดนี้ — ช่องว่างคือยังไม่ได้แปล
              (ตัวจางในช่องคือข้อความไทยต้นฉบับ) · ช่องที่ไม่มีข้อความไทยจะล็อกไว้ · แปลไม่ครบ =
              ลูกค้าภาษานี้เห็นข้อความไทยบนการ์ด — ดูผลจริงที่ตัวอย่างด้านขวา
            </v-alert>
            <v-text-field
              data-testid="tq2-item-shortform-title"
              :model-value="cardCopy('title')"
              :label="copyLabel('หัวการ์ด (title)', 'หัวการ์ด (title) *')"
              :placeholder="copyPlaceholder(cardSeat('title'), 'เช่น ระบุยอดเงินกองทุนของคุณ')"
              :persistent-placeholder="cardLang !== 'th'"
              :disabled="copyLocked(cardSeat('title'))"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-card-text-outline"
              hide-details
              @update:model-value="setShortFormCopy('title', $event)"
            />
            <v-text-field
              data-testid="tq2-item-shortform-subtitle"
              :model-value="cardCopy('subtitle')"
              :label="copyLabel('คำอธิบายใต้หัวการ์ด (subtitle)')"
              :placeholder="copyPlaceholder(cardSeat('subtitle'), 'เช่น กรอกยอดที่สะสมหรือลงทุนในปีภาษีนี้เฉพาะรายการที่คุณมี')"
              :persistent-placeholder="cardLang !== 'th'"
              :disabled="copyLocked(cardSeat('subtitle'))"
              variant="outlined"
              density="comfortable"
              hide-details
              class="mt-3"
              @update:model-value="setShortFormCopy('subtitle', $event)"
            />
            <!-- The unit used to live here, one per CARD. It moved down to the rows: a card mixes a money box
                 ("บาท") with a counter ("คน") and a checkbox (no unit at all), so one unit for the whole card was
                 always wrong for some of its boxes. Card copy is a plain full-width stack again. -->
            <v-text-field
              data-testid="tq2-item-shortform-submitlabel"
              :model-value="cardCopy('submitLabel')"
              :label="copyLabel('ป้ายปุ่มหลัก (submit)')"
              :placeholder="copyPlaceholder(cardSeat('submitLabel'), 'บันทึกและดำเนินการต่อ')"
              :persistent-placeholder="cardLang !== 'th'"
              :disabled="copyLocked(cardSeat('submitLabel'))"
              variant="outlined"
              density="comfortable"
              hide-details
              class="mt-3"
              @update:model-value="setShortFormCopy('submitLabel', $event)"
            />
            <v-text-field
              data-testid="tq2-item-shortform-secondarylabel"
              :model-value="cardCopy('secondaryLabel')"
              :label="copyLabel('ป้ายปุ่มรอง (secondary)')"
              :placeholder="copyPlaceholder(cardSeat('secondaryLabel'), 'ไม่มีรายการเหล่านี้')"
              :persistent-placeholder="cardLang !== 'th'"
              :disabled="copyLocked(cardSeat('secondaryLabel'))"
              variant="outlined"
              density="comfortable"
              hint="ปุ่มทางออกให้ลูกค้าที่ไม่มีรายการไหนเลย — ลบข้อความนี้จนว่าง = การ์ดไม่มีปุ่มรอง"
              persistent-hint
              class="mt-3"
              @update:model-value="setShortFormCopy('secondaryLabel', $event)"
            />

            <!-- 🔴 A JSON box and NOT a colour picker per hint, and that is a decision rather than a shortcut: the
                 app's card contract carries ~100 optional style keys and grows a few more with every redesign, so
                 a form with one control per hint would be out of date the week after it shipped — and a hint it did
                 not know about would be DROPPED on the next save (this editor rebuilds the config key by key).
                 The bag travels whole: this screen, the request, the backend and the wire all carry it without
                 anyone reading a key. -->
            <v-textarea
              data-testid="tq2-item-shortform-style"
              :model-value="shortFormStyleText"
              label="สไตล์การ์ด (JSON)"
              placeholder='{ "mainFrameColor": "#FFFFFF", "titleColor": "#1A1A1A", "titleFontSize": 18 }'
              variant="outlined"
              density="comfortable"
              rows="3"
              auto-grow
              class="mt-3 code-area"
              :error="!!shortFormStyleError"
              :error-messages="shortFormStyleError ? [shortFormStyleError] : []"
              hint="สี/ขนาดตัวอักษรของการ์ดทั้งใบ — คีย์ที่แอปอ่าน เช่น mainFrameColor · titleColor · titleFontSize · subtitleColor · submitLabelColor · submitFrameColor · secondaryLabelColor · secondaryBorderColor · secondaryLabelFontSize (เว้นว่าง = ใช้ธีมของแอป)"
              persistent-hint
              @update:model-value="setShortFormStyle(null, $event)"
            >
              <template #append-inner>
                <!-- 🔴 `@mousedown.prevent`: the button sits INSIDE the field, so without it the press also focuses
                     the textarea and drops the caret wherever the pointer landed — which, in a document that is about
                     to be reflowed, is not where the author was looking. -->
                <v-btn
                  data-testid="tq2-item-shortform-style-beautify"
                  icon
                  size="x-small"
                  variant="text"
                  :disabled="!canFormatJson(shortFormStyleText)"
                  @mousedown.prevent
                  @click.stop="beautifyStyle(null)"
                >
                  <v-icon size="18">mdi-code-braces</v-icon>
                  <v-tooltip activator="parent" location="top">จัดรูปแบบ JSON</v-tooltip>
                </v-btn>
              </template>
            </v-textarea>

            <div class="tq2-editor-header mt-4">
              <span class="tq2-label">
                ช่องกรอกยอดเงินบนการ์ด
                <span class="tq2-label-flag"> · ต้องมีอย่างน้อย 1 ช่อง (Field + หัวข้อ ครบ)</span>
              </span>
              <v-btn
                data-testid="tq2-item-shortform-add"
                size="small"
                variant="outlined"
                color="primary"
                @click="addShortFormField"
              >
                <v-icon start>mdi-plus</v-icon>เพิ่มช่อง
              </v-btn>
            </div>
            <!-- Order matters twice over: it is the top-to-bottom order the app draws the boxes in, AND it is what
                 `sameGroupAsPrevious` reads — a row only merges upward into the row that is currently above it, so
                 moving a row moves which block it belongs to. -->
            <div v-if="shortFormConfig.fields.length" class="tq2-row-list">
              <div
                v-for="(row, idx) in shortFormConfig.fields"
                :key="idx"
                class="tq2-shortform-row"
                :data-testid="`tq2-item-shortform-row-${idx}`"
              >
                <div class="d-flex align-center ga-2 mb-1">
                  <span class="tq2-row-index">{{ idx + 1 }}</span>
                  <v-chip
                    v-if="idx > 0 && row.sameGroupAsPrevious"
                    size="x-small"
                    variant="tonal"
                    color="primary"
                    :data-testid="`tq2-item-shortform-grouped-${idx}`"
                  >
                    <v-icon start size="13">mdi-arrow-up-left</v-icon>อยู่กลุ่มเดียวกับแถวบน
                  </v-chip>
                  <v-spacer />
                  <v-btn
                    size="x-small"
                    density="comfortable"
                    variant="text"
                    icon
                    aria-label="เลื่อนช่องขึ้น"
                    :disabled="idx === 0"
                    :data-testid="`tq2-item-shortform-up-${idx}`"
                    @click="moveShortFormField(idx, -1)"
                  >
                    <v-icon size="18">mdi-arrow-up</v-icon>
                    <v-tooltip activator="parent" location="top">เลื่อนขึ้น</v-tooltip>
                  </v-btn>
                  <v-btn
                    size="x-small"
                    density="comfortable"
                    variant="text"
                    icon
                    aria-label="เลื่อนช่องลง"
                    :disabled="idx === shortFormConfig.fields.length - 1"
                    :data-testid="`tq2-item-shortform-down-${idx}`"
                    @click="moveShortFormField(idx, 1)"
                  >
                    <v-icon size="18">mdi-arrow-down</v-icon>
                    <v-tooltip activator="parent" location="top">เลื่อนลง</v-tooltip>
                  </v-btn>
                  <v-btn
                    size="x-small"
                    density="comfortable"
                    variant="text"
                    icon
                    color="error"
                    aria-label="ลบช่องออกจากการ์ด"
                    :data-testid="`tq2-item-shortform-remove-${idx}`"
                    @click="removeShortFormField(idx, row.fieldKey || row.label)"
                  >
                    <v-icon size="18">mdi-close</v-icon>
                    <v-tooltip activator="parent" location="top">ลบ</v-tooltip>
                  </v-btn>
                </div>
                <div class="tq2-shortform-grid">
                  <!-- Same registry source as the FIELD_INPUT branch (`ruleOptions` / `loadingRules`), and a picker
                       for the same reason: the number the customer types is written to whatever item binds THIS key,
                       so a typo binds the amount to a name nothing in the version reads. -->
                  <v-autocomplete
                    :data-testid="`tq2-item-shortform-row-fieldkey-${idx}`"
                    v-model="row.fieldKey"
                    :items="ruleOptions"
                    item-title="title"
                    item-value="value"
                    :return-object="false"
                    label="ผูกกับ Field (fieldKey) *"
                    :loading="loadingRules"
                    :no-data-text="rulePickerNoData"
                    :error="shortFormDuplicateKeys.includes(String(row.fieldKey || '').trim().toLowerCase())"
                    variant="outlined"
                    density="compact"
                    hide-details
                    prepend-inner-icon="mdi-link-variant"
                  />
                  <v-text-field
                    :data-testid="`tq2-item-shortform-labelth-${idx}`"
                    :model-value="copyIn(row.label)"
                    @update:model-value="setShortFormRowCopy(row, 'label', $event)"
                    :label="copyLabel('หัวข้อกำกับช่อง', 'หัวข้อกำกับช่อง (ไทย)')"
                    :placeholder="copyPlaceholder(row.label, 'เช่น กองทุนสำรองเลี้ยงชีพ (PVD)')"
                    :persistent-placeholder="cardLang !== 'th'"
                    :disabled="copyLocked(row.label)"
                    variant="outlined"
                    density="compact"
                    hide-details
                  />
                  <!-- 🔴 How the row is DRAWN, not what it means. A counter and a money box travel the same way
                       (the value is the string "2" either way) and land in the same sibling item — this only
                       decides whether the customer gets a keyboard or a − n + control. Counting people with a
                       keyboard is what invites "2 คน" into a box the backend can only read as digits. -->
                  <v-select
                    :data-testid="`tq2-item-shortform-control-${idx}`"
                    v-model="row.control"
                    :items="shortFormControlOptions"
                    item-title="title"
                    item-value="value"
                    :return-object="false"
                    label="ชนิดช่อง"
                    variant="outlined"
                    density="compact"
                    hide-details
                    prepend-inner-icon="mdi-form-textbox"
                  />
                  <!-- Placeholder is grey hint text INSIDE a box, so it belongs to the money box alone: a counter
                       and a checkbox both open at a value and have nothing to hint. -->
                  <v-text-field
                    v-if="!row.control"
                    :data-testid="`tq2-item-shortform-placeholder-${idx}`"
                    :model-value="copyIn(row.placeholder)"
                    @update:model-value="setShortFormRowCopy(row, 'placeholder', $event)"
                    :label="copyLabel('Placeholder ในช่อง')"
                    :placeholder="copyPlaceholder(row.placeholder, '0')"
                    :persistent-placeholder="cardLang !== 'th'"
                    :disabled="copyLocked(row.placeholder)"
                    variant="outlined"
                    density="compact"
                    hide-details
                  />
                  <!-- Drawing bounds only — the server never clamps a submitted count against them (same rule as
                       the simulation card's `limit`). Left blank: min 0, no ceiling, step 1. -->
                  <div
                    v-else-if="row.control === 'STEPPER'"
                    class="tq2-shortform-bounds"
                    :data-testid="`tq2-item-shortform-bounds-${idx}`"
                  >
                    <v-text-field
                      :data-testid="`tq2-item-shortform-min-${idx}`"
                      v-model="row.min"
                      label="ต่ำสุด"
                      type="number"
                      placeholder="0"
                      variant="outlined"
                      density="compact"
                      hide-details
                    />
                    <v-text-field
                      :data-testid="`tq2-item-shortform-max-${idx}`"
                      v-model="row.max"
                      label="สูงสุด"
                      type="number"
                      placeholder="ไม่จำกัด"
                      variant="outlined"
                      density="compact"
                      hide-details
                    />
                    <v-text-field
                      :data-testid="`tq2-item-shortform-step-${idx}`"
                      v-model="row.step"
                      label="ก้าวละ"
                      type="number"
                      placeholder="1"
                      variant="outlined"
                      density="compact"
                      hide-details
                    />
                  </div>
                  <!-- The unit that trails THIS box, one per row: a card mixes money ("บาท") with a counter
                       ("คน"), which is why it is no longer one setting for the whole card.
                       🔴 SHOWN ON THE MONEY BOX ALONE, because that is the only row the app draws a suffix on:
                       `"number"` and `"checkbox"` rows "have no suffix view, so it is ignored there". Offering
                       the box on a counter would collect a "คน" that no customer ever sees.
                       🔴 Hidden, not cleared: flipping a row to Stepper/Checkbox and back keeps the unit the
                       author typed, the same way the placeholder box above survives the trip. -->
                  <v-text-field
                    v-if="!row.control"
                    :data-testid="`tq2-item-shortform-unit-${idx}`"
                    :model-value="copyIn(row.unitLabel)"
                    @update:model-value="setShortFormRowCopy(row, 'unitLabel', $event)"
                    :label="copyLabel('หน่วยท้ายช่อง (unitLabel)')"
                    :placeholder="copyPlaceholder(row.unitLabel, 'บาท')"
                    :persistent-placeholder="cardLang !== 'th'"
                    :disabled="copyLocked(row.unitLabel)"
                    hint="เว้นว่าง = แอปเติม «บาท» ให้เอง (ไม่ใช่ไม่มีหน่วย)"
                    persistent-hint
                    variant="outlined"
                    density="compact"
                  />
                  <!-- 🔴 Row 1 is forced OFF for display, not just disabled: a stored `true` on the first row (left
                       behind by a reorder) would otherwise show a switch that is on AND greyed out, i.e. a setting
                       the author can see but cannot turn off. The first row always opens a group on the wire. -->
                  <v-switch
                    :data-testid="`tq2-item-shortform-samegroup-${idx}`"
                    :model-value="idx === 0 ? false : row.sameGroupAsPrevious"
                    label="รวมกลุ่มกับแถวบน"
                    color="primary"
                    inset
                    density="compact"
                    hide-details
                    :disabled="idx === 0"
                    @update:model-value="row.sameGroupAsPrevious = $event === true"
                  />
                  <!-- 🔴 TWO seats, not one moved, and the NAMES now say which is which. `noteBelow` is what the
                       app draws UNDER the box; `noteAbove` is a SECOND caption for the caveat that has to be read
                       BEFORE the number is typed. The pair used to be `note`/`noteAbove`, which said nothing about
                       position — and this label, the DTO's doc and the preview each guessed "above" at some point,
                       each time making the preview disagree with every real phone.
                       🔴 The CONFIG says `noteBelow`; the WIRE still says `note`, because that is the key the phone
                       reads. The rename is author-facing only — see TaxShortFormFieldV2Dto. -->
                  <v-textarea
                    :data-testid="`tq2-item-shortform-noteabove-${idx}`"
                    :model-value="copyIn(row.noteAbove)"
                    @update:model-value="setShortFormRowCopy(row, 'noteAbove', $event)"
                    :label="copyLabel('คำอธิบายเหนือช่อง (noteAbove)')"
                    :placeholder="copyPlaceholder(row.noteAbove, 'ข้อความที่ต้องอ่านก่อนกรอก เช่น ให้ใช้ยอดจากช่องไหนของสลิป')"
                    :persistent-placeholder="cardLang !== 'th'"
                    :disabled="copyLocked(row.noteAbove)"
                    variant="outlined"
                    density="compact"
                    rows="2"
                    auto-grow
                    hint="แสดงเหนือ label และช่องกรอก — ตัวเล็กสีเทา ชิดขวาโดยค่าเริ่มต้น (ปรับได้ด้วย noteAboveTextAlign)"
                    persistent-hint
                    class="tq2-shortform-note"
                  />
                  <v-textarea
                    :data-testid="`tq2-item-shortform-note-${idx}`"
                    :model-value="copyIn(row.noteBelow)"
                    @update:model-value="setShortFormRowCopy(row, 'noteBelow', $event)"
                    :label="copyLabel('คำอธิบายใต้ช่อง (noteBelow)')"
                    :placeholder="copyPlaceholder(row.noteBelow, 'ข้อความกำกับยาว ๆ เช่น เงื่อนไข/เพดานตามกฎหมาย')"
                    :persistent-placeholder="cardLang !== 'th'"
                    :disabled="copyLocked(row.noteBelow)"
                    variant="outlined"
                    density="compact"
                    rows="2"
                    auto-grow
                    hint="แสดงใต้ช่องกรอก — บนแถว checkbox จะกลายเป็นข้อความของไอคอน ⓘ"
                    persistent-hint
                    class="tq2-shortform-note"
                  />
                  <!-- Same bag, one level down — see the card's box above for why it is JSON. -->
                  <v-textarea
                    :data-testid="`tq2-item-shortform-style-${idx}`"
                    :model-value="rowStyleText(row)"
                    label="สไตล์แถว (JSON)"
                    placeholder='{ "labelColor": "#1A1A1A", "labelFontSize": 14, "marginBottom": 24 }'
                    variant="outlined"
                    density="compact"
                    rows="2"
                    auto-grow
                    :error="!!rowStyleError(row)"
                    :error-messages="rowStyleError(row) ? [rowStyleError(row)] : []"
                    hint="คีย์ของแถวนี้ เช่น labelColor · placeholderColor · unitLabelColor · noteAboveColor · noteBelowColor — และคู่ …FontSize / …TextAlign (left · right · center) · ระยะห่างของแอป marginTop · marginBottom · paddingTop · paddingBottom · paddingLeft · paddingRight (ตัวเลข dp เช่น 24) — คีย์ระยะห่างต้องอยู่ในถุง style นี้เท่านั้น วางเป็นคีย์ของช่องตรง ๆ จะถูกเก็บไว้แต่การ์ดไม่เห็น"
                    persistent-hint
                    class="tq2-shortform-note code-area"
                    @update:model-value="setShortFormStyle(row, $event)"
                  >
                    <template #append-inner>
                      <!-- 🔴 `@mousedown.prevent`: the button sits INSIDE the field, so without it the press also focuses
                           the textarea and drops the caret wherever the pointer landed — which, in a document that is about
                           to be reflowed, is not where the author was looking. -->
                      <v-btn
                        :data-testid="`tq2-item-shortform-style-beautify-${idx}`"
                        icon
                        size="x-small"
                        variant="text"
                        :disabled="!canFormatJson(rowStyleText(row))"
                        @mousedown.prevent
                        @click.stop="beautifyStyle(row)"
                      >
                        <v-icon size="18">mdi-code-braces</v-icon>
                        <v-tooltip activator="parent" location="top">จัดรูปแบบ JSON</v-tooltip>
                      </v-btn>
                    </template>
                  </v-textarea>
                </div>
              </div>
            </div>
            <p v-else class="text-caption text-medium-emphasis mb-0" data-testid="tq2-item-shortform-empty">
              ยังไม่มีช่องกรอก — กด "เพิ่มช่อง"
            </p>

            <p class="text-caption text-medium-emphasis mt-3 mb-0">
              เปิด/ปิด "รวมกลุ่มกับแถวบน" คือวิธีทำให้สองแถวอยู่ในบล็อกเดียวกัน — แอปวาดเส้นคั่นระหว่างกลุ่ม
              (ปิด = แถวนี้ขึ้นบล็อกใหม่)
            </p>

            <!-- cardType / refId / version are the CONTRACT with the app, not copy: the app picks its renderer by
                 `cardType` and its layout by `refId`, so a value the app does not know renders nothing at all. They
                 stay editable — moving to a new card version has to be a data edit, not a redeploy — but folded
                 away, because nobody authoring a card should have to touch them. -->
            <v-expansion-panels variant="accordion" class="tq2-meta-panel mt-3">
              <v-expansion-panel data-testid="tq2-item-shortform-advanced">
                <v-expansion-panel-title>
                  <v-icon size="16" class="mr-2">mdi-cellphone-cog</v-icon>
                  สัญญากับแอป (cardType / refId / version) — ขั้นสูง
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div class="tq2-shortform-grid">
                    <v-text-field
                      data-testid="tq2-item-shortform-cardtype"
                      :model-value="shortFormConfig.cardType"
                      label="cardType"
                      placeholder="TAX_AMOUNT_FORM"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @update:model-value="setShortFormCopy('cardType', $event)"
                    />
                    <v-text-field
                      data-testid="tq2-item-shortform-refid"
                      :model-value="shortFormConfig.refId"
                      label="refId (รูปแบบหน้าจอ)"
                      placeholder="type7"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @update:model-value="setShortFormCopy('refId', $event)"
                    />
                    <v-text-field
                      data-testid="tq2-item-shortform-version"
                      :model-value="shortFormConfig.version"
                      label="version"
                      type="number"
                      min="1"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @update:model-value="setShortFormVersion($event)"
                    />
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- The WHOLE config as JSON — the same editor SUMMARY has had all along, and for the same reason:
                 the boxes above cover what the card usually needs, and this covers everything else. An admin can
                 paste a card whole, diff two items, or author a key this screen has no control for.
                 🔴 IT IS A VIEW OF THE SAME OBJECT, not a second document. Typing here rewrites the structured
                 editor above (through `normalizeShortFormConfig`, so `control` is still canonicalised and the
                 bounds still belong to the counter) and the boxes above rewrite what this shows. There is no
                 "which one wins" question because there is only ever one config.
                 🔴 Still `v-else-if="isShortForm"`'s child, NOT a new `v-if` chain — see the chain note above. -->
            <v-expansion-panels
              v-model="shortFormJsonPanel"
              variant="accordion"
              class="tq2-meta-panel mt-3"
            >
              <v-expansion-panel data-testid="tq2-item-shortform-json-panel">
                <v-expansion-panel-title>
                  <v-icon size="16" class="mr-2">mdi-file-code-outline</v-icon>
                  Card config (JSON) — แก้ทั้งใบ
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="text-caption text-medium-emphasis mb-3">
                    เอกสาร config ทั้งใบของการ์ดนี้ · แก้ตรงนี้แล้วช่องด้านบนเปลี่ยนตาม และแก้ด้านบนแล้วเปิดพาเนลนี้ใหม่จะเห็นค่าล่าสุด ·
                    <strong>คีย์ที่หน้าจอนี้ไม่มีช่องให้กรอกก็เขียนได้</strong> — ระบบเก็บไว้ให้ทั้งหมด ไม่ตัดทิ้ง ·
                    <strong>แต่คีย์ที่การ์ดจะเห็นจริงมีเฉพาะ</strong> ช่องที่หน้าจอนี้รู้จัก + สิ่งที่อยู่ใน <code>style</code> /
                    <code>fields[].style</code> — คีย์อื่นที่วางระดับการ์ด/ช่องตรง ๆ (เช่น <code>marginTop</code> ข้าง <code>label</code>)
                    จะถูกเก็บไว้แต่ไม่ถึงการ์ด และจะมีคำเตือนตอนบันทึก
                  </p>
                  <v-textarea
                    data-testid="tq2-item-shortform-json"
                    :model-value="shortFormConfigText"
                    label="Card config (JSON) *"
                    variant="outlined"
                    density="comfortable"
                    rows="14"
                    auto-grow
                    class="code-area"
                    :error="!!shortFormConfigJsonError"
                    :error-messages="shortFormConfigJsonError ? [shortFormConfigJsonError] : []"
                    hint="cardType · refId · version · title · subtitle · submitLabel · secondaryLabel · style · fields[] — ช่องกรอกยังมาจากตาราง fields[] เหมือนเดิม"
                    persistent-hint
                    @update:model-value="setShortFormConfigJson($event)"
                  >
                    <template #append-inner>
                      <!-- 🔴 `@mousedown.prevent`: the button sits INSIDE the field, so without it the press also focuses
                           the textarea and drops the caret wherever the pointer landed — which, in a document that is about
                           to be reflowed, is not where the author was looking. -->
                      <v-btn
                        data-testid="tq2-item-shortform-json-beautify"
                        icon
                        size="x-small"
                        variant="text"
                        :disabled="!canFormatJson(shortFormConfigText)"
                        @mousedown.prevent
                        @click.stop="beautifyShortFormConfigJson()"
                      >
                        <v-icon size="18">mdi-code-braces</v-icon>
                        <v-tooltip activator="parent" location="top">จัดรูปแบบ JSON</v-tooltip>
                      </v-btn>
                    </template>
                  </v-textarea>
                  <v-alert type="info" variant="tonal" density="compact" class="mt-3 text-caption">
                    ตารางช่องกรอกด้านบนคือ <code>fields[]</code> ในเอกสารนี้ — เพิ่ม/ลบแถวตรงไหนก็ได้ผลเหมือนกัน ·
                    ค่าที่ระบบ normalize ให้ (เช่น <code>control</code> เป็นตัวพิมพ์ใหญ่, บันไดตัวเลขเป็นของ STEPPER เท่านั้น)
                    จะถูกจัดรูปให้อัตโนมัติหลังพิมพ์
                  </v-alert>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- The JSON envelope the app is handed. A SECOND accordion, not three more boxes in the one above:
                 that panel holds the three values the app matches on, this one holds the whole shape they sit in.
                 🔴 SHAPE only. The list of boxes the customer types into still comes from "ช่องกรอกยอดเงินบนการ์ด"
                 above — the amount of a box invented down here would have nowhere to land, because the sibling
                 FIELD_INPUT lookup, the registry check and the postback reader all read that TABLE, never this
                 template (the orphan bug this card kind already recorded once, 2026-08-13).
                 🔴 Still `v-else-if="isShortForm"`'s child, NOT a new `v-if` chain — see the chain note above. -->
            <v-expansion-panels variant="accordion" class="tq2-meta-panel mt-3">
              <v-expansion-panel data-testid="tq2-item-shortform-template-panel">
                <v-expansion-panel-title>
                  <v-icon size="16" class="mr-2">mdi-code-json</v-icon>
                  โครง JSON ของการ์ด (ขั้นสูง)
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p class="text-caption text-medium-emphasis mb-3">
                    โครง JSON ที่ส่งออกไปให้แอปวาดการ์ดใบนี้ ·
                    <strong>ปล่อยว่าง = ใช้โครงมาตรฐานของระบบ</strong> (ไม่ต้องกรอกอะไรเลย) ·
                    รายการช่องยังมาจากตาราง "ช่องกรอกยอดเงินบนการ์ด" ด้านบนเหมือนเดิม —
                    ตรงนี้<strong>จัดโครงอย่างเดียว ไม่ได้สร้างช่องใหม่</strong>
                  </p>
                  <div class="tq2-editor-header">
                    <span class="tq2-label">
                      Card template (JSON)
                      <span class="tq2-label-flag"> · ว่าง = ใช้โครงมาตรฐานของระบบ</span>
                    </span>
                    <v-btn
                      data-testid="tq2-item-shortform-template-default"
                      size="small"
                      variant="outlined"
                      color="primary"
                      :loading="shortFormTemplateLoading"
                      :disabled="shortFormTemplateLoading"
                      @click="applyShortFormDefaultTemplate"
                    >
                      <v-icon start>mdi-file-restore-outline</v-icon>ใส่โครงเริ่มต้น
                    </v-btn>
                  </div>
                  <v-textarea
                    data-testid="tq2-item-shortform-template"
                    v-model="item.shortFormTemplateJson"
                    label="Card template (JSON)"
                    variant="outlined"
                    density="comfortable"
                    rows="12"
                    auto-grow
                    class="code-area"
                    :error="!!shortFormTemplateError"
                    :error-messages="shortFormTemplateError ? [shortFormTemplateError] : []"
                    :hint="shortFormTemplateHint"
                    persistent-hint
                  >
                    <template #append-inner>
                      <!-- 🔴 `@mousedown.prevent`: the button sits INSIDE the field, so without it the press also focuses
                           the textarea and drops the caret wherever the pointer landed — which, in a document that is about
                           to be reflowed, is not where the author was looking. -->
                      <v-btn
                        data-testid="tq2-item-shortform-template-beautify"
                        icon
                        size="x-small"
                        variant="text"
                        :disabled="!canFormatJson(item.shortFormTemplateJson)"
                        @mousedown.prevent
                        @click.stop="beautifyItemJson('shortFormTemplateJson')"
                      >
                        <v-icon size="18">mdi-code-braces</v-icon>
                        <v-tooltip activator="parent" location="top">จัดรูปแบบ JSON</v-tooltip>
                      </v-btn>
                    </template>
                  </v-textarea>
                  <!-- 🔴 `v-pre`: everything below is SAMPLE TEXT for the author to copy, and `{{ … }}` is Vue's own
                       interpolation syntax — without v-pre the compiler would try to evaluate `form.title` as an
                       expression of THIS component and print an empty string where the sample should be. -->
                  <p v-pre class="text-caption text-medium-emphasis mt-3 mb-0">
                    ตัวอย่าง ·
                    ค่าเดี่ยว: <code>"title": "{{ form.title }}"</code> ·
                    รายการที่ยาวไม่คงที่: <code>{ "@repeat": "form.groups", "@template": { … } }</code>
                    (ใน @template ใช้ <code>$</code> แทนสมาชิกตัวปัจจุบัน เช่น <code>"@repeat": "$.fields"</code>) ·
                    คีย์ที่ต้องหายไปทั้งคีย์เมื่อไม่มีค่า: <code>{ "@omitWhenNull": "$.note", "@value": "{{ $.note }}" }</code>
                  </p>
                  <v-alert
                    v-if="shortFormTemplateNotice"
                    :type="shortFormTemplateNoticeType"
                    variant="tonal"
                    density="compact"
                    class="mt-3 text-caption"
                    data-testid="tq2-item-shortform-template-message"
                  >
                    {{ shortFormTemplateNotice }}
                  </v-alert>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-alert type="info" variant="tonal" density="compact" class="mt-3 text-caption">
              ยอดที่ลูกค้ากรอกในการ์ดนี้ไม่ได้เก็บไว้ที่ข้อนี้ — แต่ละแถวจะไปลงที่
              <strong>ข้อ FIELD_INPUT ที่ผูก Field เดียวกันในเวอร์ชันนี้</strong> ·
              ถ้าเวอร์ชันนี้ยังไม่มีข้อที่ผูก Field นั้น ยอดจะไม่มีที่บันทึก
            </v-alert>
          </template>

          <!-- SAVE_STATE → nothing to bind and nothing to configure. No fieldKey field (the item stores no answer —
               it pins a name on the FORM, not on an answer row, and a FieldKey-less item is the one
               SeedUnansweredDefaults skips instead of seeding an empty gate fact) and no JSON box (it draws no card).
               Besides the stage-2 boxes inside its branch, it gets two settings just below this chain: the shared
               `stateCode` box and the App Bar `stateGroup` dropdown.
               🔴 `v-else-if`, NOT `v-if`: this is the LAST branch of the per-kind chain that starts at `isSummary`
               above, and the chain is what keeps exactly one kind editor on screen. Chaining is also why it sits
               HERE, immediately after `isShortForm` closes — the §40 catalog block below is a deliberate bare `v-if`
               (see its own note), so a branch appended after THAT would be chained to the catalog instead of to the
               kind chain, and the kind it belongs to would silently never render. -->
          <!-- MESSAGE → nothing to bind and nothing to configure either: the "question text" box above IS the whole
               item (the sentence the walk says on the way past, LLM-phrased through the ask-side prompts), so this
               branch only tells the author what the shared boxes mean for this kind.
               🔴 `v-else-if`, in the SAME chain, placed just before `isSaveState` for the reason its note gives. -->
          <template v-else-if="isMessage">
            <v-alert type="info" variant="tonal" density="compact" class="mt-3 text-caption">
              ข้อนี้ <strong>ไม่ถูกถามลูกค้า</strong> — เดินมาถึงเมื่อไหร่ ระบบจะ <strong>พูด</strong>
              "ข้อความคำถาม" ด้านล่างให้ลูกค้าฟัง (เรียบเรียงผ่าน LLM ตาม "คำสั่ง AI ตอนถาม" ของข้อนี้และภาษาของบทสนทนา
              แต่จะไม่ถูกแต่งเป็นคำถาม) แล้วเดินต่อทันทีในเทิร์นเดียวกัน ·
              พูด<strong>ครั้งเดียว</strong> (แสตมป์ว่าพูดแล้ว ตอน resume ไม่พูดซ้ำ) · ไม่มีปุ่มตอบ ·
              วางไว้<strong>ท้ายสุดของชุดคำถาม</strong> = ประโยคปิดฟอร์ม — พอพูดจบและไม่เหลือคำถาม ฟอร์มจะปิดเอง
              โดยไม่ต่อข้อความปิดฟอร์มกลางจาก Tax Planning Settings อีก (ข้อความนั้นเหลือใช้เฉพาะหน้า resume ของแอป)
            </v-alert>
          </template>

          <!-- USAGE_FUNNEL_STATE (#746) → a /TaxDashboard funnel marker: nothing to bind, one REQUIRED setting — which
               funnel state passing this point means. Stored in `stateGroup` (the column SAVE_STATE uses for its App Bar
               group — a different closed vocabulary, which is why the App Bar dropdown below stays SAVE_STATE-only).
               🔴 `v-else-if`, in the SAME chain, placed just before `isSaveState` for the reason its note gives. -->
          <template v-else-if="isUsageFunnelState">
            <v-alert type="info" variant="tonal" density="compact" class="mt-3 text-caption">
              ข้อนี้ <strong>ไม่ถูกถามลูกค้า</strong> — แค่เลือกว่าลูกค้าเดินมาถึงจุดนี้ = ผ่าน<strong>ขั้นไหน</strong>ของตาราง
              <strong>Usage Funnel by Form</strong> (หน้า TaxDashboard)
            </v-alert>
            <v-select
              data-testid="tq2-item-usage-funnel-state"
              :model-value="item.stateGroup || null"
              :items="usageFunnelStateItems"
              item-title="title"
              item-value="value"
              label="ขั้นใน Usage Funnel ที่จะนับ *"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-filter-variant"
              :error="!!usageFunnelStateError"
              :error-messages="usageFunnelStateError ? [usageFunnelStateError] : []"
              hint="ลูกค้าเดินผ่านข้อนี้ = ฟอร์มถูกนับในช่อง Complete ของขั้นนี้"
              persistent-hint
              class="mt-3"
              @update:model-value="(v) => { item.stateGroup = v || ''; }"
            />
          </template>

          <template v-else-if="isSaveState">
            <v-alert type="info" variant="tonal" density="compact" class="mt-3 text-caption">
              ข้อนี้ <strong>ไม่ถูกถามลูกค้า</strong> — ระบบเดินผ่านแล้วจดไว้ที่ฟอร์มว่า
              "ตอนนี้ลูกค้าเดินมาถึงฉากนี้" แล้วไปข้อถัดไปทันทีในเทิร์นเดียวกัน ·
              <strong>ข้อความคำถาม / คำสั่ง AI ตอนถาม / ปุ่มตอบ (quickReply) ด้านล่าง จะถูกใช้ตอนลูกค้ากลับมาทำฟอร์มต่อเท่านั้น</strong>
              (= <strong>จังหวะ 1</strong> — “ทำต่อไหมคะ?” [กรอกข้อมูลต่อเลย] [สร้างแผนใหม่])
              — แทนข้อความ resume กลางที่ตั้งไว้ใน Tax Planning Settings ·
              การ์ด SUMMARY/SIMULATE ที่ใส่ "ชื่อสถานะ" ตรงกับข้อนี้ จะถูกแสดงซ้ำให้ลูกค้าตอนนั้นด้วย
            </v-alert>

            <!-- ── จังหวะ 2 ของ resume: "นี่คือข้อมูลเดิมของคุณ — ใช้ต่อ หรือขอแก้?" ────────────────────────────
                 SAVE_STATE เท่านั้น (STATE_CONFIRM_KINDS) — พอลูกค้ากดปุ่มของจังหวะ 1 แล้ว บอทจะพูดข้อความนี้
                 พร้อมการ์ดของฉากนี้
                 🔴 "จำนวนปุ่ม" เป็นตัวกำหนดพฤติกรรม (เจ้าของงานเคาะ 2026-08-30):
                    ≥2 ปุ่ม = รอคำตอบ (ปุ่มที่ 1 ขอแก้ / ปุ่มที่ 2 ใช้ต่อ) ·
                    1 ปุ่ม  = รอคำตอบ แต่ปุ่มนั้นแปลว่า "ใช้ต่อ" เสมอ ·
                    0 ปุ่ม  = ไม่รอคำตอบ (lead-in) — พูดข้อความ + การ์ด แล้วต่อคำถามจริงในจอเดียวกัน
                 🔴 ทั้งสามช่องอยู่ในสาย `v-else-if` เดิม ไม่ได้ห้อยกับ `v-if="hasIncomeCatalog"` ข้างล่าง -->
            <div class="tq2-block mt-3">
              <div class="tq2-editor-header">
                <span class="tq2-label">
                  จังหวะ 2 ตอนกลับมาทำต่อ — สรุปข้อมูลเดิมแล้วถามยืนยัน
                  <span class="tq2-label-flag"> · เว้นว่าง = ไม่มีจังหวะ 2 (กด “ทำต่อ” แล้วไปคำถามจริงทันทีเหมือนเดิม) · จำนวนปุ่มด้านล่างเป็นตัวกำหนดว่าจะ “รอคำตอบ” หรือ “พูดนำแล้วไปต่อ”</span>
                </span>
              </div>
              <v-textarea
                data-testid="tq2-item-confirm-text"
                v-model="item.confirmTextTh"
                label="ข้อความจังหวะ 2 (ภาษาไทย)"
                placeholder="เช่น Genie ขอสรุปข้อมูลแผนภาษีที่คุณทำค้างไว้ล่าสุด ดังนี้นะคะ — คุณยังมีรายได้จากทางอื่นอีกหรือไม่คะ?"
                variant="outlined"
                density="comfortable"
                rows="3"
                auto-grow
                :error="!!stateConfirmTextError"
                :error-messages="stateConfirmTextError ? [stateConfirmTextError] : []"
                hint="ว่าง = ปิดจังหวะ 2 ทั้งจังหวะ (พฤติกรรมเดิม 100%) · ใส่แล้ว = พอกดปุ่มของจังหวะ 1 บอทจะพูดข้อความนี้ พร้อมการ์ด SUMMARY/SIMULATE ของฉากนี้ · มีปุ่มด้านล่าง = รอคำตอบก่อนถามคำถามจริง · ไม่มีปุ่มเลย = พูดข้อความนี้เป็นบรรทัดนำแล้วถามคำถามจริงต่อในจอเดียวกัน · บรรทัดท้ายที่ขึ้นต้นด้วย @whenNoCard: = ข้อความที่ใช้แทนเมื่อการ์ดของฉากยังไม่มีรายการให้แสดง (ลูกค้ายังไม่ได้กรอกอะไรในฉากนี้) — ไม่ขึ้นจอยืนยัน แต่ส่งข้อความนี้เข้า LLM พร้อมคำถามที่ค้างให้เรียบเรียงเป็นข้อความเดียว"
                persistent-hint
                class="mb-3"
              />

              <!-- ปุ่มของจังหวะ 2 — ทรงเดียวกับ "ปุ่มคำตอบด่วน (quick reply)" ด้านล่างของฟอร์มนี้เป๊ะ
                   (ป้ายล้วน ไม่มีรหัสซ่อน + ช่องแปล en/zh) แต่เก็บคนละคอลัมน์ เพราะปุ่มชุดนั้นเป็นของจังหวะ 1 -->
              <div data-testid="tq2-item-confirm-options" class="tq2-options-block">
                <div class="tq2-editor-header">
                  <span class="tq2-label">
                    ปุ่มของจังหวะ 2
                    <span class="tq2-label-flag"> · 2 ปุ่มขึ้นไป: ปุ่มที่ 1 = ขอแก้ · ปุ่มที่ 2 = ใช้ต่อ · ปุ่มเดียว = ใช้ต่อ · ไม่ใส่เลย = ไม่รอคำตอบ</span>
                  </span>
                  <div class="d-flex align-center ga-1">
                    <v-btn
                      data-testid="tq2-item-confirm-option-add"
                      size="small"
                      variant="outlined"
                      color="primary"
                      @click="addConfirmChip"
                    >
                      <v-icon start>mdi-plus</v-icon>เพิ่มปุ่ม
                    </v-btn>
                    <!-- Same translator, same rules, as the stage-1 quick-reply row below (`tq2-item-chip-translate`):
                         one shared method reads the Thai box of every row and OVERWRITES en/zh. Disabled while a
                         translation of THIS list is running and whenever no row has a Thai label to translate from. -->
                    <v-btn
                      data-testid="tq2-item-confirm-option-translate"
                      size="small"
                      color="primary"
                      variant="elevated"
                      :loading="translatingConfirmChips"
                      :disabled="translatingConfirmChips || !translatableConfirmChipCount"
                      @click="translateConfirmChips"
                    >
                      <v-icon start>mdi-translate</v-icon>
                      {{ translatingConfirmChips ? `Translating ${confirmTranslationProgress.completed}/${confirmTranslationProgress.total}` : 'Translate' }}
                    </v-btn>
                  </div>
                </div>
                <!-- 🔴 ลำดับคือความหมาย ไม่ใช่แค่การจัดหน้า: ระบบตัดสินจาก "ตำแหน่ง" ของปุ่ม ไม่ได้อ่านคำบนปุ่ม
                     สลับสองปุ่มนี้ = สลับผลลัพธ์ (กด “ใช้ข้อมูลเดิมต่อเลย” แล้วบอทกลับไปถามว่าจะแก้อะไร) -->
                <p class="text-caption text-medium-emphasis mb-2">
                  <b>ใส่ 2 ปุ่มขึ้นไป</b> — <b>ปุ่มที่ 1 = “ขอแก้ข้อมูล”</b> (บอทจะถามว่าจะแก้รายการไหน แล้วเปิดข้อนั้นให้ตอบใหม่ — ค่าเดิมไม่ถูกลบ) ·
                  <b>ปุ่มที่ 2 = “ใช้ข้อมูลเดิมต่อเลย”</b> (เดินไปถามคำถามจริงข้อถัดไป) ·
                  ระบบดูที่<b>ลำดับ</b> ไม่ได้ดูข้อความบนปุ่ม — สลับกัน = ผลสลับกัน ·
                  ช่องไทยคือป้ายต้นทาง อังกฤษ/จีนเป็นคำแปล (ไม่บังคับ)
                </p>
                <!-- 🔴 อีกสองทรงที่ระบบรองรับ (เจ้าของงานเคาะ 2026-08-30) — เขียนแยกย่อหน้าเพราะเป็นคนละกติกา
                     ไม่ใช่กรณีพิเศษของกติกา “ลำดับ” ข้างบน -->
                <p class="text-caption text-medium-emphasis mb-2">
                  <b>ใส่ปุ่มเดียว</b> = ปุ่มนั้นคือ <b>“ใช้ข้อมูลเดิมต่อเลย”</b> เสมอ (เช่น “ยืนยันข้อมูล”) —
                  จอนี้จะไม่มีปุ่ม “ขอแก้” ให้กด ลูกค้าที่อยากแก้ให้<b>พิมพ์</b>บอก (เช่น “ไม่ใช่” / “ขอแก้”) ระบบอ่านออก ·
                  <b>ไม่ใส่ปุ่มเลย</b> = โหมด <b>lead-in</b> — บอทพูดข้อความจังหวะ 2 + แสดงการ์ดของฉาก
                  แล้ว<b>ถามคำถามจริงต่อในจอเดียวกัน</b> (ไม่รอคำตอบ) ใช้กับฉากที่คำถามถัดไปมีปุ่ม/การ์ดของตัวเองอยู่แล้ว
                </p>
                <!-- 🔴 Same all-or-nothing rule as stage 1, and for the same reason: the runtime renders the WHOLE
                     row in one language, so one missing (or duplicated) translation drops the entire stage-2
                     button row back to Thai — not just the row that is missing it. -->
                <p class="text-caption text-medium-emphasis mb-2">
                  คำแปลใช้ได้ต่อเมื่อ <b>ครบทุกปุ่มของจังหวะ 2</b> และ <b>ไม่ซ้ำกันเอง</b> — ขาดช่องเดียวหรือซ้ำกัน
                  ปุ่มชุดนี้จะกลับไปเป็นภาษาไทยทั้งแถว
                </p>
                <div v-if="confirmChipRows.length" class="d-flex ga-2 mb-2">
                  <v-chip
                    v-for="b in confirmChipLangBadges"
                    :key="b.lang"
                    :data-testid="`tq2-item-confirm-option-badge-${b.lang}`"
                    size="small"
                    :color="b.ok ? 'success' : 'warning'"
                    variant="tonal"
                  >{{ b.text }}</v-chip>
                </div>
                <div v-if="confirmChipRows.length" class="tq2-row-list">
                  <div class="tq2-multilingual-chip-head" aria-hidden="true">
                    <span class="tq2-row-index">#</span>
                    <span>Thai</span>
                    <span>English</span>
                    <span>Chinese</span>
                    <span class="tq2-chip-action-head"></span>
                  </div>
                  <div
                    v-for="(row, idx) in confirmChipRows"
                    :key="idx"
                    class="tq2-option-row tq2-multilingual-option-row"
                  >
                    <span class="tq2-row-index">{{ idx + 1 }}.</span>
                    <v-text-field
                      :data-testid="`tq2-item-confirm-option-th-${idx}`"
                      :model-value="row.label"
                      :label="confirmChipRoleLabel(idx)"
                      :placeholder="idx === 0 ? 'เช่น อัปเดตข้อมูล' : 'เช่น ใช้ข้อมูลเดิมต่อเลย'"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @update:model-value="setConfirmChipLabel(idx, $event)"
                    />
                    <v-text-field
                      :data-testid="`tq2-item-confirm-option-en-${idx}`"
                      :model-value="row.labelEn"
                      label="English"
                      :placeholder="idx === 0 ? 'e.g. Update my answers' : 'e.g. Continue with this'"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @update:model-value="setConfirmChipLanguage(idx, 'labelEn', $event)"
                    />
                    <v-text-field
                      :data-testid="`tq2-item-confirm-option-zh-${idx}`"
                      :model-value="row.labelZh"
                      label="Chinese"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @update:model-value="setConfirmChipLanguage(idx, 'labelZh', $event)"
                    />
                    <v-btn
                      size="small"
                      density="comfortable"
                      variant="text"
                      icon
                      color="error"
                      aria-label="ลบปุ่มของจังหวะ 2"
                      :data-testid="`tq2-item-confirm-option-remove-${idx}`"
                      @click="removeConfirmChip(idx, row.label)"
                    >
                      <v-icon size="18">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </div>
                <v-alert
                  v-else
                  data-testid="tq2-item-confirm-options-empty"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="text-caption"
                >
                  ยังไม่มีปุ่มของจังหวะ 2 = โหมด <b>lead-in</b> — บอทจะพูดข้อความจังหวะ 2 + แสดงการ์ดของฉาก
                  แล้วถามคำถามจริงต่อใน<b>จอเดียวกัน</b> โดยไม่รอคำตอบ (เหมาะกับฉากที่คำถามถัดไปมีปุ่ม/การ์ดของตัวเองอยู่แล้ว) ·
                  ถ้าต้องการให้<b>รอคำตอบ</b> ให้ใส่ปุ่ม: 1 ปุ่ม = “ใช้ข้อมูลเดิมต่อเลย” · 2 ปุ่ม = ขอแก้ / ใช้ต่อ ตามลำดับด้านบน
                </v-alert>
                <v-alert
                  v-if="confirmChipTranslationMessage"
                  :type="confirmChipTranslationMessageType"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                  data-testid="tq2-item-confirm-option-translation-message"
                >
                  {{ confirmChipTranslationMessage }}
                </v-alert>
              </div>

              <!-- ── ข้ามจังหวะ 2 แล้วยกข้อความ/ปุ่มไปใช้กับคำถามถัดไป ──────────────────────────────────
                   ฉากที่หมุดกับคำถามถัดไป "ถามเรื่องเดียวกัน" (เช่น ฉากสรุปภาษี + CONFIRM ที่ทวนการ์ดใบเดิม)
                   ลูกค้าจะโดนถามยืนยันสองจอติดกัน ติ๊กช่องนี้แล้วเหลือจอเดียว: ไม่พักฟอร์ม เดินไปถามคำถามจริง
                   ในเทิร์นเดียวกัน โดยใช้ข้อความ + ปุ่มของจังหวะ 2 แทนของข้อนั้น
                   🔴 ต้องมีทั้งข้อความและปุ่ม — ไม่มีข้อความ = ไม่มีอะไรให้ยืม · ไม่มีปุ่ม = เป็น lead-in อยู่แล้ว -->
              <v-checkbox
                data-testid="tq2-item-confirm-skip-to-question"
                v-model="item.confirmSkipToQuestion"
                :disabled="!confirmSkipToQuestionAvailable"
                density="comfortable"
                hide-details="auto"
                class="mt-1"
                :error="!!stateConfirmSkipError"
                :error-messages="stateConfirmSkipError ? [stateConfirmSkipError] : []"
              >
                <template #label>
                  <span>
                    ข้ามจอยืนยันของจังหวะ 2 — เอาข้อความและปุ่มนี้ไปใช้กับ<b>คำถามถัดไป</b>แทน
                    <span class="tq2-label-flag d-block">
                      ใช้กับฉากที่คำถามถัดจากหมุดถามเรื่องเดียวกับจอนี้ (เช่น CONFIRM ที่ทวนการ์ดใบเดิม) ·
                      ติ๊กแล้วลูกค้าเห็นจอเดียว คำตอบที่กดจะตกไปที่คำถามจริงตามปกติ ·
                      ต้องมีทั้งข้อความจังหวะ 2 และปุ่มอย่างน้อย 1 ปุ่ม
                    </span>
                  </span>
                </template>
              </v-checkbox>

              <!-- ── หลังแก้แผนที่จบแล้ว ให้กลับมาที่ฉากนี้ (postCompleteEditReturn) ─────────────────────────────
                   SAVE_STATE เท่านั้น (STATE_CONFIRM_KINDS — fence เดียวกับ confirmSkipToQuestion ข้างบน)
                   ลูกค้าที่วางแผนจบแล้วพิมพ์ขอแก้ข้อที่เคยตอบ ⇒ เปิดโหมดแก้หลังจบ ถามเฉพาะข้อนั้น แล้วพากลับมาที่ฉาก
                   ของหมุดนี้ (การ์ดที่ติดชื่อสถานะเดียวกัน + ทุกข้อหลังหมุด) โดยไม่ถามข้อระหว่างทางซ้ำ
                   🔴 ไม่มีเงื่อนไขก่อนติ๊ก (ไม่ disabled) · ติ๊กได้หมุดเดียวต่อชุดคำถาม (backend ปฏิเสธใบที่สองตอน Save)
                   🔴 ไม่ติ๊กเลยทุกหมุด = ฟีเจอร์ปิด = แผนที่จบแล้วแก้ด้วยการพิมพ์ไม่ได้ (เหมือนเดิม) -->
              <v-checkbox
                data-testid="tq2-item-post-complete-edit-return"
                v-model="item.postCompleteEditReturn"
                density="comfortable"
                hide-details="auto"
                class="mt-1"
              >
                <template #label>
                  <span>
                    หลังแก้แผนที่จบแล้ว ให้กลับมาที่ฉากนี้
                    <span class="text-caption text-medium-emphasis d-block">
                      เมื่อลูกค้าขอแก้ข้อที่เคยตอบบนแผนที่จบแล้ว ระบบจะเปิดโหมดแก้หลังจบ ถามเฉพาะข้อนั้น
                      แล้วพากลับมาที่ฉากของหมุดนี้ (การ์ดของฉาก + ทุกข้อหลังหมุด) โดยไม่ถามข้อระหว่างทางซ้ำ ·
                      ติ๊กได้หมุดเดียวต่อชุดคำถาม · ไม่ติ๊กเลย = แผนที่จบแล้วแก้ไม่ได้ (เหมือนเดิม)
                    </span>
                  </span>
                </template>
              </v-checkbox>

              <v-textarea
                data-testid="tq2-item-confirm-prompt"
                v-model="item.confirmSystemPrompt"
                label="คำสั่ง AI ของจังหวะ 2 (ไม่บังคับ)"
                variant="outlined"
                density="comfortable"
                rows="3"
                auto-grow
                hint="เว้นว่าง = ใช้ “คำสั่ง AI ตอนถาม (Question System Prompt)” ของข้อนี้เอง · ใช้สำหรับเรียบเรียง/แปลข้อความจังหวะ 2 เท่านั้น ไม่ใช่กฎสกัดคำตอบ (จังหวะนี้ไม่บันทึกค่าอะไรเลย)"
                persistent-hint
                class="mt-3"
              />
            </div>
          </template>

          <!-- ── The save-slot name. OUTSIDE the per-kind chain above on purpose: three kinds share this one box
               (SAVE_STATE writes the slot, SUMMARY / SIMULATE name the slot they belong to), and a chain branch can
               only ever match one of them. Its own `v-if`, so it neither joins nor ends that chain.
               🔴 The list is `STATECODE_KINDS`, IMPORTED from the store — the same list `buildItemRequest` uses to
               decide whether to send the value, so the box can never be offered for a kind the payload NULLs. -->
          <v-text-field
            v-if="showStateCode"
            data-testid="tq2-item-state-code"
            v-model="item.stateCode"
            maxlength="60"
            :label="isSaveState ? 'ชื่อสถานะที่จะบันทึก (stateCode) *' : 'ชื่อสถานะที่การ์ดใบนี้ผูกอยู่ (stateCode)'"
            placeholder="เช่น AFTER_INCOME"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-bookmark-outline"
            :error="!!saveStateCodeError"
            :error-messages="saveStateCodeError ? [saveStateCodeError] : []"
            :hint="isSaveState
              ? 'ตั้งชื่อ “ฉาก” ตรงจุดนี้ของฟอร์ม — พอลูกค้ากลับมาทำต่อ ระบบจะกลับมาอ่านข้อนี้เพื่อเอาข้อความและปุ่มไปใช้ทักทาย · ใส่ได้ชื่อเดียว (ห้ามใส่ , )'
              : 'ไม่บังคับ — ประกาศว่าการ์ดใบนี้เป็นของฉากไหน เพื่อให้ถูกแสดงซ้ำตอนลูกค้ากลับมาทำต่อ · '
                + 'ต้องพิมพ์ให้ตรงกับชื่อที่ตั้งไว้ที่ข้อ SAVE_STATE เป๊ะ ๆ (เว้นว่าง/พิมพ์ไม่ตรง = การ์ดจะไม่ถูกหยิบมาโชว์ตอน resume) · '
                + 'ผูกได้หลายฉาก คั่นด้วยจุลภาค เช่น RESUME_SIM_SUMMARY,RESUME_PRODUCT_RECOMMEND (รวมกันไม่เกิน 60 ตัวอักษร)'"
            persistent-hint
            class="mt-3"
          />

          <!-- ── App Bar group state. Same placement rule as the stateCode box above: its own bare `v-if`, OUTSIDE the
               per-kind chain, so it neither joins nor ends it. Gated by `STATEGROUP_KINDS` IMPORTED from the store
               (the list `buildItemRequest` uses). The rows come from `api/v1/BarManagement/options` — 🔴 never type
               the values here. Not required: blank = the walker leaves the form's group as it was.
               🔴 `:model-value` maps '' → null: Vuetify treats '' as a selected (empty) row, and the clear button
               emits null, which is stored back as '' like every other blank box on `item`. -->
          <v-select
            v-if="showStateGroup"
            data-testid="tq2-item-state-group"
            :model-value="item.stateGroup || null"
            :items="stateGroupItems"
            item-title="title"
            item-value="value"
            label="กลุ่มสถานะ (group state)"
            :loading="loadingStateGroupOptions"
            :no-data-text="stateGroupOptionsFailed ? 'โหลดรายการไม่สำเร็จ — ปิดแล้วเปิดหน้าต่างนี้ใหม่เพื่อลองอีกครั้ง' : 'ไม่มีรายการ'"
            clearable
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-flag-outline"
            hint="ลูกค้าเดินผ่านหมุดนี้แล้ว ฟอร์มจะถูกนับว่าค้างอยู่ในกลุ่มนี้ — ใช้กับเงื่อนไข “Pending in Specific State” ของ Bar Management และ Proactive Message · เว้นว่าง = คงกลุ่มเดิมของฟอร์ม"
            persistent-hint
            class="mt-3"
            @update:model-value="(v) => { item.stateGroup = v || ''; }"
          />

          <!-- INCOME_CLASSIFIER → §40 category catalog (code/flagKey/labelTh/keywords/metadata)
               🔴 `v-if`, NOT `v-else-if`: the fieldKey field above already matches INCOME_CLASSIFIER, so chaining
               this branch onto it made the whole catalog editor unreachable — a classifier opened for editing showed
               only its key, and the 8 §40 categories that drive both the ambiguity flags and the chip row were
               invisible (and un-editable) even though the save payload kept round-tripping them. -->
          <div v-if="hasIncomeCatalog" class="mt-3">
            <div class="tq2-editor-header">
              <span class="tq2-label">
                {{ isIncomeClassify ? 'หมวดเงินได้ §40 ที่ข้อนี้คุมบทสนทนาให้' : 'ประเภทเงินได้ §40 ที่ให้ AI จำแนก' }}
                <span class="tq2-label-flag"> · ต้องมีอย่างน้อย 1 ประเภท (รหัส/flag/ชื่อ ครบ)</span>
              </span>
              <v-btn data-testid="tq2-item-income-add" size="small" variant="outlined" color="primary" @click="addIncomeCategory">
                <v-icon start>mdi-plus</v-icon>เพิ่มประเภท
              </v-btn>
            </div>
            <v-alert v-if="isIncomeClassify" type="info" variant="tonal" density="compact" class="mb-2 text-caption">
              ข้อนี้คุมแค่ <strong>ลำดับการถาม</strong> — <strong>ข้อความคำถามและกฎการสกัดคำตอบยังอยู่ที่ข้อ
              <code>INCOME_40_x</code> เหมือนเดิม</strong> แก้ที่ข้อนั้นที่เดียว มีผลทั้งตอนถามผ่านบทสนทนานี้และตอนถามข้อนั้นตรง ๆ
              · ข้างล่างแสดงประโยคของแต่ละหมวดให้ดูเฉย ๆ (แก้ไม่ได้จากตรงนี้)
            </v-alert>
            <div v-if="item.incomeCategories.length" class="tq2-row-list">
              <div v-for="(cat, idx) in item.incomeCategories" :key="idx" class="tq2-income-row">
                <div class="d-flex align-center ga-2 mb-1">
                  <span class="tq2-row-index">{{ idx + 1 }}</span>
                  <span v-if="isIncomeClassify && cat.labelTh" class="text-caption text-medium-emphasis">{{ cat.labelTh }}</span>
                  <v-spacer />
                  <v-btn size="x-small" density="comfortable" variant="text" icon aria-label="ลบประเภท" :data-testid="`tq2-item-income-remove-${idx}`" @click="removeIncomeCategory(idx, cat.labelTh || cat.code)">
                    <v-icon size="18">mdi-close</v-icon>
                    <v-tooltip activator="parent" location="top">ลบ</v-tooltip>
                  </v-btn>
                </div>
                <div class="tq2-income-grid">
                  <v-text-field :data-testid="`tq2-item-income-code-${idx}`" v-model="cat.code" label="รหัส (code) *" placeholder="40_1" variant="outlined" density="compact" hide-details prepend-inner-icon="mdi-identifier" />
                  <v-text-field :data-testid="`tq2-item-income-flagkey-${idx}`" v-model="cat.flagKey" label="Flag key *" placeholder="type_40_1" variant="outlined" density="compact" hide-details prepend-inner-icon="mdi-flag-variant" />
                  <v-text-field :data-testid="`tq2-item-income-labelth-${idx}`" v-model="cat.labelTh" label="ชื่อประเภท (ไทย) *" placeholder="เงินเดือน/ค่าจ้าง" variant="outlined" density="compact" hide-details class="tq2-income-label" />
                  <v-combobox :data-testid="`tq2-item-income-keywords-${idx}`" v-model="cat.keywords" label="คำที่เกี่ยวข้อง (keywords)" placeholder="พิมพ์แล้ว Enter" multiple chips closable-chips :return-object="false" variant="outlined" density="compact" hide-details class="tq2-income-keywords" />
                </div>

                <!-- ── WIRING (INCOME_CLASSIFY only) ───────────────────────────────────────────────────────
                     🔴 Pointers, never wording. This block once carried four textareas holding a COPY of the §40
                     item's question and extraction rules, and that copy silently outranked the item: editing the
                     question on the screen that shows it changed nothing about what the customer heard, and the two
                     drifted apart with no warning on either screen. What is left here is the part only this item can
                     answer — which sibling row each section writes to. The words are read live from that row, and
                     shown read-only below so the author can see which text this section will actually use.
                     🔴 Hidden for INCOME_CLASSIFIER on purpose: that kind fans nothing out, and `buildItemRequest`
                     strips these members from its payload, so an editor here would offer settings that vanish. -->
                <template v-if="isIncomeClassify">
                  <div class="tq2-mini-section mt-3">
                    <div class="tq2-mini-title">
                      <v-icon size="16" class="mr-1">mdi-arrow-right-bottom</v-icon>
                      ปลายทางของหมวดนี้
                    </div>
                    <div class="tq2-income-grid">
                      <v-select
                        :data-testid="`tq2-item-income-target-${idx}`"
                        v-model="cat.incomeFieldKey"
                        :items="fanOutTargetOptions"
                        item-title="title"
                        item-value="value"
                        label="ปลายทางที่ยอดไปลง (incomeFieldKey) *"
                        :no-data-text="fanOutNoData"
                        variant="outlined"
                        density="compact"
                        prepend-inner-icon="mdi-target"
                        clearable
                        hide-details
                      />
                      <v-select
                        :data-testid="`tq2-item-income-period-${idx}`"
                        v-model="cat.periodFieldKey"
                        :items="fanOutTargetOptions"
                        item-title="title"
                        item-value="value"
                        label="คำถามนำก่อนถามยอด (periodFieldKey)"
                        :no-data-text="fanOutNoData"
                        variant="outlined"
                        density="compact"
                        prepend-inner-icon="mdi-help-rhombus-outline"
                        clearable
                        hide-details
                      />
                    </div>
                    <!-- The §40 items this section drives, READ-ONLY and drawn like the rows they are in the item
                         list — same kind chip, same FieldKey styling, same answer-kind chip, same VisibleWhen
                         wording. Shown rather than hidden because "which row is this, and what does it actually
                         ask" is the question an author opens this panel with, and answering it here is what
                         removes the temptation to keep a second copy. Editing happens on that item, where every
                         other reader sees it too. -->
                    <div
                      v-for="(preview, pIdx) in sectionWording(cat)"
                      :key="pIdx"
                      :data-testid="`tq2-item-income-wording-${idx}-${pIdx}`"
                      :class="['tq2-wording', { 'tq2-wording-dangling': preview.missing }]"
                    >
                      <div class="tq2-wording-head">
                        <span class="tq2-wording-role">
                          <v-icon size="13" class="mr-1">{{ preview.roleIcon }}</v-icon>{{ preview.roleLabel }}
                        </span>
                        <span v-if="preview.order" class="tq2-wording-order">@{{ preview.order }}</span>
                        <v-chip v-if="!preview.missing" :color="preview.kindColor" variant="tonal" size="x-small" label>
                          {{ preview.itemKind }}
                        </v-chip>
                        <span class="tq2-wording-key">{{ preview.fieldKey }}</span>
                        <v-chip
                          v-if="!preview.missing"
                          :color="preview.answer.color"
                          :prepend-icon="preview.answer.icon"
                          variant="tonal"
                          size="x-small"
                          label
                        >
                          {{ preview.answer.label }}
                          <v-tooltip activator="parent" location="top">{{ preview.answer.hint }}</v-tooltip>
                        </v-chip>
                        <v-chip v-if="preview.isRequired" color="success" variant="tonal" size="x-small" label>
                          <v-icon start size="12">mdi-asterisk</v-icon>จำเป็น
                        </v-chip>
                        <v-chip v-if="preview.inactive" color="error" variant="tonal" size="x-small" label>
                          <v-icon start size="12">mdi-eye-off-outline</v-icon>ปิดใช้งาน
                          <v-tooltip activator="parent" location="top">
                            ข้อนี้ถูกปิดอยู่ — ลูกค้าจะไม่ถูกถาม และยอดของหมวดนี้จะไม่มีที่ลง
                          </v-tooltip>
                        </v-chip>
                        <v-spacer />
                        <span v-if="preview.gateLabel" class="tq2-wording-gate">
                          {{ preview.gateLabel }}
                          <v-tooltip activator="parent" location="top">{{ preview.gateRaw }}</v-tooltip>
                        </span>
                      </div>
                      <div
                        v-if="preview.missing"
                        class="tq2-wording-body tq2-wording-missing"
                      >
                        เวอร์ชันนี้ไม่มีข้อไหนผูก FieldKey นี้ — คำถามจะไม่ถูกถาม และคำตอบไม่มีที่บันทึก
                      </div>
                      <template v-else>
                        <div :class="['tq2-wording-body', { 'tq2-wording-missing': !preview.text }]">
                          {{ preview.text || 'ข้อนี้ยังไม่ได้ใส่ข้อความคำถาม — ระบบจะถามด้วยประโยคสำรอง' }}
                        </div>
                        <div class="tq2-wording-foot">
                          <v-icon size="13" class="mr-1">mdi-robot-outline</v-icon>
                          <span :class="{ 'tq2-wording-missing': !preview.hasAnswerPrompt }">
                            {{ preview.hasAnswerPrompt ? 'มีกฎการสกัดคำตอบของข้อนี้' : 'ยังไม่มีกฎการสกัดคำตอบ — ระบบใช้กฎกลาง' }}
                          </span>
                          <span v-if="preview.label" class="tq2-wording-label">{{ preview.label }}</span>
                        </div>
                      </template>
                    </div>

                    <div class="tq2-editor-header mt-3">
                      <span class="tq2-label">
                        ประเภทย่อยของหมวดนี้ (subTypes)
                        <span class="text-medium-emphasis"> · ว่าง = หมวดนี้ไม่ต้องถามประเภทย่อย</span>
                      </span>
                      <v-btn size="x-small" variant="text" color="primary" :data-testid="`tq2-item-income-subtype-add-${idx}`" @click="addIncomeSubType(cat)">
                        <v-icon start size="16">mdi-plus</v-icon>เพิ่มประเภทย่อย
                      </v-btn>
                    </div>
                    <div v-for="(sub, sIdx) in cat.subTypes" :key="sIdx" class="tq2-subtype-row">
                      <v-text-field :data-testid="`tq2-item-income-subtype-code-${idx}-${sIdx}`" v-model="sub.code" label="รหัส (code) *" placeholder="BIZ_TRADING" variant="outlined" density="compact" hide-details />
                      <v-text-field :data-testid="`tq2-item-income-subtype-label-${idx}-${sIdx}`" v-model="sub.labelTh" label="ชื่อ (ไทย) *" placeholder="ซื้อมาขายไป" variant="outlined" density="compact" hide-details />
                      <v-btn size="x-small" density="comfortable" variant="text" icon aria-label="ลบประเภทย่อย" :data-testid="`tq2-item-income-subtype-remove-${idx}-${sIdx}`" @click="removeIncomeSubType(cat, sIdx, sub.labelTh || sub.code)">
                        <v-icon size="18">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>

                <v-expansion-panels variant="accordion" class="tq2-meta-panel mt-2">
                  <v-expansion-panel>
                    <v-expansion-panel-title>
                      <v-icon size="16" class="mr-2">mdi-tag-multiple-outline</v-icon>
                      Metadata (provenance — ทางเลือก)
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div class="tq2-income-grid">
                        <v-text-field :data-testid="`tq2-item-income-asset-${idx}`" v-model="cat._meta.assetCategory" label="assetCategory" variant="outlined" density="compact" hide-details />
                        <v-text-field :data-testid="`tq2-item-income-property-${idx}`" v-model="cat._meta.propertyType" label="propertyType" variant="outlined" density="compact" hide-details />
                        <v-text-field :data-testid="`tq2-item-income-profession-${idx}`" v-model="cat._meta.professionType" label="professionType" variant="outlined" density="compact" hide-details />
                        <v-text-field :data-testid="`tq2-item-income-business-${idx}`" v-model="cat._meta.businessType" label="businessType" variant="outlined" density="compact" hide-details />
                      </div>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </div>
            <p v-else class="text-caption text-medium-emphasis mb-0">ยังไม่มีประเภท — กด "เพิ่มประเภท"</p>
          </div>

        </div>

        <!-- ── BLOCK: สิ่งที่ผู้ใช้เห็น ─────────────────────────────── -->
        <div class="tq2-block">
          <!-- USAGE_FUNNEL_STATE (#746): nothing on this item is ever shown to the customer — the box is only a note
               for the admin, so it must not be labelled as customer-facing copy. -->
          <h4 class="tq2-block-title">{{ isUsageFunnelState ? 'ข้อมูลของข้อ (ลูกค้าไม่เห็น)' : 'สิ่งที่ผู้ใช้เห็น' }}</h4>
          <v-textarea
            data-testid="tq2-item-questiontext"
            v-model="item.questionTextTh"
            :label="isUsageFunnelState ? 'คำอธิบาย (ไม่บังคับ · ลูกค้าไม่เห็น)' : 'ข้อความคำถาม (ภาษาไทย) — สิ่งที่ผู้ใช้เห็น'"
            placeholder="เช่น ปีนี้คุณจ่ายเบี้ยประกันชีวิตของตัวเองไปเท่าไรคะ"
            variant="outlined"
            density="comfortable"
            rows="2"
            auto-grow
          />
          <v-text-field
            data-testid="tq2-item-label"
            v-model="item.label"
            label="Label (สั้น ๆ สำหรับ admin) *"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-format-title"
            :rules="[rules.required]"
          />
          <!-- ── คำที่ลูกค้าใช้เรียกข้อนี้ (editKeywords) ───────────────────────────────────────────────
               EDIT_KEYWORD_KINDS เท่านั้น (kind ที่เป็นเป้าของการขอแก้ได้ — store ส่ง [] ให้ kind อื่นเสมอ)
               ตัวจับคำขอแก้นับทุกคำในนี้เป็น "ชื่อ" ของข้อเท่ากับ Label (ข้อความต้องมีคำทั้งคำ · ยาวสุดชนะ)
               และส่งให้ AI ของหมวดขอแก้เป็นคำใบ้ · เมนู «จะแก้รายการไหน» ยังพิมพ์ Label เดิม (คำเรียกไม่ถูกโชว์)
               SUMMARY (การ์ดสรุป) ใช้ช่องเดียวกันแต่ความหมายต่าง: คำเรียกใช้ "ดึงการ์ดสรุปมาแสดง" ขณะคำถามอื่นค้างจอ
               (ไม่ใช่เป้าของการขอแก้) ⇒ label/placeholder/hint สลับตาม kind ผ่าน editKeywordsLabel/…Placeholder/…Hint
               🔴 bare v-if ตั้งใจ: บล็อก «สิ่งที่ผู้ใช้เห็น» ไม่ใช่ chain v-if/v-else-if ของ kind editor
                  (precedent: v-row v-if="showWidget" ข้างล่าง) -->
          <v-combobox
            v-if="showEditKeywords"
            data-testid="tq2-item-edit-keywords"
            v-model="item.editKeywords"
            :label="editKeywordsLabel"
            :placeholder="editKeywordsPlaceholder"
            :hint="editKeywordsHint"
            persistent-hint
            multiple
            chips
            closable-chips
            :return-object="false"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-tag-text-outline"
            class="mb-3"
          />
          <v-textarea
            data-testid="tq2-item-helptext"
            v-model="item.helpText"
            label="Help text (คำอธิบายเสริม)"
            variant="outlined"
            density="comfortable"
            rows="2"
            auto-grow
          />

          <!-- inputType/answerType stay gated on SCALAR_KINDS — the save payload NULLs them for the other
               kinds, so showing them there would offer a setting that silently never persists. -->
          <v-row v-if="showWidget" dense>
            <v-col cols="12" md="6">
              <v-select
                data-testid="tq2-item-inputtype"
                v-model="item.inputType"
                :items="inputTypeOptions"
                item-title="title"
                item-value="value"
                label="ชนิดการรับคำตอบ (inputType)"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-form-textbox"
                hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                data-testid="tq2-item-answertype"
                v-model="item.answerType"
                :items="answerTypeOptions"
                item-title="title"
                item-value="value"
                label="ชนิดค่าคำตอบ (answerType)"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-numeric"
                hide-details
              />
            </v-col>
          </v-row>

          <!-- quick reply = the ONLY answer-button list (R9: the separate value/label
               `options` editor is gone — a bubble is authored here or nowhere).
               🔴 Shown for EVERY kind, not just SCALAR_KINDS: `buildItemRequest` writes `answerOptions` for all
               three, so an INCOME_CLASSIFIER / CONFIRM row can and does carry authored chips — hiding the editor
               did not stop the chips from existing, it only stopped anyone from seeing which row owns them.
               🔴 …EXCEPT CALCULATE. It can never have a button: `TaxQuickReplyV2.Derive` answers
               `IsNonAskableKind` with `SourceNone` before it even looks at `answerOptions`, and the item is never
               asked in the first place. Showing the editor there would offer buttons the runtime is guaranteed
               to throw away. (SUMMARY is inside that same backend set but keeps its editor — it is the kind most
               often flipped over FROM another one, and a stray authored chip has to stay visible to be removable.) -->
          <!-- 🔴 …and MESSAGE, for the same reason with one more: a statement invites no reply, and the runtime lifts
               every chip off it anyway (TaxQuickReplyV2's non-askable twin).
               …and USAGE_FUNNEL_STATE (#746): a silent funnel marker is never on screen, so a chip could only be
               authored to be thrown away. -->
          <div v-if="!isCalculate && !isMessage && !isUsageFunnelState" class="tq2-options-block mt-3">
            <div class="tq2-editor-header">
              <span class="tq2-label">ปุ่มคำตอบด่วน (quick reply)</span>
              <div class="d-flex align-center ga-1">
                <v-btn
                  v-if="isIncomeClassifier"
                  data-testid="tq2-item-chips-from-categories"
                  size="small"
                  variant="text"
                  color="primary"
                  :disabled="!incomeCategoryChips.length"
                  @click="fillChipsFromIncomeCategories"
          >
                  <v-icon start size="18">mdi-auto-fix</v-icon>เติมจากประเภท §40
                  <v-tooltip activator="parent" location="top">
                    คัดลอกชื่อประเภทเงินได้ด้านบนมาเป็นปุ่ม (ทับของเดิม)
                  </v-tooltip>
                </v-btn>
                <v-btn
                  data-testid="tq2-item-chips-raw-toggle"
                  size="small"
                  variant="text"
                  :color="rawChips ? 'primary' : undefined"
                  @click="toggleRawChips"
          >
                  RAW JSON
                </v-btn>
              </div>
            </div>
            <p class="text-caption text-medium-emphasis mb-1">
              เพิ่มทีละอัน (เป็น <b>ป้าย</b> ล้วน ไม่มีรหัสซ่อน) ระบบจะแปลงเป็น quickReply syntax ให้เอง
              — ช่องไทยคือป้ายต้นทาง ส่วนอังกฤษและจีนเป็นคำแปล (ไม่บังคับ)
            </p>
            <!-- 🔴 กฎ all-or-nothing เป็น "รายข้อ" ไม่ใช่รายปุ่ม เพราะเวลาแสดงจริงจะแสดงทั้งแถวพร้อมกัน
                 ปุ่มที่แปลไว้ปุ่มเดียวจึงกลายเป็นแถวสองภาษาปนกัน — ระบบเลยเลือกไทยทั้งแถวแทน -->
            <p class="text-caption text-medium-emphasis mb-2">
              คำแปลใช้ได้ต่อเมื่อ <b>ครบทุกปุ่มของข้อนี้</b> และ <b>ไม่ซ้ำกันเอง</b> — ขาดช่องเดียวหรือซ้ำกัน
              ปุ่มของข้อนี้จะกลับไปเป็นภาษาไทยทั้งแถว (ไม่ใช่เฉพาะปุ่มที่ขาด)
            </p>
            <div v-if="chipRows.length" class="d-flex ga-2 mb-2">
              <v-chip
                v-for="b in chipLangBadges"
                :key="b.lang"
                :data-testid="`tq2-item-chip-badge-${b.lang}`"
                size="small"
                :color="b.ok ? 'success' : 'warning'"
                variant="tonal"
              >{{ b.text }}</v-chip>
            </div>
            <!-- What happens when this list is left empty — different per kind, and invisible everywhere else. -->
            <v-alert
              v-if="!chipRows.length"
              data-testid="tq2-item-chips-fallback"
              type="info"
              variant="tonal"
              density="compact"
              class="mb-3"
          >
              {{ quickReplyFallbackHint }}
            </v-alert>

            <template v-if="!rawChips">
              <div v-if="chipRows.length" class="tq2-row-list">
                <div class="tq2-multilingual-chip-head" aria-hidden="true">
                  <span class="tq2-row-index">#</span>
                  <span>Thai</span>
                  <span>English</span>
                  <span>Chinese</span>
                  <span class="tq2-chip-action-head"></span>
                </div>
                <div v-for="(row, idx) in chipRows" :key="idx" class="tq2-option-row tq2-multilingual-option-row">
                  <span class="tq2-row-index">{{ idx + 1 }}.</span>
                  <v-text-field
                    :data-testid="`tq2-item-chip-th-${idx}`"
                    :model-value="row.label"
                    label="Thai"
                    placeholder="เช่น โสด"
                    variant="outlined"
                    density="compact"
                    hide-details
                    @update:model-value="setChipLabel(idx, $event)"
                  />
                  <v-text-field
                    :data-testid="`tq2-item-chip-en-${idx}`"
                    :model-value="row.labelEn"
                    label="English"
                    placeholder="e.g. Single"
                    variant="outlined"
                    density="compact"
                    hide-details
                    @update:model-value="setChipLanguage(idx, 'labelEn', $event)"
                  />
                  <v-text-field
                    :data-testid="`tq2-item-chip-zh-${idx}`"
                    :model-value="row.labelZh"
                    label="Chinese"
                    placeholder="例如：單身"
                    variant="outlined"
                    density="compact"
                    hide-details
                    @update:model-value="setChipLanguage(idx, 'labelZh', $event)"
                  />
                  <v-btn
                    size="small"
                    density="comfortable"
                    variant="text"
                    icon
                    color="error"
                    aria-label="ลบปุ่มคำตอบ"
                    :data-testid="`tq2-item-chip-remove-${idx}`"
                    @click="removeChip(idx, row.label)"
          >
                    <v-icon size="18">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </div>
            </template>

            <template v-else>
              <!-- Escape hatch: the column stores the array verbatim, so ["โสด"] and
                   [{"label":"โสด","labelEn":"Single","labelZh":"單身"}] are both valid — only reachable here.
                   🔴 `value` is authorable here and NOWHERE else, on purpose: it makes the backend print
                   `[ตัวเลือก: …]` into this question's prompt, which is a real change to what the model reads. -->
              <v-textarea
                data-testid="tq2-item-chips-raw"
                :model-value="chipsRawText"
                label='["โสด", { "label": "สมรส", "labelEn": "Married", "labelZh": "已婚" }]'
                variant="outlined"
                density="compact"
                rows="4"
                auto-grow
                :error-messages="chipsRawError"
                @update:model-value="onChipsRawInput"
              />
            </template>

            <div class="tq2-chip-actions">
              <v-btn data-testid="tq2-item-chip-add" size="small" variant="tonal" @click="addChip">
                <v-icon start>mdi-plus</v-icon>Add answer
              </v-btn>
              <v-btn
                data-testid="tq2-item-chip-translate"
                size="small"
                color="primary"
                variant="elevated"
                :loading="translatingChips"
                :disabled="translatingChips || !translatableChipCount"
                @click="translateChips"
              >
                <v-icon start>mdi-translate</v-icon>
                {{ translatingChips ? `Translating ${translationProgress.completed}/${translationProgress.total}` : 'Translate' }}
              </v-btn>
            </div>
            <v-alert
              v-if="chipTranslationMessage"
              :type="chipTranslationMessageType"
              variant="tonal"
              density="compact"
              class="mt-2"
              data-testid="tq2-item-chip-translation-message"
              >
              {{ chipTranslationMessage }}
            </v-alert>

            <v-switch
              data-testid="tq2-item-preferconfig"
              v-model="quickReplyUseLlm"
              label="ให้ LLM ปรุงปุ่มคำตอบของคำถามนี้ (เรียก LLM)"
              color="primary"
              inset
              density="compact"
              :hint="preferConfigHint"
              persistent-hint
              class="mt-2"
            />
          </div>
        </div>

        <!-- ── BLOCK: การแสดงผล & เงื่อนไข ───────────────────────────── -->
        <div class="tq2-block">
          <h4 class="tq2-block-title">การแสดงผล &amp; เงื่อนไข</h4>
          <v-text-field
            data-testid="tq2-item-visiblewhen"
            v-model="item.visibleWhen"
            label="เงื่อนไขการแสดง (VisibleWhen DSL)"
            placeholder="Always"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-eye-outline"
            :hint="visibleWhenHintText"
            persistent-hint
          />
          <v-alert
            v-if="visibleWhenWarn"
            data-testid="tq2-item-visiblewhen-warn"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-2"
              >
            {{ visibleWhenWarn }}
          </v-alert>
          <div class="d-flex flex-wrap align-center ga-6 mt-2">
            <!-- 🔴 Hidden for the kinds the walker resolves on its own. This is not cosmetic: `buildItemRequest`
                 already forces `isRequired` false for them, and the BACKEND throws on a required SUMMARY /
                 CALCULATE — so a toggle left here is one that either silently does nothing or 400s the
                 whole version-wide bulk save. -->
            <v-switch v-if="canBeRequired" data-testid="tq2-item-required" v-model="item.isRequired" label="จำเป็นต้องตอบ" color="primary" inset hide-details density="compact" />
            <span v-else class="text-caption text-medium-emphasis" data-testid="tq2-item-required-na">
              ชนิดนี้ตั้งเป็น "จำเป็นต้องตอบ" ไม่ได้ — ระบบไม่ได้ถามลูกค้า จึงไม่มีคำตอบมาปิดข้อนี้ได้
            </span>
            <v-switch data-testid="tq2-item-active" v-model="item.isActive" label="เปิดใช้งาน" color="primary" inset hide-details density="compact" />
          </div>
        </div>

        <!-- ── BLOCK: คำสั่ง AI (collapsed) ─────────────────────────── -->
        <!-- …not for USAGE_FUNNEL_STATE (#746): the walk never phrases or extracts anything on a funnel marker. -->
        <v-expansion-panels v-if="!isUsageFunnelState" variant="accordion" class="tq2-prompt-panel">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <v-icon size="18" class="mr-2">mdi-robot-outline</v-icon>
              คำสั่ง AI เฉพาะคำถามนี้ (ขั้นสูง)
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-textarea
                data-testid="tq2-item-qprompt"
                v-model="item.questionSystemPrompt"
                label="System prompt — ตอนถาม (Question)"
                variant="outlined"
                density="comfortable"
                rows="3"
                auto-grow
                hide-details
                class="mb-4"
              />
              <!-- 🔴 The retrieval QUERY sits with the ask-side picker, not with the answer-side one: the words that
                   find the right document and the sentence the customer reads are rarely the same, and only the ask
                   op can be authored in advance. What the extract op has to look up is decided by what the customer
                   just typed, so it takes the question + their message and has no box here. -->
              <v-textarea
                data-testid="tq2-item-kquery"
                v-model="item.questionKnowledgeQuery"
                label="คำค้นเอกสาร — ตอนปรุงคำถาม (retrieve doc for question)"
                placeholder="เว้นว่าง = ใช้ตัวคำถามเองเป็นคำค้น"
                hint="ข้อความที่ใช้ค้นเอกสารจาก Knowledge ด้านล่างตอนเรียบเรียงคำถาม"
                persistent-hint
                variant="outlined"
                density="comfortable"
                rows="2"
                auto-grow
                class="mb-4"
              />
              <v-autocomplete
                data-testid="tq2-item-knowledge-question"
                v-model="item.questionKnowledgeIds"
                :items="knowledgeCatalog"
                item-title="title"
                item-value="knowledgeCode"
                label="Knowledge — สำหรับปรุงคำถาม"
                placeholder="พิมพ์เพื่อค้นหา Knowledge"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-book-open-variant"
                multiple
                chips
                closable-chips
                hide-details
                no-data-text="ไม่พบ Knowledge"
                class="mb-4"
              />
              <v-textarea
                data-testid="tq2-item-aprompt"
                v-model="item.answerSystemPrompt"
                label="System prompt — ตอนสกัดคำตอบ (Answer)"
                variant="outlined"
                density="comfortable"
                rows="3"
                auto-grow
                hide-details
                class="mb-4"
              />
              <v-autocomplete
                data-testid="tq2-item-knowledge-answer"
                v-model="item.answerKnowledgeIds"
                :items="knowledgeCatalog"
                item-title="title"
                item-value="knowledgeCode"
                label="Knowledge — สำหรับสกัดคำตอบ"
                placeholder="พิมพ์เพื่อค้นหา Knowledge"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-book-open-variant"
                multiple
                chips
                closable-chips
                hide-details
                no-data-text="ไม่พบ Knowledge"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
      <!-- ── RIGHT: what the app will draw. Rendered for card kinds only — `v-if`, not `v-show`, so a kind with
           no card has no column in the DOM at all and the dialog is byte-for-byte the one it has always been.
           READ-ONLY: the panel is handed the item and never writes to it (normalizing lives in `resetForm` /
           `onKindChange`, and doing it per keystroke has broken this dialog before). -->
      <div v-if="showCardPreview" class="tq2-preview-col" data-testid="tq2-preview-col">
        <!-- Same switch as the SHORT_FORM copy header (one `cardLang`): the preview renders in that language, and
             the backend warns above the card when a seat is untranslated and the card falls back to Thai. -->
        <div class="tq2-preview-lang">
          <span class="text-caption text-medium-emphasis">ภาษาของตัวอย่าง</span>
          <v-btn-toggle
            v-model="cardLang"
            mandatory
            divided
            density="compact"
            variant="outlined"
            color="primary"
            data-testid="tq2-preview-lang"
          >
            <v-btn
              v-for="l in cardLangItems"
              :key="l.value"
              :value="l.value"
              size="small"
              :data-testid="`tq2-preview-lang-${l.value}`"
            >
              {{ l.title }}
              <span v-if="l.count" class="tq2-lang-count" :class="{ 'tq2-lang-count--gap': !l.complete }">{{ l.count }}</span>
            </v-btn>
          </v-btn-toggle>
        </div>
        <TaxCardPreviewPanelV2
          :item="item"
          :short-form-config-text="shortFormConfigText"
          :lang="cardLang"
          :active="showCardPreview && modelValue"
        />
      </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn data-testid="tq2-item-dialog-cancel" variant="outlined" @click="closeDialog">
          <v-icon start>mdi-close</v-icon>ยกเลิก
        </v-btn>
        <v-btn data-testid="tq2-item-dialog-save" color="primary" variant="elevated" @click="submit">
          <v-icon start>mdi-check</v-icon>บันทึกลง builder
        </v-btn>
      </v-card-actions>

      <ConfirmDeleteDialog ref="confirmDelete" />
    </v-card>
  </v-dialog>
</template>

<script>
import { taxConfigV2Api } from '../services/taxConfigV2Api';
import { taxQuestionV2Api } from '../services/taxQuestionV2Api';
import { taxCalculateV2Api } from '../services/taxCalculateV2Api';
import { languageTranslationApi } from '../services/languageTranslationApi';
import {
  taxQuestionV2Store,
  ITEM_KIND_OPTIONS,
  INPUT_TYPE_OPTIONS,
  ANSWER_TYPE_OPTIONS,
  VISIBLE_WHEN_HINTS,
  FIELDKEY_KINDS,
  INCOME_CATALOG_KINDS,
  SCALAR_KINDS,
  NON_ASKABLE_KINDS,
  STATECODE_KINDS,
  STATE_CONFIRM_KINDS,
  STATEGROUP_KINDS,
  EDIT_KEYWORD_KINDS,
  USAGE_FUNNEL_STATE_OPTIONS,
  normalizeCalcConfig,
  normalizeShortFormConfig,
  blankShortFormField,
  shortFormFieldKeys,
  isLangBag,
  langTh,
  CARD_LANGS,
  KIND_COLORS,
  ANSWER_KIND_META,
} from '../utils/taxQuestionV2Store';
import { describeVisibleWhen, visibleWhenWarning } from '../utils/visibleWhen';
import { canFormatJsonText, formatJsonText } from '../utils/jsonText';
import TaxCardPreviewPanelV2 from './TaxCardPreviewPanelV2.vue';
import ConfirmDeleteDialog from './ConfirmDeleteDialog.vue';
// 🔴 IMPORTED, never re-stated here. Which kinds have a card is one fact with one owner
// (`utils/taxCardPreview.js`, where the preview request is built from the same list); a second copy in this
// file would be a list that eventually disagrees with the one the request is actually built from — the exact
// drift FIELDKEY_KINDS / SCALAR_KINDS already caused in this dialog (see the note below).
import { hasCardPreview } from '../utils/taxCardPreview';

// R1: 8 item kinds. FIELD_INPUT is the single value-capturing kind (binds one FieldKey).
const KIND_HINTS = {
  FIELD_INPUT: 'ผูกกับ Field ตัวเดียว — เลือก Field Key ที่สร้างใน Tax Config V2 เพื่อเก็บคำตอบ',
  INCOME_CLASSIFIER: 'ให้ AI จำแนกข้อความรายได้เป็นประเภท §40 แล้วตั้ง flag ต่อประเภท',
  INCOME_CLASSIFY: 'คุมบทสนทนาเรื่องรายได้ทั้งหมดจากข้อเดียว — ไล่ถาม "หมวด → ยอด" ทีละรายการ '
    + 'ตามลำดับที่ลูกค้าพูดถึง และไม่ปิดข้อจนกว่าจะครบทุกรายการ · ข้อนี้ไม่เก็บยอดเงินเอง: '
    + 'แต่ละหมวดจะเขียนทะลุไปที่ข้อ §40 ที่ระบุไว้ (INCOME_40_x) — ข้อ §40 พวกนั้นต้องยังเปิดอยู่ ไม่งั้นยอดจะไม่มีที่ลง',
  CONFIRM: 'จุดยืนยัน/สรุป — สรุปคำตอบแล้วถามให้ยืนยัน/แก้ไข (ไม่ผูกฟิลด์)',
  CONFIRM_SKIP: 'จุดยืนยันข้อที่ข้าม — ถามว่าจะกลับไปกรอกข้อที่ลูกค้าข้ามไว้หรือยัง (ทั้งฟอร์ม, ไม่ผูกฟิลด์)',
  SUMMARY: 'การ์ดสรุป — ไม่ถามอะไรเลย อ่านข้อมูลในฟอร์มมาประกอบเป็น JSON แล้วส่งไปให้แอปแสดงเป็นการ์ด (ไม่ผูกฟิลด์)',
  CALCULATE: 'จุดคำนวณ — ระบบรันสูตรของ Tax Calculate V2 ให้เองตามลำดับที่จัดไว้ ลูกค้าไม่เห็นข้อนี้และไม่ถูกถาม (ไม่ผูกฟิลด์ — ผลลัพธ์ลง FieldKey ที่ตัวสูตรประกาศเอง)',
  SHORT_FORM: 'ฟอร์มสั้น — การ์ดที่ให้ลูกค้ากรอกหลายยอดจบในหน้าจอเดียว แทนการถามทีละข้อ · ข้อนี้ไม่เก็บยอดเงินเอง: แต่ละแถวชี้ไปที่ FieldKey ของข้อ FIELD_INPUT ในเวอร์ชันเดียวกัน และยอดจะไปลงที่แถวคำตอบของข้อนั้น — ตัวมันเองเก็บแค่คีย์ว่า "การ์ดใบนี้ถูกตอบแล้ว"',
  SAVE_STATE: 'หมุดบันทึกสถานะ — ไม่ถามอะไรเลย เดินมาถึงข้อนี้เมื่อไหร่ ระบบจดว่า "ตอนนี้ลูกค้าอยู่ฉากนี้" '
    + 'ไว้ที่ฟอร์ม แล้วเดินต่อทันทีในเทิร์นเดียวกัน (ไม่ผูกฟิลด์) · ข้อความคำถาม / คำสั่ง AI / ปุ่มตอบของข้อนี้ '
    + 'ไม่ได้ใช้ตอนเดินผ่าน แต่ถูกหยิบมาใช้ "ตอนลูกค้ากลับมาทำฟอร์มต่อ" แทนข้อความ resume กลางของระบบ '
    + '(= จังหวะ 1 "ทำต่อไหมคะ?") · ถ้ากรอก "ข้อความจังหวะ 2" ไว้ด้วย พอลูกค้ากดปุ่มของจังหวะ 1 บอทจะสรุปข้อมูลเดิม '
    + 'พร้อมการ์ดของฉากนี้ แล้วทำตาม "จำนวนปุ่มของจังหวะ 2": 2 ปุ่มขึ้นไป = รอคำตอบ [ขอแก้] / [ใช้ต่อ] · '
    + '1 ปุ่ม = รอคำตอบ และปุ่มนั้นแปลว่า "ใช้ต่อ" (อยากแก้ให้พิมพ์บอก) · ไม่มีปุ่มเลย = ไม่รอคำตอบ '
    + 'พูดข้อความ + การ์ด แล้วถามคำถามจริงต่อในจอเดียวกัน '
    + '(เว้นว่าง = ไม่มีจังหวะ 2 ไปคำถามจริงทันทีเหมือนเดิม) · '
    + 'การ์ด SUMMARY/SIMULATE ที่ใส่ชื่อสถานะเดียวกันจะถูกแสดงซ้ำให้ตอนนั้นด้วย',
  END_FORM: '⚠️ จุดจบฟอร์ม — เดินมาถึงข้อนี้เมื่อไหร่ ฟอร์มถูกปิด (Completed) ทันที และไม่ถามข้อถัดไปอีกเลย แต่ "คำถามนี้ยังค้างอยู่บนจอ" ลูกค้าคุยต่อได้ตามปกติ · ระบบจะเริ่มฟอร์มใหม่ก็ต่อเมื่อ Answer System Prompt ของข้อนี้สกัดได้ชัดว่าลูกค้าต้องการเริ่มใหม่ (ตอบไม่ชัด = ให้คืน "UNCLEAR" แล้วคำถามจะค้างต่อ) · ต้องใส่เงื่อนไขแสดงผล (VisibleWhen) เสมอ ไม่งั้นฟอร์มจะจบตรงนี้ทุกครั้ง',
  USAGE_FUNNEL_STATE: 'จุดนับ Usage Funnel — ไม่ถามและไม่พูดอะไรเลย เดินมาถึงข้อนี้เมื่อไหร่ ระบบบันทึกว่า '
    + '"ฟอร์มนี้ผ่านขั้นที่เลือกแล้ว" ลงตาราง Usage Funnel by Form ของหน้า TaxDashboard แล้วเดินต่อทันทีในเทิร์นเดียวกัน · '
    + 'นับครั้งเดียวต่อฟอร์ม (วางขั้นเดียวกันหลายจุดได้ เช่น สายตัวเอง/สายคู่สมรส) · ไม่ผูกฟิลด์ ไม่มีปุ่มตอบ '
    + 'ตั้งเป็นข้อบังคับไม่ได้ · วางตามลำดับ funnel — ระบบไม่ตรวจลำดับให้',
  MESSAGE: 'ข้อความแจ้ง — ไม่ถามอะไรเลย เดินมาถึงข้อนี้เมื่อไหร่ ระบบจะ "พูด" ข้อความคำถามของข้อนี้ให้ลูกค้าฟัง '
    + '(เรียบเรียงผ่าน LLM ด้วยคำสั่ง AI ตอนถามของข้อนี้ + ภาษาของบทสนทนา เหมือนคำถามทั่วไป แต่ห้ามกลายเป็นคำถาม) '
    + 'แล้วเดินต่อทันทีในเทิร์นเดียวกัน · พูดครั้งเดียว: ระบบแสตมป์ว่าพูดแล้ว ตอน resume จะไม่พูดซ้ำ · '
    + 'ไม่ผูกฟิลด์ ไม่มีปุ่มตอบ ตั้งเป็นข้อบังคับไม่ได้ · '
    + 'วางไว้ท้ายสุดของชุดคำถาม = ประโยคปิดฟอร์ม (พอพูดจบ ไม่เหลือคำถาม ฟอร์มจะปิดเอง โดยไม่ต่อข้อความปิดฟอร์มกลางของระบบอีก)',
};

// What the SUMMARY card's template box accepts. A JS string bound with `:hint` for the same reason as the
// SHORT_FORM one below: `{{ … }}` is Vue's own interpolation syntax, so an inline attribute would be
// evaluated instead of shown.
const SUMMARY_TEMPLATE_HINT = 'รูป JSON ที่จะส่งให้แอป — ใช้ {{ cards.… }} ชี้ค่า · '
  + '@repeat + @template สร้าง array ที่ยาวไม่คงที่ · '
  + '@omitWhenNull ตัดคีย์/แถวทิ้งเมื่อ "ไม่มีค่า" (null) · '
  + '@omitWhenZero ตัดทิ้งเมื่อ "ไม่มีค่า หรือ เป็น 0" — ใช้กับแถวขั้นบันไดที่ไม่ได้เสียภาษี '
  + '(0 คือข้อมูลจริงบนการ์ดนี้ @omitWhenNull จึงไม่ตัดให้) · '
  + 'ใส่เป็นลิสต์ได้ เช่น ["cards.calc.RATE_1","cards.calc.TAX_1"] = ตัดแถวเมื่ออันใดอันหนึ่งเป็น 0 '
  + '(ขั้นยกเว้น 0% ไม่มีทั้งอัตราและภาษี) · '
  + '@value ใช้คู่กับ @omitWhen… เมื่อคีย์นั้นเก็บค่าเดี่ยว ไม่ใช่อ็อบเจ็กต์';

// What the SHORT_FORM structure box accepts, spelled out once.
// 🔴 A JS string on purpose, bound with `:hint`, NOT written inline as a static attribute: `{{ … }}` is Vue's own
//    interpolation syntax, so keeping the sample paths on this side of the fence is what guarantees the author
//    reads the literal directive instead of whatever the compiler decided to do with it.
const SHORT_FORM_TEMPLATE_HINT = 'ว่าง = ใช้โครงมาตรฐานของระบบ (ไม่ต้องกรอก) · '
  + 'ใช้ {{ form.title }} ชี้ค่า — root ชื่อ form ตัวเล็กเป๊ะ (เทียบตัวพิมพ์เล็ก/ใหญ่ ผิดตัวเดียวคือ error ตอนบันทึก) · '
  + '@repeat + @template สร้าง array ที่ยาวไม่คงที่ (form.groups → $.fields) · '
  + '@omitWhenNull ตัดคีย์ทิ้งเมื่อไม่มีค่า · @omitWhenZero ตัดทิ้งเมื่อไม่มีค่าหรือเป็น 0 · '
  + '@value ใช้คู่กับ @omitWhen… เมื่อคีย์นั้นเก็บค่าเดี่ยว ไม่ใช่อ็อบเจ็กต์ · '
  // 🔴 The sentence that stops the question this box actually raises. The default structure names NO style key
  //    (the vocabulary is the app's and grows without us), so an author reading it concludes the card cannot be
  //    styled — and would then paste colours in here by hand, where a typo is silent. Say where they belong.
  + 'สี/ขนาดตัวอักษรไม่ต้องใส่ในโครงนี้ — ระบบผสมให้เองจากช่อง "สไตล์การ์ด" และ "สไตล์แถว" ด้านบน '
  + '(จะเขียนคีย์ลงในโครงนี้เองก็ได้ ค่าที่เขียนเองจะชนะ)';

// What the SIMULATE structure box accepts. Same fence as the others: a JS string, bound with `:hint`.
const SIMULATION_TEMPLATE_HINT = 'ว่าง = ใช้โครงมาตรฐานของระบบ (ไม่ต้องกรอก) · '
  + 'ใช้ {{ sim.picker.title }} ชี้ค่า — root ชื่อ sim ตัวเล็กเป๊ะ (เทียบตัวพิมพ์เล็ก/ใหญ่ ผิดตัวเดียวคือ error ตอนบันทึก) · '
  + '@repeat: "sim.picker.groups" คู่กับ @template สร้างกลุ่มกี่กลุ่มก็ได้ — ใน @template ใช้ $ แทนกลุ่มปัจจุบัน '
  + 'และซ้อน @repeat: "$.items" อีกชั้นสำหรับแถบเลื่อนในกลุ่มนั้น ({{ $.id }} · {{ $.value }} · {{ $.min }}) · '
  + '@omitWhenNull ตัดคีย์ทิ้งเมื่อไม่มีค่า · @value ใช้คู่กับ @omitWhen… เมื่อคีย์นั้นเก็บค่าเดี่ยว '
  + '(เช่น { "@omitWhenNull": "$.unit", "@value": "{{ $.unit }}" }) · '
  // 🔴 The two claims a template may never drop, said where the author is typing rather than only in a rejection.
  + 'ห้ามตัด postback.fieldKey และ id ของแถบเลื่อนออก — สองคีย์นี้คือสิ่งที่ทำให้ปุ่มบนการ์ดส่งค่ากลับได้ '
  + 'ถ้าหาย ระบบจะไม่ยอมใช้โครงนี้และกลับไปใช้โครงมาตรฐานแทน · '
  + 'สี/ขนาดตัวอักษรไม่ต้องใส่ในโครงนี้ — ระบบผสมให้เองจากช่อง "สไตล์" ใน Card config (จะเขียนเองก็ได้ ค่าที่เขียนเองจะชนะ)';

// What the FILING_CHOICE structure box accepts.
// 🔴 A JS string on purpose, bound with `:hint`, NOT written inline as a static attribute — the same fence as the
//    two hints above: `{{ … }}` is Vue's own interpolation syntax, so an inline attribute would be EVALUATED
//    against this component and the author would read an empty string where the sample path should be.
const FILING_TEMPLATE_HINT = 'ว่าง = ใช้โครงมาตรฐานของระบบ (ไม่ต้องกรอก) · '
  + 'ใช้ {{ filing.title }} ชี้ค่า — root ชื่อ filing ตัวเล็กเป๊ะ (เทียบตัวพิมพ์เล็ก/ใหญ่ ผิดตัวเดียวคือ error ตอนบันทึก) · '
  + '@repeat: "filing.options" คู่กับ @template สร้างไทล์ตัวเลือกกี่ใบก็ได้ — ใน @template ใช้ $ แทนใบปัจจุบัน '
  + '({{ $.label }} · {{ $.amount }} · {{ $.value }}) และซ้อน @repeat: "$.details" อีกชั้นสำหรับบรรทัดย่อยในใบนั้น · '
  + '@omitWhenNull ตัดคีย์ทิ้งเมื่อไม่มีค่า · @omitWhenZero ตัดทิ้งเมื่อไม่มีค่าหรือเป็น 0 · '
  + '@value ใช้คู่กับ @omitWhen… เมื่อคีย์นั้นเก็บค่าเดี่ยว ไม่ใช่อ็อบเจ็กต์ '
  + '(เช่น { "@omitWhenNull": "$.value", "@value": "{{ $.value }}" }) · '
  // 🔴 The sentence that answers the question this box actually raises, same as the SHORT_FORM one above: the
  //    default structure names NO style key, so an author reading it concludes the card cannot be styled — and
  //    would then hand-paste colours in here, where a typo is silent. Say where they belong instead.
  + 'สี/ขนาดตัวอักษรไม่ต้องใส่ในโครงนี้ — ระบบผสมให้เองจากถุง style ใน Card config ด้านบน '
  + '(style · optionStyle · bestOptionStyle · submitButtonStyle) แต่คีย์ที่เขียนเองในโครงนี้จะชนะ';

// The key the CARD's style draft is filed under. A row's draft is keyed by the row OBJECT itself, so the card —
// which has no object of its own to point at — needs a sentinel that can never collide with one. A frozen empty
// object rather than a string for exactly that reason: no row can ever be `===` to it.
const SHORT_FORM_CARD_STYLE = Object.freeze({});

/** The draft key for the whole-config JSON box. Its own frozen sentinel, for the same reason as the one above. */
const SHORT_FORM_CONFIG_JSON = Object.freeze({});

/** What the language switch calls each member of a copy bag (`CARD_LANGS`). */
const CARD_LANG_TITLES = { th: 'ไทย', en: 'EN', zh: '中文' };
/** The SHORT_FORM seats that are COPY — the ones a language bag may hold. 🔴 `cardType` / `refId` go through the same
 *  setter but are ids: writing them as a translation would turn an id into a bag. */
const SHORT_FORM_CARD_COPY_KEYS = ['title', 'subtitle', 'submitLabel', 'secondaryLabel'];
const SHORT_FORM_ROW_COPY_KEYS = ['label', 'placeholder', 'unitLabel', 'noteAbove', 'noteBelow'];
/** A seat needs a translation when its Thai reading has Thai script in it — a placeholder of "0" does not. */
const THAI_SCRIPT = /[\u0E00-\u0E7F]/;

// 🔴 FIELDKEY_KINDS / SCALAR_KINDS are IMPORTED, never re-declared. This file used to keep its own copy of the
// first list, and when the store's copy was the one wired into the save payload the two drifted: the dialog let
// you type a key the payload then threw away.

export default {
  name: 'TaxQuestionItemV2Dialog',
  components: { TaxCardPreviewPanelV2, ConfirmDeleteDialog },
  props: {
    modelValue: { type: Boolean, default: false },
    initialItem: { type: Object, default: null },
    configVersionId: { type: Number, default: null },
    knowledgeCatalog: { type: Array, default: () => [] },
    // Every other item in THIS question version, so an INCOME_CLASSIFY category can only ever point its fan-out at
    // a row that actually exists here. The backend warns about a dangling target, but a warning arrives after the
    // save — and by then the driver has a section whose amount has nowhere to land.
    siblingItems: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      item: taxQuestionV2Store.createBlankItem(),
      submitAttempted: false,
      rules: {
        required: (value) => (value !== null && value !== undefined && String(value).trim() !== '') || 'Required',
      },
      ruleOptions: [],
      loadingRules: false,
      itemKindOptions: ITEM_KIND_OPTIONS,
      inputTypeOptions: INPUT_TYPE_OPTIONS,
      answerTypeOptions: ANSWER_TYPE_OPTIONS,
      // quick-reply raw-JSON escape hatch (per-open, never persisted)
      rawChips: false,
      chipsRawText: '',
      chipsRawError: '',
      translatingChips: false,
      chipTranslationMessage: '',
      chipTranslationMessageType: 'info',
      translationProgress: { completed: 0, total: 0 },
      // The SAME four slots for the stage-2 (`confirmOptions`) row. Kept separate rather than shared because both
      // rows are on screen at once on a SAVE_STATE item: one shared spinner/among would put the progress counter and
      // the result alert of one list onto the other list's button.
      translatingConfirmChips: false,
      confirmChipTranslationMessage: '',
      confirmChipTranslationMessageType: 'info',
      confirmTranslationProgress: { completed: 0, total: 0 },
      // ── App Bar group-state dropdown (SAVE_STATE) ──
      // 🔴 UI-ONLY, NOT on `this.item`, for the reason the CALCULATE note below gives: `submit()` emits the whole item.
      /** @type {{value:string, title:string}[]} rows of `api/v1/BarManagement/options` → `conditionStateOptions` */
      stateGroupOptions: [],
      loadingStateGroupOptions: false,
      stateGroupOptionsFailed: false,
      // ── CALCULATE panel state ──
      // 🔴 UI-ONLY, and deliberately NOT on `this.item`: `submit()` deep-clones the whole item and emits it, so a
      //    catalog or a half-picked dropdown value parked there would ride along into the save payload (the same
      //    reason income categories keep their editable buffer in `_meta` and strip it before emitting).
      /** @type {Object[]} calc versions of `configVersionId` (raw dtos — `calcVersionID` with a capital ID) */
      calcVersions: [],
      /** @type {Object[]} function summaries of the SELECTED calc version (raw dtos — `functionID`) */
      calcFunctions: [],
      /** which calc version `calcFunctions` actually describes — a successful load stamps it here, and only a
       *  match makes a selected-but-unresolvable row report "ไม่พบสูตรนี้" (otherwise every row would flash the
       *  red chip while the list is still in flight) */
      calcFunctionsVersionId: null,
      calcFunctionsFailed: false,
      loadingCalcVersions: false,
      loadingCalcFunctions: false,
      /** functionID currently highlighted in the "เพิ่มสูตร" dropdown — nothing is added until "เพิ่ม" is pressed */
      calcFnToAdd: null,
      /** one-shot Thai warning after a calc-version switch wiped the picked formulas */
      calcNotice: '',
      // Out-of-order guards: switching versions quickly must not let a slow earlier response overwrite the list
      // that belongs to the version now selected (same seq pattern as TaxConfigurationV2.loadCalcVersions).
      calcVersionSeq: 0,
      calcFunctionSeq: 0,
      visibleWhenHintText: `ใส่ "Always" เพื่อแสดงเสมอ · ตัวอย่าง: ${VISIBLE_WHEN_HINTS.slice(1).join(' · ')}`,
      // ── SHORT_FORM structure-template panel state (per-open, UI only — never part of the emitted item) ──
      shortFormTemplateLoading: false,
      shortFormTemplateNotice: '',
      shortFormTemplateNoticeType: 'info',
      // ── FILING_CHOICE structure-template panel state. Same contract as the three above: per-open, UI only, and
      //    never part of the emitted item — the template itself lives on `item.filingTemplateJson`. ──
      filingTemplateLoading: false,
      filingTemplateNotice: '',
      filingTemplateNoticeType: 'info',
      simulationTemplateLoading: false,
      simulationTemplateNotice: '',
      simulationTemplateNoticeType: 'info',
      // ── style boxes: the text an author is CURRENTLY typing, keyed by what it styles ────────────────────
      // 🔴 The text and the stored bag are two different things and the drafts are why. Re-printing
      //    JSON.stringify(bag) on every keystroke would reformat the box under the cursor, and a half-typed
      //    `{ "titleColor":` does not parse — with no draft it would vanish the instant it stopped being valid.
      //    Key is the ROW OBJECT itself (SHORT_FORM_CARD_STYLE for the card): rows have no id, and an index would
      //    hand the draft to whichever row moved into that slot after a reorder.
      styleDrafts: new Map(),
      // Which accordion of the whole-config JSON box is open (undefined = closed). Watched, so opening it
      // re-reads the model — see the watcher.
      shortFormJsonPanel: undefined,
      // Which member of the copy bags the card's text boxes show and edit, and which language the preview renders
      // in. Back to Thai every time the dialog opens (`resetForm`), so nobody types Thai into an English seat.
      cardLang: 'th',
    };
  },
  computed: {
    isEditMode() {
      return Boolean(this.initialItem && this.initialItem.questionItemID != null);
    },
    isFieldInput() {
      return this.item.itemKind === 'FIELD_INPUT';
    },
    isIncomeClassifier() {
      return this.item.itemKind === 'INCOME_CLASSIFIER';
    },
    isIncomeClassify() {
      return this.item.itemKind === 'INCOME_CLASSIFY';
    },
    // Both §40-catalog kinds. Everything about the catalog EDITOR is shared; only the mini section (each category's
    // own wording, its extraction rules and where its answer lands) is INCOME_CLASSIFY-only, because only that kind
    // fans out. Gating the shared editor on `isIncomeClassifier` alone is what left the v7 driver looking empty.
    hasIncomeCatalog() {
      return INCOME_CATALOG_KINDS.includes(this.item.itemKind);
    },
    isConfirmLike() {
      return this.item.itemKind === 'CONFIRM' || this.item.itemKind === 'CONFIRM_SKIP';
    },
    isSummary() {
      return this.item.itemKind === 'SUMMARY';
    },
    isCalculate() {
      return this.item.itemKind === 'CALCULATE';
    },
    /// The switch the author sees. 🔴 It is the INVERSE of the stored column: `QuickReplyPreferConfig` is named
    //  for the opposite claim ("prefer the configured answers"), so ON — "call the LLM" — is stored as false.
    //  Renaming the column would be a migration across five layers for no behavioural gain, so the inversion is
    //  kept here, in the one place a human reads it.
    quickReplyUseLlm: {
      get() {
        return this.item.quickReplyPreferConfig === false;
      },
      set(v) {
        this.item.quickReplyPreferConfig = !v;
      },
    },
    isFilingChoice() {
      return this.item.itemKind === 'FILING_CHOICE';
    },
    isSimulate() {
      return this.item.itemKind === 'SIMULATE';
    },
    // 🔴 The key is REQUIRED for both, and the backend 400s without it. FILING_CHOICE stores the customer's actual
    //    choice (separate|joint) — every later calculation reads it — and SIMULATE needs a row for its scenario.
    filingOrSimulateKeyError() {
      if (!this.isFilingChoice && !this.isSimulate) return null;
      return String(this.item.fieldKey || '').trim()
        ? null
        : (this.isFilingChoice
            ? 'ต้องระบุ fieldKey สำหรับเก็บวิธียื่นที่ลูกค้าเลือก (separate|joint)'
            : 'ต้องระบุ fieldKey ของข้อนี้เอง (ใช้เก็บแผนจำลองที่ลูกค้าเลือก)');
    },
    isSaveState() {
      return this.item.itemKind === 'SAVE_STATE';
    },
    isMessage() {
      return this.item.itemKind === 'MESSAGE';
    },
    isUsageFunnelState() {
      return this.item.itemKind === 'USAGE_FUNNEL_STATE';
    },
    /** USAGE_FUNNEL_STATE's dropdown rows (#746). Same safety as `stateGroupItems`: a saved value missing from the
     *  list (an imported / hand-edited row) is appended as its own row, so it shows instead of vanishing. */
    usageFunnelStateItems() {
      const current = String(this.item.stateGroup || '');
      if (!current || USAGE_FUNNEL_STATE_OPTIONS.some((o) => o.value === current)) return USAGE_FUNNEL_STATE_OPTIONS;
      return [...USAGE_FUNNEL_STATE_OPTIONS, { value: current, title: `${current} (ไม่อยู่ในรายการ)` }];
    },
    /** 🔴 REQUIRED — the state IS the item (a marker with no state logs nothing), and the backend 400s on it inside
     *  the VERSION-WIDE bulk save, so it is caught here like `saveStateCodeError`. */
    usageFunnelStateError() {
      if (!this.isUsageFunnelState) return null;
      const current = String(this.item.stateGroup || '').trim();
      if (!current) return 'ต้องเลือกขั้นใน Usage Funnel ของข้อนี้';
      return USAGE_FUNNEL_STATE_OPTIONS.some((o) => o.value === current)
        ? null
        : `ขั้น '${current}' ไม่อยู่ในรายการ — เลือกใหม่`;
    },
    /**
     * Does this kind carry a `stateCode` box?
     *
     * 🔴 DELEGATED to the store's `STATECODE_KINDS`, never re-typed as three literals here — the same rule the
     * FIELDKEY_KINDS / SCALAR_KINDS note at the top of the script section records the hard way. `buildItemRequest`
     * NULLs `stateCode` for any kind absent from that list, so a second copy that drifted would let the author fill
     * a box whose value the save payload then throws away.
     */
    showStateCode() {
      return STATECODE_KINDS.includes(this.item.itemKind);
    },
    /** Does this kind carry the App Bar `stateGroup` dropdown? SAVE_STATE only. 🔴 NOT `STATEGROUP_KINDS` any more
     *  (#746): USAGE_FUNNEL_STATE persists `stateGroup` too, but holds a funnel state there and has its own dropdown
     *  in the kind chain — the App Bar list on it would offer values its backend validation refuses. */
    showStateGroup() {
      return this.isSaveState;
    },
    /** The dropdown rows. A saved value missing from the loaded list (list not loaded yet, or renamed on the
     *  server) is appended as its own row, so opening and saving the item never silently drops it. */
    stateGroupItems() {
      const current = String(this.item.stateGroup || '');
      const rows = this.stateGroupOptions;
      if (!current || rows.some((o) => o.value === current)) return rows;
      return [...rows, { value: current, title: current }];
    },
    /// 🔴 REQUIRED on SAVE_STATE and on SAVE_STATE only — the code IS the item's whole reason to exist (a nameless
    //  save-slot can never be read back on resume), while on SUMMARY / SIMULATE it is the optional "which slot does
    //  this card belong to" tag. Same shape as `filingOrSimulateKeyError` above, for the same reason: the backend
    //  400s on it, and the save that carries it is the VERSION-WIDE bulk save.
    saveStateCodeError() {
      if (!this.isSaveState) return null;
      return String(this.item.stateCode || '').trim()
        ? null
        : 'ต้องตั้งชื่อสถานะ (stateCode) — ข้อ SUMMARY/SIMULATE และตอนลูกค้ากลับมาทำต่อ อ้างถึงฉากนี้ด้วยชื่อนี้';
    },
    /**
     * Rows of the STAGE-2 chips, projected into the three language boxes the editor binds.
     *
     * 🔴 Same projection as `chipRows` (stage 1 / quick reply) and deliberately so — a chip is a LABEL in this
     * product, and both lists are read by the same backend chip pipeline. What is NOT shared is the storage:
     * these live on `confirmOptions`, because `answerOptions` on this same item is already spoken for by
     * stage 1 ("ทำต่อไหมคะ?"). Merging them would print both rows of buttons on both screens.
     */
    confirmChipRows() {
      return (Array.isArray(this.item.confirmOptions) ? this.item.confirmOptions : [])
        .map((o) => {
          const option = o && typeof o === 'object' && !Array.isArray(o) ? o : {};
          return {
            label: o && typeof o === 'object' && !Array.isArray(o)
              ? String(option.label ?? option.Label ?? option.value ?? option.Value ?? '')
              : String(o ?? ''),
            labelEn: String(option.labelEn ?? option.LabelEn ?? ''),
            labelZh: String(option.labelZh ?? option.LabelZh ?? ''),
          };
        });
    },
    // Stage-2 twins of `translatableChipCount` / `chipLangBadges`. Same predicate, same badge rule — the Thai box
    // is the only source a translation can come from, and the runtime's all-or-nothing rule is the runtime's, not
    // a per-list house style, so both rows have to report it the same way.
    translatableConfirmChipCount() {
      return (Array.isArray(this.item.confirmOptions) ? this.item.confirmOptions : [])
        .filter((o) => String(this.answerOptionField(o, 'label') || '').trim()).length;
    },
    confirmChipLangBadges() {
      return this.langBadgesFor(this.confirmChipRows);
    },
    /// Mirrors the backend rule (`SAVE_STATE` validation): buttons with NO text is a screen the customer can tap
    //  but cannot read — stage 2 would open with two anonymous chips and no question. The reverse is legal on
    //  purpose: text with no buttons is caught by its own message below, and text with neither is simply "no
    //  stage 2 at all", which is the documented off switch.
    stateConfirmTextError() {
      if (!this.isSaveState) return null;
      // `@whenNoCard:` only ever REPLACES the ordinary stage-2 sentence (fusion-note #621) — on its own there is no
      // stage 2 to replace, and the backend reads the pin as "no stage 2 at all", silently.
      const confirmText = String(this.item.confirmTextTh || '');
      const confirmMain = confirmText.split(/^[ \t]*@whenNoCard[ \t]*:/im)[0].trim();
      if (confirmText.trim() && !confirmMain) {
        return 'ใส่ @whenNoCard: ได้เฉพาะต่อท้ายข้อความจังหวะ 2 ปกติ — ข้างบนบรรทัดนี้ยังว่าง จังหวะ 2 จะไม่ทำงานเลย';
      }
      const hasChips = this.confirmChipRows.some((r) => String(r.label || '').trim());
      if (!hasChips) return null;
      return String(this.item.confirmTextTh || '').trim()
        ? null
        : 'ตั้งปุ่มของจังหวะ 2 ไว้แล้ว แต่ยังไม่ได้ใส่ข้อความจังหวะ 2 — ปุ่มลอยที่ไม่มีคำถามคือจอที่ลูกค้าอ่านไม่ออก';
    },
    /**
     * Can "ข้ามจอยืนยัน แล้วยกข้อความ/ปุ่มไปใช้กับคำถามถัดไป" even be ticked on this item?
     *
     * 🔴 Both halves are required and the box is DISABLED without them rather than merely flagged: the tick is a
     * loan, and a pin with no copy has nothing to lend while a pin with no buttons already walks on by itself
     * (that IS the lead-in mode the alert above describes). Same rule as the backend's save-time refusal.
     */
    confirmSkipToQuestionAvailable() {
      if (!this.isSaveState) return false;
      const hasChips = this.confirmChipRows.some((r) => String(r.label || '').trim());
      return hasChips && !!String(this.item.confirmTextTh || '').trim();
    },
    /// The tick surviving after its own preconditions were edited away — an admin who deletes the buttons of a
    //  screen they had set to lend them leaves a checked box that means nothing. Flagged rather than silently
    //  unticked, because unticking on their behalf is a decision they never see us make.
    stateConfirmSkipError() {
      if (!this.isSaveState || !this.item.confirmSkipToQuestion) return null;
      if (this.confirmSkipToQuestionAvailable) return null;
      return String(this.item.confirmTextTh || '').trim()
        ? 'ติ๊ก “ข้ามจอยืนยัน” ไว้ แต่ไม่มีปุ่มของจังหวะ 2 ให้ยกไปใช้ — จังหวะ 2 ที่ไม่มีปุ่มเดินต่อเองอยู่แล้ว (lead-in)'
        : 'ติ๊ก “ข้ามจอยืนยัน” ไว้ แต่ยังไม่ได้ใส่ข้อความจังหวะ 2 — ไม่มีข้อความให้ยกไปใช้กับคำถามถัดไป';
    },
    isShortForm() {
      return this.item.itemKind === 'SHORT_FORM';
    },
    /**
     * Does this kind draw a card, i.e. is there a second column at all?
     *
     * 🔴 DELEGATED, never spelled out here: `hasCardPreview` is the same predicate `buildPreviewRequest` uses to
     * decide whether there is a request to send, so a kind listed here as well would be a kind this dialog could
     * offer a column for while the request builder returned `null` — an empty panel with no explanation. It also
     * governs the dialog's whole geometry (width, height, split), which is why it is read a few times below.
     */
    showCardPreview() {
      return hasCardPreview(this.item && this.item.itemKind);
    },
    /// The two kinds the walker resolves on its own cannot be "required" — the backend throws on each of them,
    /// so the toggle is hidden rather than left to fail at save time.
    /// END_FORM is hidden for a different reason: it IS asked, but it is asked on a form that is already Completed,
    /// so no answer to it can ever move the progress bar — a required one would hold the fraction below 100% for
    /// the life of the form while sitting on the very question that ended it.
    canBeRequired() {
      return !NON_ASKABLE_KINDS.includes(this.item.itemKind) && this.item.itemKind !== 'END_FORM';
    },
    /// Read-side view of the CALCULATE config. `resetForm` / `onKindChange` normalize `item.calcConfig` on every
    /// path, but a row authored before this kind existed can still reach a render with the field missing — and a
    /// template that touched `.functions` on undefined would blank the whole dialog.
    calcConfig() {
      const c = this.item.calcConfig;
      if (!c || typeof c !== 'object') return { calcVersionId: null, functions: [] };
      return { calcVersionId: c.calcVersionId ?? null, functions: Array.isArray(c.functions) ? c.functions : [] };
    },
    /// Read-side view of the SHORT_FORM card config — same guard and the same reason as `calcConfig` above: every
    /// path normalizes `item.shortFormConfig`, but a row authored before this kind existed can still reach a render
    /// with the field missing, and a template that touched `.fields` on undefined would blank the whole dialog.
    /// 🔴 `fields` hands back the SAME array (and therefore the same row objects) the item owns — that is what lets
    ///    the row editor bind its inputs straight at it. Never map/copy it here or every keystroke
    ///    would be written to a throwaway clone.
    shortFormConfig() {
      const c = this.item.shortFormConfig;
      const src = c && typeof c === 'object' && !Array.isArray(c) ? c : {};
      return {
        cardType: src.cardType ?? 'TAX_AMOUNT_FORM',
        refId: src.refId ?? 'type7',
        version: src.version ?? 1,
        // 🔴 The plain boxes bind the THAI reading. A copy value may be a language bag ({th,en,zh}); handing the
        // object straight to a v-text-field renders the literal '[object Object]' in the admin, and the first
        // keystroke would then write that garbage back. See langTh / setShortFormCopy.
        title: langTh(src.title),
        subtitle: langTh(src.subtitle),
        submitLabel: langTh(src.submitLabel),
        secondaryLabel: langTh(src.secondaryLabel),
        fields: Array.isArray(src.fields) ? src.fields : [],
      };
    },
    /**
     * How far each language is translated on this mini-form: `{ en: { done, total }, zh: … }`.
     *
     * Counted over the seats this screen SHOWS — `placeholder` / `unitLabel` only on a money-box row, because a
     * counter or a checkbox never draws them — and only where the Thai has Thai script in it. A seat is done when
     * its bag carries that language; a plain Thai string is a seat nobody translated yet.
     * Read off `item.shortFormConfig` (the stored document), never the Thai `shortFormConfig` view.
     */
    shortFormTranslationCounts() {
      const cfg = this.item.shortFormConfig;
      const seats = [];
      if (cfg && typeof cfg === 'object') {
        SHORT_FORM_CARD_COPY_KEYS.forEach((k) => seats.push(cfg[k]));
        (Array.isArray(cfg.fields) ? cfg.fields : []).forEach((row) => {
          if (!row) return;
          SHORT_FORM_ROW_COPY_KEYS.forEach((k) => {
            if (!row.control || (k !== 'placeholder' && k !== 'unitLabel')) seats.push(row[k]);
          });
        });
      }
      const needed = seats.filter((v) => THAI_SCRIPT.test(langTh(v)));
      const counts = {};
      CARD_LANGS.filter((lang) => lang !== 'th').forEach((lang) => {
        counts[lang] = { done: needed.filter((v) => this.copyMember(v, lang) !== '').length, total: needed.length };
      });
      return counts;
    },
    cardLangTitle() {
      return CARD_LANG_TITLES[this.cardLang] || this.cardLang;
    },
    /** The language switch's buttons. A SHORT_FORM also shows how many of its seats each language covers — the
     *  other kinds keep their copy in the JSON boxes, where the preview's own gap warning does that job. */
    cardLangItems() {
      const counts = this.isShortForm ? this.shortFormTranslationCounts : {};
      return CARD_LANGS.map((lang) => {
        const c = counts[lang];
        return {
          value: lang,
          title: CARD_LANG_TITLES[lang] || lang,
          count: c && c.total ? `${c.done}/${c.total}` : '',
          complete: !c || c.done === c.total,
        };
      });
    },
    /** The two ways a mini-form row can be drawn. '' rather than null for the money box, so the select shows the
     *  choice as made rather than as empty — every card authored before steppers existed is a money box. */
    shortFormControlOptions() {
      return [
        { title: 'ช่องกรอกจำนวนเงิน', value: '' },
        { title: 'ปุ่มนับ − / + (จำนวนคน)', value: 'STEPPER' },
        { title: 'Checkbox (มี / ไม่มี — ส่ง 1 หรือ 0)', value: 'CHECKBOX' },
      ];
    },
    /** Member fieldKeys used more than once in this card. The backend 400s on the duplicate, and until it is marked
     *  the two colliding rows look exactly like every other row — the author has no way to tell which pair is at
     *  fault from the error list alone. */
    shortFormDuplicateKeys() {
      // 🔴 Read the RAW rows, never `shortFormFieldKeys(this.item)`. That helper de-duplicates (it feeds the
      //    builder table's "N ช่อง" summary), so counting repeats in its output can never find one — the guard
      //    below looked right, always returned [], and the duplicate sailed through to the backend, where
      //    TaxShortFormCardV2.Validate throws and the VERSION-WIDE bulk save 400s, taking every other edit in
      //    that batch with it. The rule is the same one normalizeCalcConfig states for functionIds: surface the
      //    authoring mistake here, do not collapse it.
      // Case-insensitive, because that is how the backend compares them.
      const seen = new Set();
      const dup = new Set();
      (this.shortFormConfig.fields || []).forEach((row) => {
        const key = String((row && row.fieldKey) || '').trim();
        if (!key) return;
        const probe = key.toLowerCase();
        if (seen.has(probe)) dup.add(probe);
        seen.add(probe);
      });
      return [...dup];
    },
    /** The item's own key colliding with one of its member fields, shown ON the key field. Same rule the backend
     *  enforces: the checkpoint key marks the card answered, so pointing it at one of the amounts would have the
     *  card's own bookkeeping overwrite a number the customer typed. */
    shortFormOwnKeyError() {
      if (!this.isShortForm) return '';
      const own = String(this.item.fieldKey || '').trim().toLowerCase();
      if (!own) return '';
      // Case-insensitive, matching the backend's OrdinalIgnoreCase compare — an exact-match test only ever
      // under-warns, and the author then finds out from a 400 on the version-wide save instead.
      return shortFormFieldKeys(this.item).some((k) => String(k).toLowerCase() === own)
        ? 'คีย์นี้ซ้ำกับ Field ของช่องกรอกในการ์ด — backend จะปฏิเสธการบันทึก'
        : '';
    },
    /// Parse errors shown live. The backend rejects the same things on save — this is only so the author sees a
    /// typo while the dialog is still open rather than as a 400 after pressing Save.
    summaryTemplateError() {
      return this.jsonError(this.item.summaryTemplateJson);
    },
    cardConfigError() {
      return this.jsonError(this.item.cardConfigJson);
    },
    /// Same live parse check for the SHORT_FORM structure template. Blank is legal here (it means "use the
    /// built-in structure"), and `jsonError` already returns '' for a blank string — unlike the two above, this
    /// one is ALSO a save blocker, see `validationErrors`.
    shortFormTemplateError() {
      return this.jsonError(this.item.shortFormTemplateJson);
    },
    shortFormTemplateHint() {
      return SHORT_FORM_TEMPLATE_HINT;
    },
    /// Same live parse check for the FILING_CHOICE structure template. Blank is legal (it means "use the built-in
    /// structure") and `jsonError` already returns '' for a blank string — and like the SHORT_FORM one above (and
    /// UNLIKE the two SUMMARY boxes) this one is ALSO a save blocker, see `validationErrors`.
    filingTemplateError() {
      return this.jsonError(this.item.filingTemplateJson);
    },
    filingTemplateHint() {
      return FILING_TEMPLATE_HINT;
    },
    /// Same live parse check for the SIMULATE structure template. Blank is legal ("use the built-in structure")
    /// and, like the two above, this one is ALSO a save blocker — see `validationErrors`.
    simulationTemplateError() {
      return this.jsonError(this.item.simulationTemplateJson);
    },
    simulationTemplateHint() {
      return SIMULATION_TEMPLATE_HINT;
    },
    summaryTemplateHint() {
      return SUMMARY_TEMPLATE_HINT;
    },
    /**
     * The whole-config JSON box's text: the live draft while the author is typing, otherwise the config printed.
     *
     * 🔴 The draft is what makes the box editable AT ALL. Re-printing `JSON.stringify(config)` on every keystroke
     * would reformat the document under the cursor, and a half-typed one does not parse — with no draft it would
     * be thrown away the instant it stopped being valid JSON, i.e. after the first character.
     *
     * 🔴 Printed from `item.shortFormConfig` — the stored document — NOT from the `shortFormConfig` computed. That
     *    computed is the Thai view the plain boxes bind to: it flattens every language bag to its `th` member and
     *    leaves out `style` and every unknown key. Printing it here hid the English/Chinese copy, and the first
     *    keystroke in this box then wrote the flattened document back through `setShortFormConfigJson`, deleting
     *    those translations from the card for the next builder SAVE.
     */
    shortFormConfigText() {
      const draft = this.styleDrafts.get(SHORT_FORM_CONFIG_JSON);
      if (draft !== undefined) return draft;
      const c = this.item.shortFormConfig;
      const doc = c && typeof c === 'object' && !Array.isArray(c) ? c : this.shortFormConfig;
      return JSON.stringify(doc, null, 2);
    },
    /** '' when the box holds a usable config. Blank is an ERROR here — a mini-form has no blank state, unlike a
     *  style bag, where blank means "not styled". */
    shortFormConfigJsonError() {
      const draft = this.styleDrafts.get(SHORT_FORM_CONFIG_JSON);
      if (draft === undefined) return '';
      if (!String(draft).trim()) return 'ต้องมีเอกสาร config — เว้นว่างไม่ได้';
      let parsed;
      try {
        parsed = JSON.parse(draft);
      } catch (err) {
        return String((err && err.message) || 'JSON ไม่ถูกต้อง');
      }
      return parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)
        ? ''
        : 'ต้องเป็นอ็อบเจ็กต์ JSON เช่น { "cardType": "TAX_AMOUNT_FORM", "fields": [] }';
    },
    /** The card-wide style box's text — the live draft when there is one, otherwise the stored bag printed. */
    shortFormStyleText() {
      return this.styleText(SHORT_FORM_CARD_STYLE);
    },
    shortFormStyleError() {
      return this.styleError(SHORT_FORM_CARD_STYLE);
    },
    fieldKeyLabel() {
      if (this.isIncomeClassify) return 'Checkpoint key (fieldKey) *';
      if (this.isIncomeClassifier) return 'Classifier key (fieldKey) *';
      return this.item.itemKind === 'CONFIRM_SKIP' ? 'Skip-checkpoint key (fieldKey)' : 'Confirm key (fieldKey)';
    },
    fieldKeyPlaceholder() {
      if (this.hasIncomeCatalog) return 'เช่น INCOME_TYPES';
      return this.item.itemKind === 'CONFIRM_SKIP' ? 'เช่น confirm_skipped' : 'เช่น confirm_income_types';
    },
    fieldKeyHint() {
      // 🔴 The two income kinds use their key for DIFFERENT things, and the difference is worth spelling out here:
      //    a classifier's key names the row that stores the §40 flag vector, while a driver's key names a checkpoint
      //    ("this income conversation is settled") — the amounts land on the §40 rows the catalog below points at.
      if (this.isIncomeClassify) {
        return 'คีย์เช็คพอยต์ของบทสนทนารายได้ — ไม่ใช่ที่เก็บยอดเงิน · ยอดแต่ละหมวดจะไปลงที่ข้อ §40 ที่ระบุใน "ปลายทางที่ยอดไปลง" ของหมวดนั้น';
      }
      return this.isIncomeClassifier
        ? 'คีย์ของตัวจำแนก — ใช้ตั้งชื่อแถวคำตอบ และเป็นฐานของ flag ที่ gate ของหมวดรายได้ใช้เป็นเงื่อนไข'
        : 'คีย์คงที่ของจุดนี้ (ไม่ผูกกฎภาษี/ข้อมูลส่วนตัว) — เก็บตามที่พิมพ์';
    },
    showWidget() {
      // Same list the save payload honours (SCALAR_KINDS). Hiding the two dropdowns for a kind the payload still
      // writes — or showing them for one it nulls — is how a kind's authored radio/text gets erased without
      // anyone touching it.
      return SCALAR_KINDS.includes(this.item.itemKind);
    },
    // «คำที่ลูกค้าใช้เรียกข้อนี้ (จับคำขอแก้)» — same list the save payload honours (EDIT_KEYWORD_KINDS), for the same
    // reason as showWidget above: a box shown for a kind the payload sends `[]` for would be erased on save.
    showEditKeywords() {
      return EDIT_KEYWORD_KINDS.includes(String(this.item.itemKind || '').trim().toUpperCase());
    },
    // SUMMARY keeps its words in the same box, but they mean something else: «ดึงการ์ดสรุปมาแสดง» (the node's recall
    // lane) — not an edit-request target. Only the wording flips; every other kind keeps the original strings verbatim.
    editKeywordsIsSummary() {
      return String(this.item.itemKind || '').trim().toUpperCase() === 'SUMMARY';
    },
    editKeywordsLabel() {
      return this.editKeywordsIsSummary
        ? 'คำที่ลูกค้าใช้เรียกการ์ดนี้ (ดึงการ์ดสรุปมาแสดง)'
        : 'คำที่ลูกค้าใช้เรียกข้อนี้ (จับคำขอแก้)';
    },
    editKeywordsPlaceholder() {
      return this.editKeywordsIsSummary
        ? 'พิมพ์แล้ว Enter — เช่น สรุปรายได้, การ์ดสรุปรายได้'
        : 'พิมพ์แล้ว Enter — เช่น วิธียื่น, ยื่นแยก, แยกยื่น';
    },
    editKeywordsHint() {
      return this.editKeywordsIsSummary
        ? 'คำหรือวลีที่ลูกค้าพิมพ์เมื่ออยากดูการ์ดสรุปใบนี้อีกครั้ง — ระบบดึงการ์ดมาแนบกับคำถามที่ค้างเมื่อข้อความมีคำใดคำหนึ่งนี้ทั้งคำ และไม่มีคำว่า แก้/เปลี่ยน/ปรับ · ดึงเฉพาะการ์ดที่เคยแสดงไปแล้ว · ใช้คำที่ชี้การ์ดใบนี้โดยเฉพาะ อย่าใช้คำกว้าง ๆ (เช่น รายได้ ภาษี) · พิมพ์หลายภาษาปนกันได้'
        : 'คำหรือวลีที่ลูกค้ามักพิมพ์เมื่อขอแก้ข้อนี้ (ไม่ใช่ชื่อที่โชว์ในเมนู) — ระบบจับคำขอแก้เมื่อข้อความมีคำใดคำหนึ่งนี้ทั้งคำ และส่งให้ AI เป็นคำใบ้ · ใช้คำที่ชี้ข้อนี้โดยเฉพาะ อย่าใช้คำกว้าง ๆ (เช่น ภาษี เงิน) · พิมพ์หลายภาษาปนกันได้';
    },
    kindHint() {
      return KIND_HINTS[this.item.itemKind] || '';
    },
    rulePickerNoData() {
      return this.configVersionId == null
        ? 'เปิดจากหน้า builder ที่มี config version จึงจะเลือก Field Key ได้'
        : 'ไม่พบ Field Key ในเวอร์ชันนี้ — สร้างที่ Tax Config V2 ก่อน';
    },
    // ── CALCULATE pickers ──
    // 🔴 The API answers with `calcVersionID` / `functionID` (capital ID); the stored config uses
    //    `calcVersionId` / `functionId` (lower d). Every crossing of that boundary happens HERE and in the
    //    add/save methods — mixing the two silently yields `undefined` ids that normalizeCalcConfig then drops.
    calcVersionOptions() {
      return (this.calcVersions || [])
        .map((v) => {
          const bits = [];
          if (v.description) bits.push(String(v.description));
          if (Number(v.functionCount) > 0) bits.push(`${Number(v.functionCount)} สูตร`);
          return {
            value: Number(v.calcVersionID),
            title: String(v.versionCode || '').trim() || `#${v.calcVersionID}`,
            subtitle: bits.join(' · '),
            isActive: v.isActive === true,
          };
        })
        .filter((o) => Number.isFinite(o.value) && o.value > 0);
    },
    calcVersionNoData() {
      if (this.configVersionId == null) return 'เปิดจากหน้า builder ที่มี config version จึงจะเลือกเวอร์ชันการคำนวณได้';
      if (this.loadingCalcVersions) return 'กำลังโหลดเวอร์ชันการคำนวณ…';
      return 'ไม่พบเวอร์ชันการคำนวณใต้ config version นี้ — สร้างที่หน้า Tax Calculate V2 ก่อน';
    },
    /** Formulas still available to add — the picked ones are removed, because a duplicate functionId is a 400. */
    calcFunctionOptions() {
      const used = new Set(this.calcConfig.functions.map((f) => Number(f && f.functionId)));
      return (this.calcFunctions || [])
        .map((f) => ({
          value: Number(f.functionID),
          title: String(f.name || '').trim() || String(f.functionCode || '').trim() || `#${f.functionID}`,
          functionCode: String(f.functionCode || '').trim(),
          fieldKey: String(f.fieldKey || '').trim(),
          warn: this.calcFunctionWarning(f),
        }))
        .filter((o) => Number.isFinite(o.value) && o.value > 0 && !used.has(o.value));
    },
    calcFunctionNoData() {
      if (this.calcConfig.calcVersionId == null) return 'เลือกเวอร์ชันการคำนวณก่อน';
      if (this.loadingCalcFunctions) return 'กำลังโหลดรายการสูตร…';
      if (this.calcFunctionsFailed) return 'โหลดรายการสูตรไม่สำเร็จ — ปิดแล้วเปิด dialog นี้ใหม่อีกครั้ง';
      if (this.calcFunctions.length) return 'เลือกครบทุกสูตรในเวอร์ชันนี้แล้ว';
      return 'ไม่พบสูตรในเวอร์ชันการคำนวณนี้ — สร้างที่หน้า Tax Calculate V2 ก่อน';
    },
    /** True only when `calcFunctions` is a COMPLETE, successful list of the selected version. A row that cannot be
     *  resolved is only called "missing" under this flag — otherwise a slow fetch would accuse every row. */
    calcFunctionsReady() {
      return !this.loadingCalcFunctions
        && !this.calcFunctionsFailed
        && this.calcConfig.calcVersionId != null
        && this.calcFunctionsVersionId === this.calcConfig.calcVersionId;
    },
    /** The stored refs (`functionId` + `functionCode` only) joined to the API rows, IN STORED ORDER = run order.
     *  Resolution is by id then by code — a calc-version clone regenerates FunctionID but keeps FunctionCode, the
     *  same precedence the executor will use. An unresolved ref still produces a row: config the admin authored is
     *  never hidden from them, it is flagged. */
    selectedCalcFunctions() {
      const byId = new Map();
      const byCode = new Map();
      (this.calcFunctions || []).forEach((f) => {
        const id = Number(f.functionID);
        if (Number.isFinite(id)) byId.set(id, f);
        const code = String(f.functionCode || '').trim();
        if (code) byCode.set(code, f);
      });
      const ready = this.calcFunctionsReady;
      return this.calcConfig.functions.map((ref) => {
        const code = String((ref && ref.functionCode) || '').trim();
        const meta = byId.get(Number(ref && ref.functionId)) || (code ? byCode.get(code) : null) || null;
        const metaCode = meta ? String(meta.functionCode || '').trim() : '';
        return {
          functionId: Number(ref && ref.functionId),
          functionCode: metaCode || code,
          name: meta
            ? (String(meta.name || '').trim() || metaCode || `#${meta.functionID}`)
            : (code || `#${ref && ref.functionId}`),
          fieldKey: meta ? String(meta.fieldKey || '').trim() : '',
          resolved: Boolean(meta),
          missing: ready && !meta,
          warn: meta ? this.calcFunctionWarning(meta) : '',
        };
      });
    },
    // Project the stored answer-options objects into the three language fields used by the editor.
    // A chip stays whatever shape it already has: editing any label of a {label,value} chip keeps its
    // value (only RAW JSON can author one), so the row editor is never lossy.
    chipRows() {
      return (Array.isArray(this.item.answerOptions) ? this.item.answerOptions : [])
        .map((o) => {
          const option = o && typeof o === 'object' && !Array.isArray(o) ? o : {};
          return {
            label: o && typeof o === 'object' && !Array.isArray(o)
              ? String(option.label ?? option.Label ?? option.value ?? option.Value ?? '')
              : String(o ?? ''),
            labelEn: String(option.labelEn ?? option.LabelEn ?? ''),
            labelZh: String(option.labelZh ?? option.LabelZh ?? ''),
          };
        });
    },
    translatableChipCount() {
      return (Array.isArray(this.item.answerOptions) ? this.item.answerOptions : [])
        .filter((o) => String(this.answerOptionField(o, 'label') || '').trim()).length;
    },
    /**
     * Per-language completeness of THIS item's chips, as the runtime will read it.
     *
     * 🔴 The rule it reports is the runtime's, not a house style: a language is used only when EVERY
     * chip of the item has it AND no two of them collide. One missing box, or two buttons that ended
     * up with the same English word, and the whole row renders in Thai — so an author needs to see
     * "3/5" rather than discovering it by testing the bot in English.
     */
    chipLangBadges() {
      return this.langBadgesFor(this.chipRows);
    },
    // The §40 catalog projected into chip shape — `{label: labelTh, value: flagKey}`, the same shape the shipped
    // authored chip rows use. Only complete categories qualify: a half-typed row would author a blank button.
    // Where an INCOME_CLASSIFY category is allowed to fan out: the FIELD_INPUT rows of THIS version, by FieldKey.
    // 🔴 EXP_* rows are excluded rather than merely rejected on save. The value in EXP_OPTION_40_x is a FLAT-RATE
    //    CODE a formula switches on — write an amount over it and the calculation silently falls back to the lowest
    //    rate, with nothing failing anywhere. Not offering the key is the cheapest place to stop that.
    // 🔴 Deduped by key: §40(1) is authored as TWO items on one FieldKey (full-year / part-year) and the driver picks
    //    the row by gate at write time, so the author picks a KEY here, never one of the two rows.
    fanOutTargetOptions() {
      const seen = new Set();
      const out = [];
      (Array.isArray(this.siblingItems) ? this.siblingItems : []).forEach((it) => {
        if (!it || String(it.itemKind || '').trim().toUpperCase() !== 'FIELD_INPUT') return;
        const key = String(it.fieldKey || '').trim();
        if (!key || seen.has(key)) return;
        if (/^EXP_/i.test(key)) return;
        seen.add(key);
        out.push({ value: key, title: it.label ? `${key} — ${it.label}` : key });
      });
      return out.sort((a, b) => a.value.localeCompare(b.value));
    },
    fanOutNoData() {
      return this.fanOutTargetOptions.length
        ? 'ไม่พบ'
        : 'เวอร์ชันนี้ยังไม่มีข้อ FIELD_INPUT ให้เขียนยอดลง — ต้องมีข้อ INCOME_40_x ก่อน';
    },
    // Which §40 amount row is claimed by more than one category. Two sections pointing at one row is not a duplicate
    // item — each row is individually valid — so nothing else here would notice; what the customer gets is one
    // section's income silently overwritten by the other's on whichever turn lands second.
    fanOutDuplicateKeys() {
      const seen = new Set();
      const dup = new Set();
      (Array.isArray(this.item.incomeCategories) ? this.item.incomeCategories : []).forEach((c) => {
        const key = String((c && c.incomeFieldKey) || '').trim().toUpperCase();
        if (!key) return;
        if (seen.has(key)) dup.add(key);
        seen.add(key);
      });
      return Array.from(dup);
    },
    incomeCategoryChips() {
      return (Array.isArray(this.item.incomeCategories) ? this.item.incomeCategories : [])
        .filter((c) => c && String(c.labelTh || '').trim() && String(c.flagKey || '').trim())
        .map((c) => ({ label: String(c.labelTh).trim(), value: String(c.flagKey).trim() }));
    },
    // What the runtime does when this list is EMPTY — TaxQuickReplyV2.Derive's precedence table, in Thai.
    //
    // 🔴 The per-kind FALLBACKS ARE GONE. `Derive` used to answer an empty list with the §40 catalog for a
    // classifier and "ใช่/ไม่ใช่" for a CONFIRM; both sources were removed, so those promises would now be lies
    // told to whoever is authoring the row. The only thing left below an empty list is the LLM.
    quickReplyFallbackHint() {
      // The driver asks a different question every turn (หมวดไหน → ยอดเท่าไหร่ → ประเภทย่อยอะไร), so a fixed chip
      // row authored here would sit under all of them. Its buttons come from the CATALOG, per turn.
      if (this.isIncomeClassify) {
        return 'ข้อนี้ปรุงปุ่มเองตามคำถามของแต่ละเทิร์น (รายชื่อหมวด / ประเภทย่อยของหมวดนั้น) — ปุ่มที่ตั้งไว้ตรงนี้จะขึ้นทุกคำถามในบทสนทนารายได้ ปกติจึงปล่อยว่าง';
      }
      if (this.isIncomeClassifier) {
        return this.incomeCategoryChips.length
          ? `ยังไม่มีปุ่มที่ตั้งไว้ — คำถามนี้จะไม่มีปุ่ม (ระบบไม่ใช้ "ชื่อประเภทเงินได้ §40" เป็นปุ่มให้อัตโนมัติแล้ว) · กด "เติมจากประเภท §40" ถ้าต้องการปุ่ม ${this.incomeCategoryChips.length} อันนั้น`
          : 'ยังไม่มีปุ่มที่ตั้งไว้ — คำถามนี้จะไม่มีปุ่มเลย';
      }
      if (this.item.itemKind === 'CONFIRM' || this.item.itemKind === 'CONFIRM_SKIP') {
        return 'ยังไม่มีปุ่มที่ตั้งไว้ — ระบบไม่มีปุ่มสำรองให้ชนิดคำถามนี้แล้ว ต้องเพิ่มเอง (ไม่งั้นจะเหลือแค่ปุ่มที่ LLM ปรุง หรือไม่มีปุ่มเลย)';
      }
      return 'ยังไม่มีปุ่มที่ตั้งไว้ — ถ้าไม่ใช่คำถามจำนวนเงิน/วันที่ ระบบจะให้ LLM ปรุงปุ่มให้เอง';
    },
    preferConfigHint() {
      const tail = 'มีผลเฉพาะเมื่อคำถามนี้มีปุ่มคำตอบ';
      return `ปิด (ค่าเริ่มต้น) = ไม่เรียก LLM ใช้ปุ่มที่ตั้งไว้ด้านบนอย่างเดียว · เปิด = เรียก LLM มาปรุงปุ่มให้เมื่อคำถามนี้ยังไม่มีปุ่มที่ตั้งไว้ · ${tail}`;
    },
    visibleWhenWarn() {
      const raw = String(this.item.visibleWhen || '').trim();
      if (!raw || /^always$/i.test(raw)) return '';
      try {
        return visibleWhenWarning(raw, this.knownFieldKeys) || '';
      } catch {
        return '';
      }
    },
    knownFieldKeys() {
      return (this.ruleOptions || []).map((o) => o && o.value).filter(Boolean);
    },
    validationErrors() {
      const e = [];
      const it = this.item;
      if (!String(it.label || '').trim()) e.push('ต้องกรอก Label');
      if (this.isFieldInput && !String(it.fieldKey || '').trim()) {
        e.push('FIELD_INPUT ต้องเลือก Field Key');
      }
      if (this.isIncomeClassifier) {
        // A blank classifier key is not cosmetic: the runtime derives `<fieldKey>_ambiguous` from it, and a blank
        // one falls back to a shared literal — so two classifiers in one version end up writing the same marker.
        if (!String(it.fieldKey || '').trim()) e.push('INCOME_CLASSIFIER ต้องกรอก Classifier key (fieldKey)');
        const complete = (Array.isArray(it.incomeCategories) ? it.incomeCategories : [])
          .filter((c) => c && String(c.code || '').trim() && String(c.flagKey || '').trim() && String(c.labelTh || '').trim());
        if (!complete.length) e.push('INCOME_CLASSIFIER ต้องมีประเภทที่กรอก รหัส + flag + ชื่อ ครบอย่างน้อย 1 ประเภท');
      }
      if (this.isIncomeClassify) {
        // Every rule below is a 400 on the backend (TaxQuestionItemV2BAL.NormalizeIncomeCategories). Repeating them
        // here is what keeps a half-authored driver from being staged into the builder as an item that only fails at
        // the very end, on the version-wide bulk save, with a dozen unrelated edits going down with it.
        const ownKey = String(it.fieldKey || '').trim();
        if (!ownKey) e.push('INCOME_CLASSIFY ต้องกรอก Checkpoint key (fieldKey)');
        const cats = Array.isArray(it.incomeCategories) ? it.incomeCategories : [];
        const complete = cats.filter(
          (c) => c && String(c.code || '').trim() && String(c.flagKey || '').trim() && String(c.labelTh || '').trim(),
        );
        if (!complete.length) e.push('INCOME_CLASSIFY ต้องมีหมวดที่กรอก รหัส + flag + ชื่อ ครบอย่างน้อย 1 หมวด');
        const noTarget = complete.filter((c) => !String(c.incomeFieldKey || '').trim());
        if (noTarget.length) {
          e.push(`ทุกหมวดต้องเลือก "ปลายทางที่ยอดไปลง": ${noTarget.map((c) => c.labelTh || c.code).join(', ')}`);
        }
        if (this.fanOutDuplicateKeys.length) {
          e.push(`มีมากกว่าหนึ่งหมวดเขียนลงแถวเดียวกัน: ${this.fanOutDuplicateKeys.join(', ')} — ยอดของหมวดหนึ่งจะทับอีกหมวด`);
        }
        const selfTarget = complete.filter(
          (c) => [c.incomeFieldKey, c.periodFieldKey]
            .some((k) => String(k || '').trim().toUpperCase() === ownKey.toUpperCase() && ownKey),
        );
        if (selfTarget.length) {
          e.push(`หมวด ${selfTarget.map((c) => c.labelTh || c.code).join(', ')} ชี้ปลายทางกลับมาที่ Checkpoint key ของข้อนี้เอง — เช็คพอยต์กับยอดเงินต้องคนละคีย์`);
        }
        const subIncomplete = complete.filter(
          (c) => (Array.isArray(c.subTypes) ? c.subTypes : [])
            .some((s) => !String((s && s.code) || '').trim() || !String((s && s.labelTh) || '').trim()),
        );
        if (subIncomplete.length) {
          e.push(`ประเภทย่อยต้องกรอกทั้งรหัสและชื่อ: ${subIncomplete.map((c) => c.labelTh || c.code).join(', ')}`);
        }
      }
      if (this.isCalculate) {
        // Both are 400s on the backend. Catching them here is what keeps a CALCULATE row from being staged into
        // the builder as a no-op item that only fails at the very end, on the version-wide bulk save.
        if (!(Number(this.calcConfig.calcVersionId) > 0)) e.push('CALCULATE ต้องเลือกเวอร์ชันการคำนวณ (Tax Calculate)');
        if (!this.calcConfig.functions.length) e.push('CALCULATE ต้องเลือกสูตรอย่างน้อย 1 สูตร');
      }
      if (this.isFilingChoice || this.isSimulate) {
        // Same reason as every other block here: these are 400s on the backend, and the save that carries them is
        // the VERSION-WIDE bulk save — one unstaged rule takes the whole batch down after a dozen other edits.
        if (this.filingOrSimulateKeyError) {
          e.push((this.isFilingChoice ? 'FILING_CHOICE' : 'SIMULATE') + ' ' + this.filingOrSimulateKeyError);
        }
        // 🔴 Mandatory for SIMULATE only: which deductions may be slid and which rows are compared are config
        //    decisions end to end, so there is no built-in default that could be right. FILING_CHOICE's config is
        //    optional COPY — blank falls back to the built-in Thai.
        if (this.isSimulate && !String(it.cardConfigJson || '').trim()) {
          e.push('SIMULATE ต้องระบุ cardConfig (รายการลดหย่อนที่ให้เลื่อน + แถวที่จะเทียบก่อน/หลัง)');
        }
        // 🔴 BLOCKING, copied from SHORT_FORM and deliberately NOT from SUMMARY: SUMMARY shows the very same parse
        //    error as a field message and still lets the save through. The backend 400s on an unparseable template,
        //    and the save that carries it is the VERSION-WIDE bulk save — one broken box takes every other item
        //    edited in the batch down with it. Blank stays legal (blank = the built-in structure), and
        //    `filingTemplateError` is '' then. FILING_CHOICE only: SIMULATE has no template column at all.
        if (this.isSimulate && this.simulationTemplateError) {
          e.push(`โครง JSON ของการ์ด (ขั้นสูง) ไม่ถูกต้อง — ${this.simulationTemplateError} · เว้นว่างไว้ = ใช้โครงมาตรฐานของระบบ`);
        }
        if (this.isFilingChoice && this.filingTemplateError) {
          e.push(`โครง JSON ของการ์ด (ขั้นสูง) ไม่ถูกต้อง — ${this.filingTemplateError} · เว้นว่างไว้ = ใช้โครงมาตรฐานของระบบ`);
        }
      }
      // 🔴 SAVE_STATE only — see `saveStateCodeError`. The slot name is what a resume turn looks the item up BY, so
      //    a nameless one is an item the runtime can never find: it would keep pinning a blank state on the form and
      //    the customer would keep getting the generic system resume line, with nothing on screen to say why.
      if (this.saveStateCodeError) e.push('SAVE_STATE ' + this.saveStateCodeError);
      // USAGE_FUNNEL_STATE (#746) — same reason: a 400 on the backend inside the version-wide bulk save.
      if (this.usageFunnelStateError) e.push('USAGE_FUNNEL_STATE ' + this.usageFunnelStateError);
      // 🔴 Buttons with no text is a 400 on the backend too. Catching it here is what keeps a half-authored resume
      //    screen out of the builder — the save that carries it is the VERSION-WIDE bulk save, so one unstaged rule
      //    takes every other edit down with it. (The reverse, text with no buttons, is LEGAL: the customer can
      //    still answer stage 2 by typing, which the runtime reads with its yes/no heuristic.)
      if (this.stateConfirmTextError) e.push('SAVE_STATE ' + this.stateConfirmTextError);
      // Same reason: the backend refuses the tick without both halves, so catching it here keeps one stale
      // checkbox from taking the whole version-wide bulk save down with it.
      if (this.stateConfirmSkipError) e.push('SAVE_STATE ' + this.stateConfirmSkipError);
      if (this.isShortForm) {
        // Every rule below is a 400 on the backend. Repeating them here is what keeps a half-authored card from
        // being staged into the builder as an item that only fails at the very end, on the version-wide bulk save —
        // by which point a dozen other items have been edited and the whole batch goes down with it.
        const cfg = this.shortFormConfig;
        const ownKey = String(it.fieldKey || '').trim();
        if (!ownKey) e.push('SHORT_FORM ต้องกรอก Checkpoint key (fieldKey) ของการ์ด');
        if (!langTh(cfg.title)) e.push('SHORT_FORM ต้องกรอกหัวการ์ด (title)');
        if (!cfg.fields.length) e.push('SHORT_FORM ต้องมีช่องกรอกอย่างน้อย 1 ช่อง');
        // The row label is optional — a blank one draws the box with no heading.
        const incomplete = cfg.fields.some((f) => !String((f && f.fieldKey) || '').trim());
        if (incomplete) e.push('ทุกช่องของ SHORT_FORM ต้องเลือก Field ให้ครบ');
        if (this.shortFormDuplicateKeys.length) {
          e.push(`SHORT_FORM มี Field ซ้ำกันในการ์ดเดียวกัน: ${this.shortFormDuplicateKeys.join(', ')}`);
        }
        if (this.shortFormOwnKeyError) {
          e.push(`Checkpoint key "${ownKey}" ซ้ำกับ Field ของช่องกรอกในการ์ด — ต้องเป็นคนละคีย์`);
        }
        // 🔴 SUMMARY shows the very same parse error as a field message and still lets the save through; that
        //    inconsistency is NOT copied here. The backend 400s on an unparseable template, and the save that
        //    carries it is the VERSION-WIDE bulk save — one broken box takes every other edit in the batch down
        //    with it. Blank stays legal (blank = the built-in structure), and `shortFormTemplateError` is '' then.
        if (this.shortFormTemplateError) {
          e.push(`โครง JSON ของการ์ด (ขั้นสูง) ไม่ถูกต้อง — ${this.shortFormTemplateError} · เว้นว่างไว้ = ใช้โครงมาตรฐานของระบบ`);
        }
        // 🔴 BLOCKING, for the same reason the template box above is. A style box that does not parse keeps its
        //    LAST GOOD bag on the config — so letting the save through would store the palette the author has just
        //    replaced, under a screen still showing the text they typed. The two would then disagree with nothing
        //    on screen to say so.
        // 🔴 BLOCKING, like the two boxes around it. An unparseable whole-config draft leaves the LAST GOOD
        //    config on the item, so letting the save through would store the card the author has just replaced,
        //    under a screen still showing the text they typed.
        if (this.shortFormConfigJsonError) {
          e.push(`Card config (JSON) ไม่ถูกต้อง — ${this.shortFormConfigJsonError}`);
        }
        if (this.shortFormStyleError) e.push(`สไตล์การ์ด (JSON) ไม่ถูกต้อง — ${this.shortFormStyleError}`);
        const badRow = cfg.fields.findIndex((f) => this.rowStyleError(f));
        if (badRow >= 0) {
          e.push(`สไตล์แถว (JSON) ของช่องลำดับที่ ${badRow + 1} ไม่ถูกต้อง — ${this.rowStyleError(cfg.fields[badRow])}`);
        }
      }
      return e;
    },
  },
  watch: {
    /**
     * 🔴 Opening the box RE-READS the config, by dropping the draft. Without this, a draft left from an earlier
     * visit would still be on screen after the author had edited the structured boxes above — showing them a
     * document that is no longer the item's, and saving it the moment they touched anything.
     */
    shortFormJsonPanel(open) {
      if (open !== undefined && open !== null) this.styleDrafts.delete(SHORT_FORM_CONFIG_JSON);
    },
    modelValue(open) {
      if (open) {
        this.resetForm();
        this.loadRules();
        this.loadStateGroupOptions();
        // Only a CALCULATE item pays for the two calc fetches; every other kind opens without touching them.
        if (this.isCalculate) this.syncCalcData();
      }
    },
  },
  methods: {
    // ── "จัดรูปแบบ JSON" — the button on every JSON box in this dialog ────────────────────────────────
    /** The two below only exist because a `v-bind` expression cannot reach module scope — every rule they
     *  follow lives in `utils/jsonText`, where it is unit-tested. */
    formatJsonText(raw) {
      return formatJsonText(raw);
    },
    canFormatJson(raw) {
      return canFormatJsonText(raw);
    },
    /** Format one of the plain string columns the item carries (`cardConfigJson`, `summaryTemplateJson`,
     *  `shortFormTemplateJson`) — the boxes that are a `v-model` straight onto `item`. */
    beautifyItemJson(key) {
      const pretty = this.formatJsonText(this.item[key]);
      if (pretty !== null) this.item[key] = pretty;
    },
    /** Format a style box. Writes through its own setter, so the parsed bag lands on the config exactly as if
     *  the author had typed the formatted text themselves. */
    beautifyStyle(target) {
      const pretty = this.formatJsonText(this.styleText(target === null ? SHORT_FORM_CARD_STYLE : target));
      if (pretty !== null) this.setShortFormStyle(target, pretty);
    },
    /** Format the SHORT_FORM whole-config box. Same rule: through the setter, never around it. */
    beautifyShortFormConfigJson() {
      const pretty = this.formatJsonText(this.shortFormConfigText);
      if (pretty !== null) this.setShortFormConfigJson(pretty);
    },
    // Live JSON check for the two SUMMARY textareas. Vuetify renders the message under the field; the backend
    // repeats the check on save, so this is a courtesy, not the guarantee.
    jsonError(raw) {
      const s = (raw || '').trim();
      if (!s) return '';
      try { JSON.parse(s); return ''; } catch (e) { return 'JSON ไม่ถูกต้อง: ' + e.message; }
    },
    resetForm() {
      this.cardLang = 'th';
      this.item = this.initialItem
        ? JSON.parse(JSON.stringify(this.initialItem))
        : taxQuestionV2Store.createBlankItem();
      if (this.item._uid == null) this.item._uid = taxQuestionV2Store.createBlankItem()._uid;
      if (!Array.isArray(this.item.answerOptions)) this.item.answerOptions = [];
      this.normalizeAnswerOptions();
      // Stage-2 copy: every item authored before these columns existed arrives without them, and the three boxes
      // bind straight onto `item` — same treatment (and same reason) as `answerOptions` right above.
      this.normalizeConfirmOptions();
      if (typeof this.item.confirmTextTh !== 'string') this.item.confirmTextTh = '';
      if (typeof this.item.confirmSystemPrompt !== 'string') this.item.confirmSystemPrompt = '';
      this.item.confirmSkipToQuestion = this.item.confirmSkipToQuestion === true;
      // Same coercion as the tick above: rows saved before the column existed arrive without it.
      this.item.postCompleteEditReturn = this.item.postCompleteEditReturn === true;
      if (typeof this.item.stateGroup !== 'string') this.item.stateGroup = '';
      if (!Array.isArray(this.item.incomeCategories)) this.item.incomeCategories = [];
      if (!Array.isArray(this.item.questionKnowledgeIds)) this.item.questionKnowledgeIds = [];
      if (!Array.isArray(this.item.answerKnowledgeIds)) this.item.answerKnowledgeIds = [];
      // «คำที่ลูกค้าใช้เรียกข้อนี้» — the combobox binds straight onto `item`; rows saved before the column existed
      // arrive without it (same treatment as the knowledge lists above).
      if (!Array.isArray(this.item.editKeywords)) this.item.editKeywords = [];
      if (typeof this.item.questionKnowledgeQuery !== 'string') this.item.questionKnowledgeQuery = '';
      if (typeof this.item.quickReplyPreferConfig !== 'boolean') this.item.quickReplyPreferConfig = false;
      // 🔴 ALWAYS through the store's normalizer, even when the field looks fine: an item authored before CALCULATE
      //    existed has no `calcConfig` at all, and a key added after the object became reactive is not tracked —
      //    the panel would render once and then never update as formulas were added.
      this.item.calcConfig = normalizeCalcConfig(this.item.calcConfig);
      // Same treatment, same reason, for the SHORT_FORM card: every item opened here predates this kind except the
      // ones authored since, so `shortFormConfig` is missing far more often than it is present — and the card editor
      // binds straight into `.fields`.
      this.item.shortFormConfig = normalizeShortFormConfig(this.item.shortFormConfig);
      // 🔴 Cleared on every open, and the CARD sentinel is why it has to be: it is one constant shared by every
      //    item, so a draft left behind by the last card would be shown — and saved — on the next one.
      this.styleDrafts.clear();
      this.shortFormJsonPanel = undefined;
      this.rawChips = false;
      this.chipsRawText = '';
      this.chipsRawError = '';
      this.translatingChips = false;
      this.chipTranslationMessage = '';
      this.chipTranslationMessageType = 'info';
      this.translationProgress = { completed: 0, total: 0 };
      // …and the stage-2 row's own four slots, for the same reason: a "Translated 4 fields" banner left behind by
      // the previous pin would be read as a report about the item that was just opened.
      this.translatingConfirmChips = false;
      this.confirmChipTranslationMessage = '';
      this.confirmChipTranslationMessageType = 'info';
      this.confirmTranslationProgress = { completed: 0, total: 0 };
      // Per-open calc UI state: the pending dropdown pick, the switch notice and the fetched function list all
      // belong to the item that was open, never to the next one this dialog instance is reused for.
      this.calcFnToAdd = null;
      this.calcNotice = '';
      this.calcFunctions = [];
      this.calcFunctionsVersionId = null;
      this.calcFunctionsFailed = false;
      // Per-open SHORT_FORM template state — the button's outcome belongs to the item that was open, never to the
      // next one this dialog instance is reused for. 🔴 The TEMPLATE ITSELF is not touched here: it is a raw string
      // on the item and normalizing/formatting it would rewrite what the author typed.
      this.shortFormTemplateLoading = false;
      this.shortFormTemplateNotice = '';
      this.shortFormTemplateNoticeType = 'info';
      // Per-open FILING_CHOICE template state — same contract, same reason as the three lines above, including the
      // 🔴: `item.filingTemplateJson` itself is NOT touched here. It is a raw string the author typed and
      // normalizing or reformatting it on every open would rewrite their document under them.
      this.filingTemplateLoading = false;
      this.filingTemplateNotice = '';
      this.filingTemplateNoticeType = 'info';
      this.simulationTemplateLoading = false;
      this.simulationTemplateNotice = '';
      this.simulationTemplateNoticeType = 'info';
      // Ensure each income category carries an editable `_meta` bucket AND every mini-section member the editor
      // binds to (both are absent on any catalog authored before INCOME_CLASSIFY existed).
      this.item.incomeCategories.forEach((c) => this.ensureCatFields(c));
      this.submitAttempted = false;
    },
    async loadRules() {
      if (this.configVersionId == null) {
        this.ruleOptions = [];
        return;
      }
      this.loadingRules = true;
      try {
        const rules = await taxConfigV2Api.getRules(this.configVersionId, null, true);
        this.ruleOptions = (rules || [])
          .map((r) => ({ value: r.fieldKey, title: `${r.fieldKey} — ${r.label || ''}`.trim() }))
          .filter((o) => o.value);
      } catch {
        // Guard (R1): a transient field-registry fetch failure must never break the dialog.
        this.ruleOptions = [];
      } finally {
        this.loadingRules = false;
      }
    },
    /** App Bar group-state rows for the SAVE_STATE dropdown. Fail-soft like `loadRules`: a failed fetch leaves an
     *  empty list (the saved value still shows through `stateGroupItems`) and never breaks the dialog. The service
     *  caches a successful load, so re-opening the dialog does not re-fetch. */
    async loadStateGroupOptions() {
      this.loadingStateGroupOptions = true;
      try {
        const rows = await taxQuestionV2Api.getStateGroupOptions();
        this.stateGroupOptions = Array.isArray(rows) ? rows : [];
        this.stateGroupOptionsFailed = false;
      } catch {
        this.stateGroupOptions = [];
        this.stateGroupOptionsFailed = true;
      } finally {
        this.loadingStateGroupOptions = false;
      }
    },
    onKindChange(next) {
      const kind = String(next || '').trim().toUpperCase();
      const prevKind = String(this.item.itemKind || '').trim().toUpperCase();
      this.item.itemKind = kind;
      // Clear bindings that do not apply to the new kind (avoid leaking a stale fieldKey).
      if (!FIELDKEY_KINDS.includes(kind)) this.item.fieldKey = null;
      // 🔴 The catalog survives a flip BETWEEN the two income kinds and is dropped for anything else. Clearing it on
      //    INCOME_CLASSIFIER → INCOME_CLASSIFY would throw away the eight §40 sections at the exact moment someone is
      //    migrating them, which is the only reason anyone ever performs that flip.
      if (!INCOME_CATALOG_KINDS.includes(kind)) this.item.incomeCategories = [];
      if (INCOME_CATALOG_KINDS.includes(kind)) {
        if (!this.item.incomeCategories.length) this.addIncomeCategory();
        else this.item.incomeCategories.forEach((c) => this.ensureCatFields(c));
      }
      // 🔴 Config must not outlive its ItemKind. The save payload already NULLs `calcConfig` for other kinds, but
      //    the DIALOG kept showing the old formula list — so flipping CALCULATE → FIELD_INPUT → CALCULATE brought
      //    back a version + formulas the author had visibly moved away from, and saved them.
      if (kind !== 'CALCULATE') {
        this.item.calcConfig = normalizeCalcConfig(null);
        this.calcFnToAdd = null;
        this.calcNotice = '';
      } else {
        this.item.calcConfig = normalizeCalcConfig(this.item.calcConfig);
        this.syncCalcData();
      }
      // 🔴 Identical rule for the SHORT_FORM card, and it matters MORE here than it does for CALCULATE: the card
      //    config names sibling FieldKeys, so one left behind on an item that is no longer a SHORT_FORM is a set of
      //    live bindings pointing at amounts this item no longer draws. The save payload NULLs it for other kinds —
      //    but the DIALOG kept showing it, so flipping SHORT_FORM → FIELD_INPUT → SHORT_FORM restored a card the
      //    author had visibly walked away from, and saved it.
      if (kind !== 'SHORT_FORM') {
        this.item.shortFormConfig = normalizeShortFormConfig(null);
        // Same discipline, one field over: the structure template describes a SHORT_FORM card and nothing else,
        // and the save payload nulls it for every other kind. Leaving it in the box would show the author a
        // structure the payload throws away — and restore it on the way back in.
        this.item.shortFormTemplateJson = '';
        this.shortFormTemplateNotice = '';
      } else {
        this.item.shortFormConfig = normalizeShortFormConfig(this.item.shortFormConfig);
        // Open with one empty row, the same way switching to INCOME_CLASSIFIER seeds one category: an empty card is
        // never a valid save, so the first row is not a guess about intent.
        if (!this.item.shortFormConfig.fields.length) this.addShortFormField();
        this.seedShortFormDefaultTemplate();
      }
      // 🔴 Same discipline, one kind over — and the sibling kind is what makes it easy to get wrong: SIMULATE
      //    shares this dialog's editor branch with FILING_CHOICE but NOT the column. `buildItemRequest` NULLs
      //    `filingTemplateJson` for every other kind, so a structure left in the box after a flip is one the save
      //    payload throws away — and the dialog would restore it on the way back in, exactly the resurrection bug
      //    `calcConfig` and `shortFormConfig` above each record.
      if (kind !== 'FILING_CHOICE') {
        this.item.filingTemplateJson = '';
        this.filingTemplateNotice = '';
      } else {
        this.seedFilingDefaultTemplate();
      }
      // 🔴 …and the sibling kind that shares this editor branch now has a column of its own, so it needs the same
      //    clear-on-flip. Without it a SIMULATE template left behind on an item flipped to FILING_CHOICE would be
      //    NULLed by `buildItemRequest` and restored by the dialog — the resurrection bug the two blocks above
      //    each record.
      if (kind !== 'SIMULATE') {
        this.item.simulationTemplateJson = '';
        this.simulationTemplateNotice = '';
      } else {
        this.seedSimulationDefaultTemplate();
      }
      // 🔴 Same discipline as `calcConfig` / `shortFormConfig` above, and it bites HARDER than either: a stale
      //    `stateCode` left on an item flipped out of SAVE_STATE / SUMMARY / SIMULATE is not dead config, it is a
      //    live pointer into another kind's save-slot. The save payload NULLs it for other kinds — but the DIALOG
      //    would keep showing it, so flipping SUMMARY → FIELD_INPUT → SUMMARY would restore a slot binding the
      //    author had visibly walked away from, and save it.
      if (!STATECODE_KINDS.includes(kind)) this.item.stateCode = '';
      // 🔴 Same discipline, one field group over — and this one is a SCRIPT, not a binding: a stage-2 bubble left
      //    on an item flipped out of SAVE_STATE is a screen the customer would be shown again the moment the kind
      //    flipped back. The payload NULLs all three for every other kind (STATE_CONFIRM_KINDS), so leaving them in
      //    the boxes would show the author something the save throws away — and restore it on the way back in.
      if (!STATE_CONFIRM_KINDS.includes(kind)) {
        this.item.confirmTextTh = '';
        this.item.confirmOptions = [];
        this.item.confirmSystemPrompt = '';
        this.item.confirmSkipToQuestion = false;
        // «หลังแก้แผนที่จบแล้ว ให้กลับมาที่ฉากนี้» — same fence (STATE_CONFIRM_KINDS); the payload sends false anyway.
        this.item.postCompleteEditReturn = false;
        // The translator's report belongs to the list that was just emptied, so it goes with it.
        this.confirmChipTranslationMessage = '';
        this.confirmChipTranslationMessageType = 'info';
        this.confirmTranslationProgress = { completed: 0, total: 0 };
      }
      // «คำที่ลูกค้าใช้เรียกข้อนี้ (จับคำขอแก้)» — same clear-on-flip (EDIT_KEYWORD_KINDS): the payload sends `[]` for
      // every other kind, so keeping the chips would show something the save throws away and restore it on the way
      // back in. A flip BETWEEN two listed kinds keeps them (they still name the same item).
      // SUMMARY is in the list too (its words recall the card instead of targeting an edit) — a flip to/from SUMMARY
      // keeps the chips like any other listed pair; the box's label/hint switch with the kind so the admin sees which
      // meaning they now carry.
      if (!EDIT_KEYWORD_KINDS.includes(kind)) this.item.editKeywords = [];
      // Same clear-on-flip for the App Bar group state (STATEGROUP_KINDS): the payload NULLs it for other kinds.
      // 🔴 …and on a flip BETWEEN the two kinds that keep it (#746): SAVE_STATE holds an App Bar group there,
      //    USAGE_FUNNEL_STATE a funnel state — carrying one over would save a value the other kind's backend refuses.
      if (!STATEGROUP_KINDS.includes(kind) || (prevKind && prevKind !== kind)) this.item.stateGroup = '';
      // 🔴 Drop a stale `isRequired` on the way IN to a non-askable kind. The payload nulls it anyway, but the
      //    toggle it belongs to is hidden for these kinds — leaving the flag set would make the dialog show one
      //    thing and save another, and flipping back out would resurrect a requirement the author had moved away from.
      if (NON_ASKABLE_KINDS.includes(kind) || kind === 'END_FORM') this.item.isRequired = false;
      if (SCALAR_KINDS.includes(kind)) {
        if (!this.item.inputType) this.item.inputType = 'text';
        if (!this.item.answerType) this.item.answerType = 'text';
      } else {
        this.item.inputType = null;
        this.item.answerType = null;
      }
    },
    // ── SHORT_FORM structure template (raw JSON string, opaque end to end) ──
    /** Seed a BRAND-NEW SHORT_FORM item with the structure the system already emits, so the author edits something
     *  real instead of an empty box. Fire-and-forget, like `syncCalcData`.
     *  🔴 An EXISTING item (questionItemID > 0) is never seeded, even when its box is empty. A stored blank means
     *    "use the built-in structure", so filling it in here would freeze TODAY's structure onto a live card at the
     *    next save — a behaviour change nobody asked for, made silently, on an item already in front of customers.
     *  🔴 A failed fetch is not an error the author must act on: blank IS the working configuration. */
    seedShortFormDefaultTemplate() {
      if (Number(this.item.questionItemID) > 0) return;
      if (String(this.item.shortFormTemplateJson || '').trim()) return;
      const uid = this.item._uid;
      taxQuestionV2Api.getShortFormDefaultTemplate()
        .then((tpl) => {
          const text = String(tpl == null ? '' : tpl).trim();
          if (!text) return;
          // The dialog may have been closed, reset onto another item, or flipped to another kind while the request
          // was in flight — `resetForm` replaces `this.item` wholesale, so the uid is what identifies it.
          if (this.item._uid !== uid || this.item.itemKind !== 'SHORT_FORM') return;
          if (String(this.item.shortFormTemplateJson || '').trim()) return;
          this.item.shortFormTemplateJson = text;
        })
        .catch(() => {});
    },
    /** The "ใส่โครงเริ่มต้น" button. Unlike the seed above this one is explicit, so it reports what happened —
     *  and asks first, because the box may hold something the author typed. */
    async applyShortFormDefaultTemplate() {
      if (this.shortFormTemplateLoading) return;
      if (String(this.item.shortFormTemplateJson || '').trim()
        && !window.confirm('โครง JSON ที่อยู่ในช่องตอนนี้จะถูกแทนที่ด้วยโครงเริ่มต้นของระบบ ต้องการทำต่อหรือไม่?')) {
        return;
      }
      this.shortFormTemplateLoading = true;
      this.shortFormTemplateNotice = '';
      try {
        const tpl = await taxQuestionV2Api.getShortFormDefaultTemplate();
        const text = String(tpl == null ? '' : tpl).trim();
        if (!text) throw new Error('empty template');
        this.item.shortFormTemplateJson = text;
        this.shortFormTemplateNoticeType = 'success';
        this.shortFormTemplateNotice = 'ใส่โครงเริ่มต้นของระบบแล้ว — โครงนี้ให้ผลเหมือนการ์ดที่ระบบวาดอยู่ทุกประการ แก้ต่อได้เลย';
      } catch {
        this.shortFormTemplateNoticeType = 'warning';
        this.shortFormTemplateNotice = 'ดึงโครงเริ่มต้นจากเซิร์ฟเวอร์ไม่สำเร็จ — ลองใหม่อีกครั้ง หรือปล่อยช่องนี้ว่างไว้ (ว่าง = ใช้โครงมาตรฐานของระบบอยู่แล้ว)';
      } finally {
        this.shortFormTemplateLoading = false;
      }
    },
    // ── FILING_CHOICE structure template (raw JSON string, opaque end to end) ──
    /** Seed a BRAND-NEW FILING_CHOICE item with the structure the system already emits, so the author edits
     *  something real instead of an empty box. Fire-and-forget, like `seedShortFormDefaultTemplate`.
     *  🔴 An EXISTING item (questionItemID > 0) is never seeded, even when its box is empty. A stored blank means
     *    "use the built-in structure", so filling it in here would freeze TODAY's structure onto a live card at the
     *    next save — a behaviour change nobody asked for, made silently, on an item already in front of customers.
     *  🔴 A failed fetch is not an error the author must act on: blank IS the working configuration. */
    seedFilingDefaultTemplate() {
      if (Number(this.item.questionItemID) > 0) return;
      if (String(this.item.filingTemplateJson || '').trim()) return;
      const uid = this.item._uid;
      taxQuestionV2Api.getFilingDefaultTemplate()
        .then((tpl) => {
          const text = String(tpl == null ? '' : tpl).trim();
          if (!text) return;
          // 🔴 The dialog may have been closed, reset onto another item, or flipped to another kind while the
          //    request was in flight — `resetForm` replaces `this.item` WHOLESALE, so the uid is the only thing
          //    that still identifies the item this fetch was started for. Re-check the kind for the same reason.
          if (this.item._uid !== uid || this.item.itemKind !== 'FILING_CHOICE') return;
          if (String(this.item.filingTemplateJson || '').trim()) return;
          this.item.filingTemplateJson = text;
        })
        .catch(() => {});
    },
    /** The "ใส่โครงเริ่มต้น" button. Unlike the seed above this one is explicit, so it reports what happened —
     *  and asks first, because the box may hold something the author typed. */
    async applyFilingDefaultTemplate() {
      if (this.filingTemplateLoading) return;
      if (String(this.item.filingTemplateJson || '').trim()
        && !window.confirm('โครง JSON ที่อยู่ในช่องตอนนี้จะถูกแทนที่ด้วยโครงเริ่มต้นของระบบ ต้องการทำต่อหรือไม่?')) {
        return;
      }
      this.filingTemplateLoading = true;
      this.filingTemplateNotice = '';
      try {
        const tpl = await taxQuestionV2Api.getFilingDefaultTemplate();
        const text = String(tpl == null ? '' : tpl).trim();
        if (!text) throw new Error('empty template');
        this.item.filingTemplateJson = text;
        this.filingTemplateNoticeType = 'success';
        this.filingTemplateNotice = 'ใส่โครงเริ่มต้นของระบบแล้ว — โครงนี้ให้ผลเหมือนการ์ดที่ระบบวาดอยู่ทุกประการ แก้ต่อได้เลย';
      } catch {
        this.filingTemplateNoticeType = 'warning';
        this.filingTemplateNotice = 'ดึงโครงเริ่มต้นจากเซิร์ฟเวอร์ไม่สำเร็จ — ลองใหม่อีกครั้ง หรือปล่อยช่องนี้ว่างไว้ (ว่าง = ใช้โครงมาตรฐานของระบบอยู่แล้ว)';
      } finally {
        this.filingTemplateLoading = false;
      }
    },
    /** The SIMULATE twin of `seedFilingDefaultTemplate` — same three guards and the same reason for each:
     *  NEW items only (an existing blank template means "use the built-in structure", and filling it in would
     *  freeze today's structure onto a live card at the next save), never over text the author already typed,
     *  and re-checked after the await because `resetForm` replaces `this.item` wholesale. */
    seedSimulationDefaultTemplate() {
      if (Number(this.item.questionItemID) > 0) return;
      if (String(this.item.simulationTemplateJson || '').trim()) return;
      const uid = this.item._uid;
      taxQuestionV2Api.getSimulationDefaultTemplate()
        .then((tpl) => {
          const text = String(tpl == null ? '' : tpl).trim();
          if (!text) return;
          if (this.item._uid !== uid || this.item.itemKind !== 'SIMULATE') return;
          if (String(this.item.simulationTemplateJson || '').trim()) return;
          this.item.simulationTemplateJson = text;
        })
        .catch(() => {});
    },
    /** The "ใส่โครงเริ่มต้น" button for SIMULATE. Explicit, so it reports what happened — and asks first,
     *  because the box may hold something the author typed. */
    async applySimulationDefaultTemplate() {
      if (this.simulationTemplateLoading) return;
      if (String(this.item.simulationTemplateJson || '').trim()
        && !window.confirm('โครง JSON ที่อยู่ในช่องตอนนี้จะถูกแทนที่ด้วยโครงเริ่มต้นของระบบ ต้องการทำต่อหรือไม่?')) {
        return;
      }
      this.simulationTemplateLoading = true;
      this.simulationTemplateNotice = '';
      try {
        const tpl = await taxQuestionV2Api.getSimulationDefaultTemplate();
        const text = String(tpl == null ? '' : tpl).trim();
        if (!text) throw new Error('empty template');
        this.item.simulationTemplateJson = text;
        this.simulationTemplateNoticeType = 'success';
        this.simulationTemplateNotice = 'ใส่โครงเริ่มต้นของระบบแล้ว — โครงนี้ให้ผลเหมือนการ์ดที่ระบบวาดอยู่ทุกประการ แก้ต่อได้เลย';
      } catch {
        this.simulationTemplateNoticeType = 'warning';
        this.simulationTemplateNotice = 'ดึงโครงเริ่มต้นจากเซิร์ฟเวอร์ไม่สำเร็จ — ลองใหม่อีกครั้ง หรือปล่อยช่องนี้ว่างไว้ (ว่าง = ใช้โครงมาตรฐานของระบบอยู่แล้ว)';
      } finally {
        this.simulationTemplateLoading = false;
      }
    },
    // ── CALCULATE (calc version + ordered formula list) ──
    /** Health of one function summary, as ONE short Thai chip label ('' = nothing to say). Stale wins over the
     *  cached status, because a stale row's `compileStatus` describes an older source than the stored one. */
    calcFunctionWarning(f) {
      if (!f) return '';
      const bits = [];
      if (f.isStale) bits.push('ต้องคอมไพล์ใหม่');
      else if (f.compileStatus === 'Error') bits.push('คอมไพล์ไม่ผ่าน');
      else if (f.compileStatus !== 'Compiled') bits.push('ยังไม่คอมไพล์');
      if (f.isActive === false) bits.push('ปิดใช้งาน');
      return bits.join(' · ');
    },
    /** Load whatever the currently open CALCULATE item needs: the version list, plus the function list of the
     *  version it already points at. Fire-and-forget — both loaders swallow their own failures. */
    syncCalcData() {
      this.loadCalcVersions();
      this.loadCalcFunctions(this.calcConfig.calcVersionId);
    },
    async loadCalcVersions() {
      if (this.configVersionId == null) {
        this.calcVersions = [];
        return;
      }
      this.calcVersionSeq += 1;
      const seq = this.calcVersionSeq;
      this.loadingCalcVersions = true;
      try {
        // includeInactive: a question version may legitimately bind a calc version that is not the active one
        // (only one per config version can be), and a row already bound to it must still resolve its name.
        const list = await taxCalculateV2Api.listVersions(this.configVersionId, true);
        if (seq !== this.calcVersionSeq) return;
        this.calcVersions = Array.isArray(list) ? list : [];
      } catch {
        // Guard (R1): a transient calc fetch failure must never break the dialog — the picker just goes empty and
        // says so; whatever the item already stores keeps round-tripping untouched.
        if (seq !== this.calcVersionSeq) return;
        this.calcVersions = [];
      } finally {
        if (seq === this.calcVersionSeq) this.loadingCalcVersions = false;
      }
    },
    async loadCalcFunctions(calcVersionId) {
      // Bump the sequence FIRST, so an in-flight response for the previous version can no longer land.
      this.calcFunctionSeq += 1;
      const seq = this.calcFunctionSeq;
      const versionId = Number(calcVersionId) > 0 ? Number(calcVersionId) : null;
      if (versionId == null) {
        this.calcFunctions = [];
        this.calcFunctionsVersionId = null;
        this.calcFunctionsFailed = false;
        this.loadingCalcFunctions = false;
        return;
      }
      this.loadingCalcFunctions = true;
      this.calcFunctionsFailed = false;
      try {
        // includeInactive again, for the join: a deactivated formula that is still referenced must render as
        // itself + a "ปิดใช้งาน" chip, not as "ไม่พบสูตรนี้ในเวอร์ชันที่เลือก".
        const list = await taxCalculateV2Api.listFunctions(versionId, true);
        if (seq !== this.calcFunctionSeq) return;
        this.calcFunctions = Array.isArray(list) ? list : [];
        this.calcFunctionsVersionId = versionId;
      } catch {
        if (seq !== this.calcFunctionSeq) return;
        // 🔴 Leave `calcFunctionsVersionId` null: a failed fetch is NOT evidence that the picked formulas are gone,
        //    and stamping it here would paint every selected row with the red "ไม่พบสูตรนี้" chip.
        this.calcFunctions = [];
        this.calcFunctionsVersionId = null;
        this.calcFunctionsFailed = true;
      } finally {
        if (seq === this.calcFunctionSeq) this.loadingCalcFunctions = false;
      }
    },
    /** Switching the calc version WIPES the picked formulas: FunctionIDs are scoped to a version, so keeping them
     *  would send ids that belong to somebody else's version and 400 on save. The wipe is announced, never silent. */
    onCalcVersionChange(next) {
      const value = Number(next) > 0 ? Number(next) : null;
      const previous = this.calcConfig.calcVersionId;
      if (value === previous) return;
      const dropped = this.calcConfig.functions.length;
      // Replace the whole object so the reactive assignment is unambiguous.
      this.item.calcConfig = { calcVersionId: value, functions: [] };
      this.calcFnToAdd = null;
      this.calcNotice = dropped
        ? `เปลี่ยนเวอร์ชันการคำนวณแล้ว — สูตรที่เลือกไว้ ${dropped} รายการถูกล้างทิ้ง เพราะสูตรของคนละเวอร์ชันใช้ข้ามกันไม่ได้ กรุณาเลือกใหม่`
        : '';
      this.loadCalcFunctions(value);
    },
    addCalcFunction() {
      const id = Number(this.calcFnToAdd);
      if (!Number.isFinite(id) || id <= 0) return;
      const fn = (this.calcFunctions || []).find((f) => Number(f.functionID) === id);
      if (!fn) return;
      if (!this.item.calcConfig || typeof this.item.calcConfig !== 'object') {
        this.item.calcConfig = normalizeCalcConfig(null);
      }
      if (!Array.isArray(this.item.calcConfig.functions)) this.item.calcConfig.functions = [];
      if (this.item.calcConfig.functions.some((f) => Number(f && f.functionId) === id)) return;
      // functionCode is stored alongside the id and kept VERBATIM (trim only, never uppercased) — it is the C#
      // class name, and the executor resolves by code first because a version clone regenerates the ids.
      this.item.calcConfig.functions.push({ functionId: id, functionCode: String(fn.functionCode || '').trim() });
      this.calcFnToAdd = null;
      this.calcNotice = '';
    },
    moveCalcFunction(idx, delta) {
      const list = this.item.calcConfig && Array.isArray(this.item.calcConfig.functions)
        ? this.item.calcConfig.functions
        : null;
      if (!list) return;
      const target = idx + delta;
      if (idx < 0 || idx >= list.length || target < 0 || target >= list.length) return;
      const [row] = list.splice(idx, 1);
      list.splice(target, 0, row);
    },
    /** Typed row number (1-based) → move that formula to the position, shifting the others. Invalid input snaps back. */
    setCalcFunctionPosition(idx, event) {
      const input = event.target;
      const list = this.item.calcConfig && Array.isArray(this.item.calcConfig.functions)
        ? this.item.calcConfig.functions
        : null;
      const n = Math.round(Number(input.value));
      input.value = idx + 1;
      if (!list || !Number.isFinite(n)) return;
      const target = Math.min(Math.max(n, 1), list.length) - 1;
      if (target === idx) return;
      const [row] = list.splice(idx, 1);
      list.splice(target, 0, row);
    },
    async removeCalcFunction(idx, label) {
      if (!(await this.confirmRemove('ต้องการลบฟังก์ชันคำนวณนี้ออกจากคำถามใช่หรือไม่?', label))) return;
      if (this.item.calcConfig && Array.isArray(this.item.calcConfig.functions)) {
        this.item.calcConfig.functions.splice(idx, 1);
      }
    },
    // ── SHORT_FORM (card copy + the ordered member-field list) ──
    /** The LIVE config object on `this.item`, created only when it is actually missing.
     *  🔴 Do NOT re-run `normalizeShortFormConfig` here on every call: it returns a fresh object with fresh row
     *     objects, so replacing the config on each keystroke would hand the row editor a clone to type into and
     *     throw the previous character away. Normalization belongs at the entry points (`resetForm`,
     *     `onKindChange`) where the object is legitimately being swapped. */
    ensureShortFormConfig() {
      const c = this.item.shortFormConfig;
      if (!c || typeof c !== 'object' || Array.isArray(c)) {
        this.item.shortFormConfig = normalizeShortFormConfig(null);
      } else if (!Array.isArray(c.fields)) {
        c.fields = [];
      }
      return this.item.shortFormConfig;
    },
    /** Write one of the card's copy strings. Blank is a meaningful value for `secondaryLabel` (it is how the card
     *  loses its decline button), so nothing here coerces an empty string away. */
    setShortFormCopy(key, value) {
      const cfg = this.ensureShortFormConfig();
      cfg[key] = this.writeCardCopy(cfg[key], value, SHORT_FORM_CARD_COPY_KEYS.includes(key) ? this.cardLang : 'th');
    },
    /**
     * Write the THAI member of a copy value without losing the other two.
     *
     * 🔴 The boxes in this dialog edit Thai. If the value is a language bag, replacing it with the typed string
     * would silently delete the English and Chinese an author (or the translation pass) put there — and the
     * builder's Save rebuilds every item, so one keystroke in one card would ship that deletion for the whole
     * version. Blank stays meaningful: `secondaryLabel: ''` is how a card loses its decline button.
     */
    writeCardCopy(current, value, lang = 'th') {
      const text = value == null ? '' : String(value);
      if (lang !== 'th') return this.writeCardTranslation(current, text, lang);
      if (!isLangBag(current)) return text;
      const next = { ...current };
      if (text) next.th = text; else delete next.th;
      return Object.keys(next).length ? next : '';
    },
    /**
     * Write ONE translation of a copy value — the English or Chinese member — and leave its Thai alone.
     *
     * A translation is OF the Thai, so a seat with no Thai has nothing to translate (its box is locked, see
     * `copyLocked`). A plain Thai string becomes a bag the first time a translation is typed, and goes back to
     * the plain string when the last translation is cleared, so an untranslated seat keeps its old shape.
     */
    writeCardTranslation(current, text, lang) {
      const th = langTh(current);
      if (!th) return current;
      const bag = isLangBag(current) ? { ...current } : { th: String(current) };
      if (text) bag[lang] = text; else delete bag[lang];
      return Object.keys(bag).some((k) => k !== 'th') ? bag : bag.th;
    },
    /** One member of a copy value, trimmed, '' when it has none. A plain string IS the Thai member. */
    copyMember(value, lang) {
      if (lang === 'th') return langTh(value);
      if (!isLangBag(value)) return '';
      const v = value[lang] ?? value[lang.toUpperCase()];
      return v == null ? '' : String(v).trim();
    },
    /** What a copy box SHOWS: the member of the language the switch is on. */
    copyIn(value) {
      return this.copyMember(value, this.cardLang);
    },
    /** A card-level copy seat as STORED (plain string or bag) — never the Thai `shortFormConfig` view. */
    cardSeat(key) {
      const c = this.item.shortFormConfig;
      return c && typeof c === 'object' ? c[key] : null;
    },
    /** A card-level copy seat in the switch's language. */
    cardCopy(key) {
      return this.copyIn(this.cardSeat(key));
    },
    /** A translation box whose seat has no Thai yet — there is nothing to translate, and a bag without Thai is
     *  one the backend reads as "no copy". */
    copyLocked(value) {
      return this.cardLang !== 'th' && !langTh(value);
    },
    /** The box's label, with the language named whenever it is not the Thai one. */
    copyLabel(base, thLabel = base) {
      return this.cardLang === 'th' ? thLabel : `${base} · ${this.cardLangTitle}`;
    },
    /** In a translation box the grey text is the THAI it translates — the one thing the translator needs. */
    copyPlaceholder(value, thPlaceholder) {
      if (this.cardLang === 'th') return thPlaceholder;
      const th = langTh(value);
      return th ? `ไทย: ${th}` : 'ยังไม่มีข้อความภาษาไทย — กรอกภาษาไทยก่อน';
    },
    // ── style boxes ────────────────────────────────────────────────────────────────────────────────────
    /** What one style box SHOWS: the draft the author is typing, or the stored bag pretty-printed, or ''. */
    styleText(target) {
      const draft = this.styleDrafts.get(target);
      if (draft !== undefined) return draft;
      const bag = this.styleBagOf(target);
      return bag ? JSON.stringify(bag, null, 2) : '';
    },
    /** '' when the box is fine (blank counts as fine — blank means "this is not styled"). */
    styleError(target) {
      const draft = this.styleDrafts.get(target);
      if (draft === undefined || !String(draft).trim()) return '';
      let parsed;
      try {
        parsed = JSON.parse(draft);
      } catch (err) {
        return String((err && err.message) || 'JSON ไม่ถูกต้อง');
      }
      // 🔴 An array or a bare string parses but cannot be merged onto a card by anyone — see `plainObject` in the
      //    store. Rejected here so the author is told, instead of on save where nothing points at this box.
      return parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)
        ? ''
        : 'ต้องเป็นอ็อบเจ็กต์ JSON เช่น { "titleColor": "#1A1A1A" }';
    },
    /** The stored bag for a target — the card's when it is the card sentinel, otherwise the row's.
     *  🔴 The card's is read off `item.shortFormConfig`, the same object `setShortFormStyle` writes to — never off the
     *     `shortFormConfig` computed, which has no `style` member at all, so the box always came up empty. */
    styleBagOf(target) {
      const holder = target === SHORT_FORM_CARD_STYLE ? this.item.shortFormConfig : target;
      const bag = holder && holder.style;
      return bag != null && typeof bag === 'object' && !Array.isArray(bag) ? bag : null;
    },
    rowStyleText(row) {
      return this.styleText(row);
    },
    rowStyleError(row) {
      return this.styleError(row);
    },
    /**
     * Write one style box. `target` is a row object, or null for the card.
     *
     * 🔴 An UNPARSEABLE draft leaves the stored bag ALONE rather than clearing it — a card must not lose its
     * palette to a keystroke that happened to land between two braces. The save is blocked instead
     * (`itemErrors`), which is the only place that can tell the difference between "still typing" and "done".
     */
    setShortFormStyle(target, text) {
      const key = target || SHORT_FORM_CARD_STYLE;
      const holder = target || this.ensureShortFormConfig();
      const raw = String(text == null ? '' : text);

      if (!raw.trim()) {
        // Cleared on purpose ⇒ the card is not styled. No draft to keep: '' round-trips as '' either way.
        this.styleDrafts.delete(key);
        holder.style = null;
        return;
      }
      this.styleDrafts.set(key, raw);
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return;
      }
      if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) return;
      holder.style = parsed;
    },
    /**
     * Write the whole-config JSON box: parse it and REPLACE the config the structured editor is bound to.
     *
     * 🔴 Through `normalizeShortFormConfig`, never straight onto the item. The structured editor binds into
     * `.fields`, so a document missing that key — or holding a string where a row should be — would leave the
     * boxes above bound to nothing. Normalizing also keeps the two views agreeing about what a row IS: the same
     * canonical `control`, the same bounds rule, and (since this change) every key neither view names.
     *
     * 🔴 An UNPARSEABLE draft leaves the config alone and blocks the save (`itemErrors`) — the same rule the
     * style boxes follow, and for the same reason: mid-edit is not the same as done.
     */
    setShortFormConfigJson(text) {
      const raw = String(text == null ? '' : text);
      this.styleDrafts.set(SHORT_FORM_CONFIG_JSON, raw);
      if (!raw.trim()) return;

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return;
      }
      if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) return;

      this.item.shortFormConfig = normalizeShortFormConfig(parsed);
      // 🔴 Every OTHER draft is now orphaned: the row-level ones are keyed by row objects this replacement just
      //    threw away, and the card-style one describes a `style` bag that may have been rewritten from here.
      //    Keeping them would show the author text belonging to a config that no longer exists.
      const keep = this.styleDrafts.get(SHORT_FORM_CONFIG_JSON);
      this.styleDrafts.clear();
      this.styleDrafts.set(SHORT_FORM_CONFIG_JSON, keep);
    },
    /** Row-level twin of setShortFormCopy — the three per-row boxes had no setter at all, so a raw v-model
     *  would have replaced a bagged row's whole value on the first keystroke. */
    setShortFormRowCopy(row, key, value) {
      if (!row) return;
      row[key] = this.writeCardCopy(row[key], value, SHORT_FORM_ROW_COPY_KEYS.includes(key) ? this.cardLang : 'th');
    },
    /** `version` stays a NUMBER: the app matches the card contract by value, and the "1" a number input hands back
     *  is a string — it would ship as `"version": "1"` and match nothing. */
    setShortFormVersion(value) {
      const n = Number(value);
      this.ensureShortFormConfig().version = Number.isFinite(n) && n > 0 ? Math.trunc(n) : 1;
    },
    addShortFormField() {
      this.ensureShortFormConfig().fields.push(blankShortFormField());
    },
    moveShortFormField(idx, delta) {
      const list = this.ensureShortFormConfig().fields;
      const target = idx + delta;
      if (idx < 0 || idx >= list.length || target < 0 || target >= list.length) return;
      const [row] = list.splice(idx, 1);
      list.splice(target, 0, row);
    },
    async removeShortFormField(idx, label) {
      if (!(await this.confirmRemove('ต้องการลบช่องนี้ออกจากการ์ดใช่หรือไม่?', label))) return;
      const list = this.ensureShortFormConfig().fields;
      if (idx >= 0 && idx < list.length) list.splice(idx, 1);
    },
    // ── quick reply (the only answer-button list) ──
    answerOptionField(option, key) {
      if (!option || typeof option !== 'object' || Array.isArray(option)) {
        return key === 'label' ? String(option ?? '') : '';
      }
      const aliases = {
        label: ['label', 'Label', 'value', 'Value'],
        labelEn: ['labelEn', 'LabelEn'],
        labelZh: ['labelZh', 'LabelZh'],
      };
      const property = (aliases[key] || [key]).find((name) => option[name] !== null && option[name] !== undefined);
      return property ? String(option[property]) : '';
    },
    normalizeAnswerOption(option) {
      if (option && typeof option === 'object' && !Array.isArray(option)) {
        return {
          ...option,
          label: this.answerOptionField(option, 'label'),
          labelEn: this.answerOptionField(option, 'labelEn'),
          labelZh: this.answerOptionField(option, 'labelZh'),
        };
      }
      return {
        label: String(option ?? ''),
        labelEn: '',
        labelZh: '',
      };
    },
    normalizeAnswerOptions() {
      if (!Array.isArray(this.item.answerOptions)) this.item.answerOptions = [];
      this.item.answerOptions = this.item.answerOptions.map((option) => this.normalizeAnswerOption(option));
    },
    ensureAnswerOptionObject(idx) {
      if (!Array.isArray(this.item.answerOptions)) this.item.answerOptions = [];
      const current = this.item.answerOptions[idx];
      if (!current || typeof current !== 'object' || Array.isArray(current)) {
        this.item.answerOptions[idx] = this.normalizeAnswerOption(current);
      } else {
        this.item.answerOptions[idx] = this.normalizeAnswerOption(current);
      }
      return this.item.answerOptions[idx];
    },
    // 🔴 An OBJECT with the three label slots, and deliberately NO `value`. A chip here is a label —
    // the answer that gets stored is decided by the item's AnswerSystemPrompt, not by the button — and
    // a row that carries a `value` makes the backend print `[ตัวเลือก: …]` into this question's prompt.
    addChip() {
      if (!Array.isArray(this.item.answerOptions)) this.item.answerOptions = [];
      this.item.answerOptions.push({ label: '', labelEn: '', labelZh: '' });
    },
    async removeChip(idx, label) {
      if (!(await this.confirmRemove('ต้องการลบปุ่มคำตอบนี้ใช่หรือไม่?', label))) return;
      if (Array.isArray(this.item.answerOptions)) this.item.answerOptions.splice(idx, 1);
    },
    /**
     * Edit the Thai (source) label of one chip.
     *
     * 🔴 A bare-string row is promoted to an object first — that is the only shape with somewhere to
     * put a translation — and every other member of an object row is carried over, so a `value`
     * authored through RAW JSON survives editing a label.
     */
    setChipLabel(idx, val) {
      const option = this.ensureAnswerOptionObject(idx);
      option.label = String(val ?? '');
    },
    setChipLanguage(idx, key, val) {
      if (!['labelEn', 'labelZh'].includes(key)) return;
      const option = this.ensureAnswerOptionObject(idx);
      option[key] = String(val ?? '');
    },
    // ── resume stage 2 chips (`confirmOptions`) ──
    // 🔴 The per-option helpers above (`answerOptionField` / `normalizeAnswerOption`) are REUSED verbatim — they
    //    take an option, not a list, so the two chip lists cannot drift into two shapes. Only the list itself is
    //    different, and that difference is the whole point: stage 1's buttons live on `answerOptions`.
    ensureConfirmOptionObject(idx) {
      if (!Array.isArray(this.item.confirmOptions)) this.item.confirmOptions = [];
      this.item.confirmOptions[idx] = this.normalizeAnswerOption(this.item.confirmOptions[idx]);
      return this.item.confirmOptions[idx];
    },
    // Stage-2 twin of `normalizeAnswerOptions`. The translator writes `labelEn` / `labelZh` INTO the row object, so
    // every row has to be an object first — a bare string row has nowhere to put a translation and the write would
    // be dropped on the floor without an error.
    normalizeConfirmOptions() {
      if (!Array.isArray(this.item.confirmOptions)) this.item.confirmOptions = [];
      this.item.confirmOptions = this.item.confirmOptions.map((option) => this.normalizeAnswerOption(option));
    },
    /// The caption on the Thai box, which is where the ORDER RULE is actually visible while authoring:
    //  the runtime decides by POSITION ([0] = reopen an answer, [1] = walk on), never by what the button says.
    confirmChipRoleLabel(idx) {
      // 🔴 ปุ่มเดียว = “ใช้ต่อ” (ไม่ใช่ “ขอแก้”) — ป้ายบนหัวช่องต้องพูดกติกาเดียวกับ StateConfirmVerdictFor
      //    ไม่งั้น admin ที่ตั้งปุ่มเดียวจะอ่านจากจอนี้ว่าปุ่มของเขาแปลว่าตรงข้ามกับที่ระบบทำจริง
      const total = this.confirmChipRows.length;
      if (total === 1) return 'Thai · ปุ่มเดียว = ใช้ต่อ';
      if (idx === 0) return 'Thai · ปุ่มที่ 1 = ขอแก้';
      if (idx === 1) return 'Thai · ปุ่มที่ 2 = ใช้ต่อ';
      return 'Thai';
    },
    addConfirmChip() {
      if (!Array.isArray(this.item.confirmOptions)) this.item.confirmOptions = [];
      this.item.confirmOptions.push({ label: '', labelEn: '', labelZh: '' });
    },
    async removeConfirmChip(idx, label) {
      if (!(await this.confirmRemove('ต้องการลบปุ่มยืนยันนี้ใช่หรือไม่?', label))) return;
      if (Array.isArray(this.item.confirmOptions)) this.item.confirmOptions.splice(idx, 1);
    },
    setConfirmChipLabel(idx, val) {
      const option = this.ensureConfirmOptionObject(idx);
      option.label = String(val ?? '');
    },
    setConfirmChipLanguage(idx, key, val) {
      if (!['labelEn', 'labelZh'].includes(key)) return;
      const option = this.ensureConfirmOptionObject(idx);
      option[key] = String(val ?? '');
    },
    /**
     * Per-language completeness of ONE chip list, exactly as the runtime reads it.
     *
     * 🔴 Shared by stage 1 (`chipLangBadges`) and stage 2 (`confirmChipLangBadges`) rather than copied, because
     * the rule it reports belongs to the backend chip pipeline both lists go through: a language is used only when
     * EVERY chip of the row has it AND no two collide — one missing box, or two buttons that ended up with the
     * same English word, and the whole row renders in Thai.
     */
    langBadgesFor(rows) {
      const list = Array.isArray(rows) ? rows : [];
      return [
        { lang: 'en', key: 'labelEn', title: 'EN' },
        { lang: 'zh', key: 'labelZh', title: '中' },
      ].map(({ lang, key, title }) => {
        const filled = list.filter((r) => String(r[key] ?? '').trim().length > 0);
        const seen = new Set(filled.map((r) => String(r[key]).trim().toLowerCase()));
        const complete = filled.length === list.length;
        const distinct = seen.size === filled.length;
        if (complete && distinct) return { lang, ok: true, text: `${title} ครบ` };
        if (complete && !distinct) return { lang, ok: false, text: `${title} ซ้ำกันเอง — จะใช้ไทย` };
        return { lang, ok: false, text: `${title} ${filled.length}/${list.length} — จะใช้ไทย` };
      });
    },
    /** Stage-1 button: translate the quick-reply row (`answerOptions`). */
    translateChips() {
      return this.translateChipList('answerOptions');
    },
    /** Stage-2 button: translate the resume-confirm row (`confirmOptions`). Same method, other list. */
    translateConfirmChips() {
      return this.translateChipList('confirmOptions');
    },
    /**
     * Fill the English + Chinese boxes of ONE chip list from its Thai boxes, one request at a time.
     *
     * 🔴 ONE implementation for both rows on purpose. The two lists differ in exactly two ways — which array on
     * `item` they live on, and which four UI slots report progress — so everything else (the overwrite prompt, the
     * sequential requests, the per-field failure list, the final message) is shared. Duplicating it is how the two
     * buttons would quietly grow different rules about overwriting or about what counts as a failure.
     *
     * Existing translations are REPLACED (after a confirm), never merged into the blanks: a row that is half-old,
     * half-new is exactly the mixed-language row the all-or-nothing rule punishes.
     *
     * @param {'answerOptions'|'confirmOptions'} listKey which chip list to translate.
     */
    async translateChipList(listKey = 'answerOptions') {
      const stage2 = listKey === 'confirmOptions';
      const busyKey = stage2 ? 'translatingConfirmChips' : 'translatingChips';
      const progressKey = stage2 ? 'confirmTranslationProgress' : 'translationProgress';
      const messageKey = stage2 ? 'confirmChipTranslationMessage' : 'chipTranslationMessage';
      const messageTypeKey = stage2 ? 'confirmChipTranslationMessageType' : 'chipTranslationMessageType';

      if (this[busyKey]) return;

      if (stage2) this.normalizeConfirmOptions();
      else this.normalizeAnswerOptions();
      const options = this.item[listKey].filter((option) => this.answerOptionField(option, 'label').trim());
      if (!options.length) return;

      const hasExistingTranslations = options.some((option) =>
        this.answerOptionField(option, 'labelEn').trim() || this.answerOptionField(option, 'labelZh').trim(),
      );
      if (hasExistingTranslations && !window.confirm('Existing English or Chinese translations will be replaced. Continue?')) {
        return;
      }

      const targets = [
        { key: 'labelEn', language: 'en', name: 'English' },
        { key: 'labelZh', language: 'zh-Hant', name: 'Chinese' },
      ];
      const total = options.length * targets.length;
      let completed = 0;
      let translated = 0;
      const failures = [];
      this[busyKey] = true;
      this[messageKey] = '';
      this[progressKey] = { completed, total };

      try {
        // Keep the requests sequential so one click does not burst the shared translator endpoint.
        for (let index = 0; index < options.length; index += 1) {
          const option = options[index];
          const source = this.answerOptionField(option, 'label').trim();
          for (const target of targets) {
            try {
              const result = await languageTranslationApi.translate(source, 'th', target.language);
              const value = String(result ?? '').trim();
              if (!value) throw new Error('Empty translation');
              option[target.key] = value;
              translated += 1;
            } catch (error) {
              failures.push(`${index + 1}. ${target.name}`);
            } finally {
              completed += 1;
              this[progressKey] = { completed, total };
            }
          }
        }
      } finally {
        this[busyKey] = false;
        // RAW JSON is a stage-1-only escape hatch (stage 2 has no such toggle), so this mirror only exists there.
        if (!stage2 && this.rawChips) this.chipsRawText = JSON.stringify(this.item.answerOptions, null, 2);
      }

      if (failures.length) {
        const details = failures.slice(0, 4).join(', ');
        this[messageTypeKey] = 'warning';
        this[messageKey] = `Translated ${translated}/${total} fields. Failed: ${details}${failures.length > 4 ? ', …' : ''}.`;
      } else {
        this[messageTypeKey] = 'success';
        this[messageKey] = `Translated ${translated} fields. Review the results before saving.`;
      }
    },
    // INCOME_CLASSIFIER convenience: turn the §40 catalog into an explicit chip list. Nothing changes at runtime
    // (the catalog is already the fallback row) — what changes is that the buttons become VISIBLE config an admin
    // can reorder, rename or trim, instead of a list that only exists inside the precedence table.
    // 🔴 Translations are left EMPTY on purpose. The catalog holds one Thai name per category and nothing else;
    // inventing an English or Chinese label from it would be this dialog making up copy, and the row rule means a
    // made-up one is worse than none (it would make the whole item render in a language nobody authored).
    fillChipsFromIncomeCategories() {
      const chips = this.incomeCategoryChips;
      if (!chips.length) return;
      this.item.answerOptions = chips.map((chip) => this.normalizeAnswerOption(chip));
      if (this.rawChips) this.chipsRawText = JSON.stringify(this.item.answerOptions, null, 2);
      this.chipsRawError = '';
    },
    toggleRawChips() {
      if (!this.rawChips) {
        const list = Array.isArray(this.item.answerOptions) ? this.item.answerOptions : [];
        this.chipsRawText = JSON.stringify(list, null, 2);
        this.chipsRawError = '';
      }
      this.rawChips = !this.rawChips;
    },
    onChipsRawInput(text) {
      this.chipsRawText = text;
      const raw = String(text ?? '').trim();
      if (!raw) {
        this.chipsRawError = '';
        this.item.answerOptions = [];
        return;
      }
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        this.chipsRawError = 'JSON ไม่ถูกต้อง — ยังไม่บันทึกค่านี้';
        return;
      }
      if (!Array.isArray(parsed)) {
        this.chipsRawError = 'ต้องเป็น array เช่น ["โสด","สมรส"]';
        return;
      }
      this.chipsRawError = '';
      this.item.answerOptions = parsed;
    },
    // ── income categories (INCOME_CLASSIFIER / INCOME_CLASSIFY) ──
    ensureCatMeta(cat) {
      const m = cat.metadata || {};
      cat._meta = {
        assetCategory: m.assetCategory ?? '',
        propertyType: m.propertyType ?? '',
        professionType: m.professionType ?? '',
        businessType: m.businessType ?? '',
      };
    },
    // 🔴 Every mini-section member has to EXIST before the editor binds to it. A key added to a reactive object after
    //    the fact is not tracked in Vue 2-style option objects and, more to the point here, `v-model` on an absent
    //    member starts from `undefined` — the field renders, the author types, and nothing is read back on submit.
    //    Catalogs authored as an INCOME_CLASSIFIER (and every row loaded from the API before this kind existed) reach
    //    the dialog without a single one of them.
    ensureCatFields(cat) {
      if (!Array.isArray(cat.keywords)) cat.keywords = [];
      if (!Array.isArray(cat.subTypes)) cat.subTypes = [];
      ['incomeFieldKey', 'periodFieldKey'].forEach((k) => {
        if (typeof cat[k] !== 'string') cat[k] = cat[k] == null ? '' : String(cat[k]);
      });
      this.ensureCatMeta(cat);
    },
    addIncomeCategory() {
      if (!Array.isArray(this.item.incomeCategories)) this.item.incomeCategories = [];
      const cat = { code: '', labelTh: '', flagKey: '', keywords: [], metadata: null };
      this.ensureCatFields(cat);
      this.item.incomeCategories.push(cat);
    },
    async removeIncomeCategory(idx, label) {
      if (!(await this.confirmRemove('ต้องการลบประเภทเงินได้นี้ใช่หรือไม่?', label))) return;
      if (Array.isArray(this.item.incomeCategories)) this.item.incomeCategories.splice(idx, 1);
    },
    // The §40 items one category points at, projected into the SAME shape the item list draws them in — kind chip,
    // FieldKey, question, answer-kind chip, required flag, VisibleWhen in words — plus the pre-question / amount role
    // that is only knowable from here. Read live off `siblingItems`; nothing on this panel edits them.
    //
    // 🔴 It mirrors the list on purpose. An author arrives here straight from that table, and a preview that
    //    described the same row differently (or thinner) made them go back and forth to answer "is this the row I
    //    think it is" — the question this panel exists to answer.
    // 🔴 Returns one row PER ITEM, not per key. INCOME_40_1 is authored twice — full-year and part-year — and an
    //    author looking at one merged line would have no way to tell that the part-year sentence exists, let alone
    //    that a part-year customer is the one who hears it.
    // 🔴 A key nobody owns still produces a row. Falling silent there showed a configured section as if it had no
    //    pre-question at all, when what it really has is an amount with nowhere to land.
    sectionWording(cat) {
      const items = Array.isArray(this.siblingItems) ? this.siblingItems : [];
      const owners = (key) => {
        const want = String(key || '').trim().toLowerCase();
        if (!want) return [];
        return items
          .filter((it) => it && String(it.fieldKey || '').trim().toLowerCase() === want)
          .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
      };
      const rows = [];
      const collect = (key, role) => {
        const want = String(key || '').trim();
        if (!want) return;
        const roleLabel = role === 'period' ? 'คำถามนำ' : 'คำถามยอด';
        const roleIcon = role === 'period' ? 'mdi-help-rhombus-outline' : 'mdi-target';
        const found = owners(want);
        if (!found.length) {
          rows.push({
            role, roleLabel, roleIcon, fieldKey: want, missing: true,
          });
          return;
        }
        found.forEach((it) => {
          const kind = String(it.itemKind || '').trim().toUpperCase();
          const gateRaw = String(it.visibleWhen || '').trim();
          const gated = Boolean(gateRaw) && !/^always$/i.test(gateRaw);
          rows.push({
            role,
            roleLabel,
            roleIcon,
            missing: false,
            order: Number(it.sortOrder) || null,
            itemKind: kind,
            kindColor: KIND_COLORS[kind] || 'grey',
            fieldKey: String(it.fieldKey || '').trim(),
            text: String(it.questionTextTh || '').trim(),
            label: String(it.label || '').trim(),
            answer: ANSWER_KIND_META[it.inputType]
              || { label: it.inputType || '—', color: 'grey', icon: 'mdi-help-circle-outline', hint: 'ไม่ทราบชนิดคำตอบ' },
            isRequired: Boolean(it.isRequired),
            // A deactivated target is the one failure this preview can catch before the save: the section keeps
            // working, the customer keeps being asked, and the amount lands on a row the walker never reads.
            inactive: it.isActive === false,
            gateLabel: gated ? describeVisibleWhen(gateRaw) : '',
            gateRaw: gated ? gateRaw : '',
            hasAnswerPrompt: Boolean(String(it.answerSystemPrompt || '').trim()),
          });
        });
      };
      collect(cat.periodFieldKey, 'period');
      collect(cat.incomeFieldKey, 'amount');
      return rows;
    },
    addIncomeSubType(cat) {
      if (!Array.isArray(cat.subTypes)) cat.subTypes = [];
      cat.subTypes.push({ code: '', labelTh: '' });
    },
    async removeIncomeSubType(cat, idx, label) {
      if (!(await this.confirmRemove('ต้องการลบประเภทย่อยนี้ใช่หรือไม่?', label))) return;
      if (Array.isArray(cat.subTypes)) cat.subTypes.splice(idx, 1);
    },
    closeDialog() {
      this.$emit('update:modelValue', false);
    },
    // Row removal stays local until "บันทึกลง builder", but a mis-click still loses what was typed — ask first.
    confirmRemove(message, label) {
      return this.$refs.confirmDelete.ask({ message, target: label });
    },
    // Fold each income category's editable `_meta` bucket back into `metadata`
    // (preserving nulls), then strip the transient `_meta` before emitting.
    materializeIncomeMetadata() {
      (Array.isArray(this.item.incomeCategories) ? this.item.incomeCategories : []).forEach((cat) => {
        const m = cat._meta || {};
        const pick = (v) => {
          const s = v == null ? '' : String(v).trim();
          return s ? s : null;
        };
        cat.metadata = {
          assetCategory: pick(m.assetCategory),
          propertyType: pick(m.propertyType),
          professionType: pick(m.professionType),
          businessType: pick(m.businessType),
        };
        delete cat._meta;
      });
    },
    submit() {
      this.submitAttempted = true;
      if (this.validationErrors.length) return;
      this.materializeIncomeMetadata();
      this.$emit('submit', JSON.parse(JSON.stringify(this.item)));
    },
  },
};
</script>

<style scoped>
/* ── The split shape: editor on the left, live card on the right ────────────────────────────────
   Every rule here is scoped to `.tq2-split`, which is applied ONLY for a kind that has a card. A
   kind without one keeps `pa-6` on `v-card-text` and none of this reaches it. */
.tq2-split {
  display: flex;
  /* 🔴 The load-bearing line. `v-dialog scrollable` makes this element the scroller; while it keeps
     that job the two columns are one scrolling surface and the preview leaves the screen as soon as
     the author scrolls the form. Hidden here = each column scrolls on its own, below. */
  overflow: hidden;
  padding: 0;
}

.tq2-editor-col {
  flex: 1 1 auto;
  /* Without this a long unbroken value in a text field sets the flex item's min-content width and the
     editor pushes the preview column off the dialog instead of wrapping. */
  min-width: 0;
}

.tq2-split .tq2-editor-col {
  overflow-y: auto;
}

.tq2-preview-lang {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}
.tq2-lang-count {
  margin-left: 6px;
  font-size: 11px;
  opacity: 0.8;
}
.tq2-lang-count--gap {
  color: rgb(var(--v-theme-warning));
  opacity: 1;
  font-weight: 600;
}
.tq2-preview-col {
  /* Fixed, not proportional: the card is drawn at a phone-ish width and stretching it would preview a
     layout the app never shows. The editor takes the rest. */
  flex: 0 0 420px;
  overflow-y: auto;
  padding: 1rem;
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  background: rgba(var(--v-theme-on-surface), 0.02);
}

/* Under ~1100px two columns leave neither one usable, so they stack and the dialog goes back to being
   ONE scroller — the columns must give up their own, or the lower one becomes a scroll trap. */
@media (max-width: 1100px) {
  .tq2-split {
    flex-direction: column;
    overflow-y: auto;
  }

  .tq2-split .tq2-editor-col {
    overflow: visible;
  }

  .tq2-preview-col {
    flex: 0 0 auto;
    border-left: none;
    border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  }
}

.tq2-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.82);
}

.tq2-label-flag {
  color: rgb(var(--v-theme-warning));
  font-weight: 600;
}

.tq2-block {
  margin-top: 8px;
  margin-bottom: 8px;
  padding: 16px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 10px;
  background: rgba(var(--v-theme-on-surface), 0.015);
}

.tq2-block-title {
  margin: 0 0 12px;
  font-size: 0.95rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.tq2-editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
  flex-wrap: wrap;
}

.tq2-row-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}

.tq2-row-index {
  flex: 0 0 24px;
  text-align: center;
  font-size: 0.8rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.tq2-row-index-input {
  width: 36px;
  flex: 0 0 36px;
  padding: 2px 0;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 4px;
  background: transparent;
  -moz-appearance: textfield;
}
.tq2-row-index-input::-webkit-outer-spin-button,
.tq2-row-index-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.tq2-row-index-input:focus { outline: none; border-color: rgb(var(--v-theme-primary)); }

.tq2-option-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tq2-multilingual-chip-head,
.tq2-multilingual-option-row {
  display: grid;
  grid-template-columns: 24px repeat(3, minmax(0, 1fr)) 32px;
  align-items: center;
  gap: 8px;
}

.tq2-multilingual-chip-head {
  padding: 0 4px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 0.75rem;
  font-weight: 600;
}

.tq2-chip-action-head {
  width: 32px;
}

.tq2-chip-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

@media (max-width: 700px) {
  .tq2-multilingual-chip-head {
    display: none;
  }

  .tq2-multilingual-option-row {
    grid-template-columns: 24px minmax(0, 1fr) 32px;
    align-items: start;
  }

  .tq2-multilingual-option-row > :nth-child(2),
  .tq2-multilingual-option-row > :nth-child(3),
  .tq2-multilingual-option-row > :nth-child(4) {
    grid-column: 2;
  }

  .tq2-multilingual-option-row > :nth-child(5) {
    grid-column: 3;
    grid-row: 1 / span 3;
    align-self: center;
  }

  .tq2-chip-actions {
    flex-wrap: wrap;
  }
}






/* CALCULATE — picker row + the ordered formula list */
.tq2-calc-add {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.tq2-calc-add > .v-input {
  flex: 1 1 auto;
  min-width: 0;
}

.tq2-calc-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 8px;
}

.tq2-calc-main {
  flex: 1 1 auto;
  min-width: 0;
}

.tq2-calc-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.tq2-calc-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.9);
}

.tq2-calc-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.tq2-calc-chips {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.tq2-income-row {
  padding: 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 8px;
}

.tq2-income-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.tq2-income-label,
.tq2-income-keywords {
  grid-column: 1 / -1;
}

/* INCOME_CLASSIFY — the mini section inside a category: its own copy, its own rules, its own fan-out target.
   Tinted and rule-marked so it reads as config that BELONGS to the category above it, not as more item-level
   fields that happen to sit nearby. */
.tq2-mini-section {
  padding: 10px 12px;
  border-left: 3px solid rgba(var(--v-theme-primary), 0.5);
  border-radius: 0 8px 8px 0;
  background: rgba(var(--v-theme-primary), 0.04);
}

.tq2-mini-title {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}

/* Read-only preview of the §40 item's own sentence. Deliberately NOT styled as an input: it must not read as
   something editable here, because editing it here is exactly the mistake this block exists to prevent. */
/* One §40 row, drawn to read like its row in the item list (chips + mono FieldKey + question in the strong
   position), so an author coming from that table recognises it without comparing field by field. */
.tq2-wording {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.tq2-wording-dangling {
  background: rgba(var(--v-theme-warning), 0.08);
}

.tq2-wording-head {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 0.72rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.tq2-wording-role {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
}

.tq2-wording-order,
.tq2-wording-key {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.tq2-wording-key {
  font-size: 0.75rem;
  color: #6b7280;
}

.tq2-wording-gate {
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(var(--v-theme-on-surface), 0.07);
}

.tq2-wording-body {
  margin-top: 4px;
  font-size: 0.82rem;
  line-height: 1.5;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.9);
}

.tq2-wording-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.tq2-wording-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tq2-wording-missing {
  color: rgb(var(--v-theme-warning));
}

.tq2-subtype-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 36px;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
}

/* SHORT_FORM — one bordered block per amount box on the card */
.tq2-shortform-row {
  padding: 10px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 8px;
}

.tq2-shortform-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  align-items: center;
}

/* the legal small print is the one field that is routinely a paragraph */
/* min / max / step sit on one line: they are three parts of ONE control's range, and stacking them
   reads as three unrelated settings. Wraps rather than shrinks on a narrow dialog. */
.tq2-shortform-bounds {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tq2-shortform-bounds > * {
  flex: 1 1 92px;
  min-width: 92px;
}

.tq2-shortform-note {
  grid-column: 1 / -1;
}



.tq2-error-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
}

.tq2-prompt-panel {
  margin-top: 8px;
}

.tq2-meta-panel :deep(.v-expansion-panel-title) {
  min-height: 40px;
  font-size: 0.82rem;
}
</style>
